import {useMemo} from 'react';
import {useI18n} from '../../i18n/useI18n';
import {
    AuthHighlightItem,
    FaqItem,
    FeatureItem,
    FlowStepContent,
    FlowStepItem,
    GuardItem,
    LinkCardItem,
    MetricItem,
    PocEntryItem,
    ProductDirectionItem,
    SectionTitleBlock,
    SignalDemoData,
    SiteCta,
    SiteNavItem,
    StoryBlock
} from './site.types';

type CopyLocale = 'zh-CN' | 'zh-TW' | 'en-US' | 'ko-KR' | 'ja-JP' | 'fr-FR';

interface HeroCopy {
    eyebrow: string;
    title: string;
    description: string;
    tags?: string[];
    panelLabel: string;
    panelTitle: string;
    panelDescription: string;
    assistText?: string;
    topCardLabel?: string;
    topCardTitle?: string;
    topCardText?: string;
    bottomCardLabel?: string;
    bottomCardTitle?: string;
    bottomCardText?: string;
}

interface SiteCopyShape {
    primaryCta: SiteCta;
    secondaryCta: SiteCta;
    tertiaryCta: SiteCta;
    /** 官网邀请码申请弹窗按钮（注册需邀请码，弹窗提交申请） */
    waitlistCta: SiteCta;
    siteNavItems: SiteNavItem[];
    footerNavItems: SiteNavItem[];
    heroMetrics: MetricItem[];
    home: {
        hero: HeroCopy;
        section: { eyebrow: string; title: string; description: string; };
        products: ProductDirectionItem[];
        proofItems: FeatureItem[];
        exploreTitle: { eyebrow: string; title: string; description: string; };
        exploreCards: LinkCardItem[];
        ctaEyebrow: string;
        ctaTitle: string;
        ctaDescription: string;
    };
    productPage: {
        hero: HeroCopy;
        directions: (StoryBlock & {
            cardEyebrow?: string;
            cardTitle?: string;
            cardDescription?: string;
            featuresTitle?: SectionTitleBlock;
            flowSteps?: FlowStepContent[];
            usageTitle?: SectionTitleBlock;
            usageSteps?: FlowStepItem[]
        })[];
    };
    resources: {
        proofBarItems: string[];
        howItWorksHero: HeroCopy;
        howItWorksSteps: FlowStepItem[];
        howItWorksFlowSteps: FlowStepContent[];
        howItWorksAiDecision: StoryBlock[];
        howItWorksBlocks: FeatureItem[];
        howItWorksSections: Record<string, SectionTitleBlock>;
        howItWorksDemo: SignalDemoData;
        howItWorksMetrics: MetricItem[];
        howItWorksCta: { eyebrow: string; title: string; description: string };
        riskHero: HeroCopy;
        riskGuardItems: GuardItem[];
        riskMatrixItems: FeatureItem[];
        riskSections: Record<string, SectionTitleBlock>;
        riskCta: { eyebrow: string; title: string; description: string };
        riskMetrics: MetricItem[];
    };
    pocPage: {
        hero: HeroCopy;
        entries: PocEntryItem[];
        features: FeatureItem[];
        faqIntro: { eyebrow: string; title: string; description: string; };
        faqItems: FaqItem[];
    };
    assistPocPage: {
        hero: HeroCopy;
        features: FeatureItem[];
        flowSteps: FlowStepItem[];
        stories: StoryBlock[];
        sectionTitles: {
            features: SectionTitleBlock;
            flow: SectionTitleBlock;
            stories: SectionTitleBlock;
            demo: SectionTitleBlock;
            cta: {
                primaryLabel: string;
                primaryTo: string;
                secondaryLabel?: string;
                secondaryTo?: string;
            };
        };
    };
    autoPocPage: {
        hero: HeroCopy;
        features: FeatureItem[];
        flowSteps: FlowStepItem[];
        stories: StoryBlock[];
        sectionTitles: {
            features: SectionTitleBlock;
            flow: SectionTitleBlock;
            stories: SectionTitleBlock;
            demo: SectionTitleBlock;
        };
    };
    patternPocPage: {
        hero: HeroCopy;
        features: FeatureItem[];
        flowSteps: FlowStepItem[];
        stories: StoryBlock[];
        sectionTitles: {
            features: SectionTitleBlock;
            flow: SectionTitleBlock;
            stories: SectionTitleBlock;
            demo: SectionTitleBlock;
            cta: {
                primaryLabel: string;
                primaryTo?: string;
            };
        };
    };
    membershipPage: {
        hero: {
            eyebrow: string;
            title: string;
            description: string;
            panelLabel: string;
            panelTitle: string;
            panelDescription: string;
        };
        sectionTitles: {
            compare: SectionTitleBlock;
            features: SectionTitleBlock;
            cta: { eyebrow: string; title: string; description: string };
        };
        priceLabels: { monthly: string; yearly: string };
        tiers: {
            name: string;
            tagline: string;
            monthlyPrice: string;
            yearlyPriceMonthly: string;
            highlight?: boolean;
            features: { label: string; enabled: boolean; note?: string }[];
        }[];
        featureIntros: FeatureItem[];
        faqIntro: { eyebrow: string; title: string; description: string };
        faqItems: FaqItem[];
    };
    contactPage: {
        hero: HeroCopy;
        contactModes: FeatureItem[];
        applicationSteps: FlowStepItem[];
        faqIntro: { eyebrow: string; title: string; description: string; };
        faqItems: FaqItem[];
    };
}

const buildSiteCopy = (locale: CopyLocale): SiteCopyShape => {
    const copies: Record<CopyLocale, SiteCopyShape> = {
        'zh-CN': {
            primaryCta: {label: '立即注册', path: '/register'},
            secondaryCta: {label: '立即开通', path: '/membership'},
            waitlistCta: {label: '获取邀请码', path: '/site/contact'},
            tertiaryCta: {label: '登录系统', path: '/login'},
            siteNavItems: [
                {label: '首页', path: '/'},
                {label: '辅助交易', path: '/site/assist-trading'},
                {label: '自动交易', path: '/site/auto-trading'},
                {label: '资产配置', path: '/site/asset-allocation'},
                {
                    label: '资源',
                    children: [
                        {label: '工作机制', path: '/site/how-it-works'},
                        {label: '风控安全', path: '/site/risk-control'}
                    ]
                },
                {
                    label: 'POC',
                    children: [
                        {label: '辅助交易', path: '/site/poc/assist-trading'},
                        {label: '自动交易', path: '/site/poc/auto-trading'},
                        {label: '形态识别', path: '/site/poc/pattern'}
                    ]
                },
                {label: '定价', path: '/site/membership'},
                {label: '联系', path: '/site/contact'},
            ],
            footerNavItems: [
                {label: '首页', path: '/'},
                {label: '辅助交易', path: '/site/assist-trading'},
                {label: '自动交易', path: '/site/auto-trading'},
                {label: '资产配置', path: '/site/asset-allocation'},
                {label: '工作机制', path: '/site/how-it-works'},
                {label: '风控安全', path: '/site/risk-control'},
                {label: 'POC 总览', path: '/site/poc'},
                {label: '辅助交易', path: '/site/poc/assist-trading'},
                {label: '自动交易', path: '/site/poc/auto-trading'},
                {label: '形态识别', path: '/site/poc/pattern'},
                {label: '定价', path: '/site/membership'},
                {label: '联系', path: '/site/contact'}
            ],
            heroMetrics: [
                {title: '市场机会', value: '洞察', note: '辅助识别关键信号与结构变化', trend: 'up'},
                {title: '资产增值', value: '匹配', note: '策略与目标对齐，持续追踪执行', trend: 'stable'},
                {title: '资产安全', value: '守护', note: '合规与风控并重，投资全程可控', trend: 'focus'}
            ],
            home: {
                hero: {
                    eyebrow: '行稳致远',
                    title: '建造您自己的贝莱德',
                    description: '与您共同进步的AI资产管家。用您的规则、节奏和边界，运转可持续的交易与资管体系。',
                    tags: ['辅助交易', '自动交易', '资产配置', '风控前置'],
                    panelLabel: '资产管理本应如此简单',
                    panelTitle: 'AI负责苦脏累，您只需专注决断',
                    panelDescription: '组合构建、风险分配、策略执行——过去机构级的资管能力，现在 AI 为您系统化落地。',
                    assistText: '用更清晰的系统，帮助交易者建立长期一致的决策与执行能力。',
                    topCardLabel: '愿景',
                    topCardTitle: '长期一致',
                    topCardText: '帮助交易者建立稳定、可复盘、可持续的执行方式',
                    bottomCardLabel: '体验方式',
                    bottomCardTitle: '邀请码体验',
                    bottomCardText: '默认简体中文，可切换繁体中文与英文'
                },
                section: {
                    eyebrow: '产品方向',
                    title: '三大产品方向，一套能力体系',
                    description: '从信息、执行到配置，呈现 AI 的系统化落地路径。'
                },
                products: [
                    {
                        key: 'assist-trading',
                        label: '辅助交易',
                        slogan: '洞察快，决策才能快',
                        description: '行情瞬息万变，辅助交易帮您极速洞察，稳稳地把握住每一次机会。',
                        tags: [],
                        bullets: [],
                        metrics: [],
                        ctaLabel: '',
                        ctaTo: '/site/assist-trading'
                    },
                    {
                        key: 'auto-trading',
                        label: '自动交易',
                        slogan: '您所愿，AI所往',
                        description: '让交易像设定投资偏好一样简单。设定您的风险底线，AI 在边界内自主判断决策、持续交易。',
                        tags: [],
                        bullets: [],
                        metrics: [],
                        ctaLabel: '',
                        ctaTo: '/site/auto-trading'
                    },
                    {
                        key: 'asset-allocation',
                        label: '资产配置',
                        slogan: '忽略细节，关注您的目标',
                        description: 'AI 资产管理：您只需要关心想要什么结果，怎么配比、何时调仓，系统自动完成。',
                        tags: ['目标', '配置', 'AI管理'],
                        bullets: ['结果导向 — 关心目标而非过程', '主动管理 — 偏离自动校正', '持续优化 — 市场变化即调整'],
                        metrics: [
                            {title: '视角', value: '组合', note: '全局掌控组合结构', trend: 'stable'},
                            {title: '目标', value: '时间', note: '长期目标持续跟踪', trend: 'up'},
                            {title: '风险管控', value: '对冲', note: '风险敞口自动管理', trend: 'focus'},
                            {title: '全链路追溯', value: '透明', note: 'AI 决策过程可查', trend: 'stable'}
                        ],
                        ctaLabel: '',
                        ctaTo: '/site/asset-allocation'
                    }
                ],
                proofItems: [
                    {title: '品牌导向的产品表达', description: '先讲价值与方向，再讲功能细节。', tags: ['品牌', '官网']},
                    {
                        title: '系统化的能力组织',
                        description: '把辅助交易、自动交易与资产配置收敛为同一体系。',
                        tags: ['体系', '组织']
                    },
                    {title: '风险先于动作', description: '把边界和限制明确放在执行之前。', tags: ['风控', '边界']}
                ],
                exploreTitle: {
                    eyebrow: '继续了解',
                    title: '先看机制，再看 POC',
                    description: '资源讲清楚系统如何工作，POC 展示产品如何落地。'
                },
                exploreCards: [
                    {
                        eyebrow: '资源',
                        title: '工作机制与风控安全',
                        description: '了解系统如何组织输入、判断、执行与边界控制。',
                        actionLabel: '查看资源',
                        path: '/site/how-it-works'
                    },
                    {
                        eyebrow: 'POC',
                        title: '辅助交易与自动交易演示',
                        description: '通过 POC 结构，直观看到两个核心方向的价值与流程。',
                        actionLabel: '进入 POC',
                        path: '/site/poc'
                    }
                ],
                ctaEyebrow: '抢先体验',
                ctaTitle: '准备好体验 D-Sense 了吗？',
                ctaDescription: '为您的罗马奠基。注册即可开启完整交易体验。'
            },
            productPage: {
                hero: {
                    eyebrow: '产品功能',
                    title: '三大能力，覆盖从判断到配置的关键路径',
                    description: '不是把更多功能堆给你，而是把真正影响交易结果的能力组织成可用系统。',
                    tags: ['辅助交易', '自动交易', '资产配置', '统一工作台'],
                    panelLabel: '产品矩阵',
                    panelTitle: '一套产品，支撑三种关键能力',
                    panelDescription: '在同一个系统里完成观察、判断、执行和资产视图。',
                    assistText: '产品页面负责把首页三大方向完整展开。',
                    topCardLabel: '价值',
                    topCardTitle: '少切换，少断层',
                    topCardText: '让判断与执行尽量留在同一套工作流中',
                    bottomCardLabel: '体验',
                    bottomCardTitle: '品牌化呈现',
                    bottomCardText: '官网叙事围绕方向、场景和价值组织'
                },
                directions: [
                    {
                        eyebrow: '辅助交易',
                        title: '盘前打开工作台，AI 已经标好了关键信号',
                        description: '每天打开工作台，系统已经把市场结构变化、关键价格区间和 AI 判断整理好。你不用从零开始翻图，而是直接从信号看起，更快形成自己的判断。',
                        bullets: ['打开工作台就能看到聚合后的关键信号', 'AI 已经帮你标注了结构和风险点', '判断效率更高，盯盘压力更小'],
                        metrics: [
                            {title: '适用场景', value: '盘前/盘中', note: '辅助观察与形成判断', trend: 'stable'},
                            {title: '输出方式', value: '结构化', note: '建议、标签、风险点可读', trend: 'up'}
                        ],
                        featuresTitle: {
                            eyebrow: '核心能力',
                            title: '四个步骤，从洞察到交易',
                            description: '从数据聚合、信号聚合、边界判断到快捷交易的完整能力链路。'
                        },
                        flowSteps: [
                            {
                                title: '数据聚合',
                                description: '整合行情、订单簿与链上数据，形成结构化输入',
                                tags: ['行情', '订单簿', '链上']
                            },
                            {
                                title: '信号聚合',
                                description: '多维度信号交叉验证，剔除噪音突出关键信息',
                                tags: ['信号', '交叉验证', '降噪']
                            },
                            {
                                title: '边界判断',
                                description: '基于风控规则和账户状态判断当前可操作边界',
                                tags: ['风控', '账户状态', '边界']
                            },
                            {
                                title: '快捷交易',
                                description: '一键执行预定义策略，无需频繁切换页面操作',
                                tags: ['一键执行', '策略', '效率']
                            }
                        ],
                        usageTitle: {
                            eyebrow: '使用流程',
                            title: '三步进入状态',
                            description: '打开工作台到开始交易，只需三个步骤。'
                        },
                        usageSteps: [
                            {
                                title: '打开工作台',
                                description: '进入工作台，行情数据，技术指标已就绪',
                                detail: '无需手动翻图查表'
                            },
                            {
                                title: '查看/选择 品种',
                                description: '快速浏览各品种信号与关键位',
                                detail: '聚焦有行情的品种'
                            },
                            {title: '一键开仓', description: '确认边界后直接执行开仓操作', detail: '减少操作步骤'}
                        ]
                    },
                    {
                        eyebrow: '人人可得，人人可享',
                        cardEyebrow: '金融科技，普惠个人',
                        title: 'AI 自动交易，从零开始',
                        cardTitle: '零基础AI自动交易',
                        description: '只要设定风险偏好，AI 就能持续为你自动交易。',
                        cardDescription: '无需写策略，无需盯盘。只要设定您的风险承受预期，AI 自动匹配适合的资产，持续迭代交易。让您的精力从市场中释放出来。',
                        bullets: ['零门槛 - 不用写策略，不懂交易也能用', '设风险 - 告诉 AI 你的风险偏好，其余交给它', '全自动 - 7×24 持续运行，彻底放手'],
                        metrics: [
                            {title: '操作门槛', value: '零基础', note: '只需设定风险承受能力', trend: 'up'},
                            {title: '运行模式', value: '全自动', note: 'AI 持续匹配资产并执行交易', trend: 'focus'}
                        ],
                        featuresTitle: {
                            eyebrow: '核心能力',
                            title: '六步闭环，AI 自主迭代交易',
                            description: '从数据聚合到状态回流，AI 在边界内自主判断、持续迭代交易。'
                        },
                        flowSteps: [
                            {
                                title: '数据聚合',
                                description: '聚合多源市场数据，形成统一的输入基础',
                                tags: ['多源', '聚合', '输入']
                            },
                            {
                                title: '市场感知',
                                description: 'AI 持续扫描市场状态与结构变化，识别交易机会',
                                tags: ['感知', '结构', '扫描']
                            },
                            {
                                title: '交易规划',
                                description: '综合市场结构与风险边界，自主形成交易判断',
                                tags: ['分析', '判断', '决策']
                            },
                            {
                                title: '风控校验',
                                description: '在用户设定的风险边界内校验是否允许执行',
                                tags: ['边界', '校验', '准入']
                            },
                            {
                                title: '自动执行',
                                description: 'AI 在边界内自主执行交易操作',
                                tags: ['下单', '执行', '追踪']
                            },
                            {
                                title: '状态回流',
                                description: '执行结果与状态持续回流，进入下一轮迭代',
                                tags: ['反馈', '状态', '交替']
                            }
                        ]
                    },
                    {
                        eyebrow: '资产配置',
                        title: 'AI 自动管理您的整套资产配置',
                        description: '设定您的目标和风险偏好，从组合构建到动态再平衡，AI 全权负责。您只需要关注结果。',
                        bullets: ['结果导向 — 关心目标而非过程', '主动管理 — 偏离自动校正', '持续优化 — 市场变化即调整'],
                        metrics: [
                            {title: '视角', value: '组合', note: '全局掌控组合结构', trend: 'stable'},
                            {title: '目标', value: '时间', note: '长期目标持续跟踪', trend: 'up'},
                            {title: '风险管控', value: '对冲', note: '风险敞口自动管理', trend: 'focus'},
                            {title: '全链路追溯', value: '透明', note: 'AI 决策过程可查', trend: 'stable'}
                        ],
                        featuresTitle: {
                            eyebrow: '核心能力',
                            title: '六个步骤，AI 全程管理你的配置',
                            description: '从目标分解到动态调仓，AI 在边界内全程管理你的资产配置策略。'
                        },
                        flowSteps: [
                            {
                                title: '目标分解',
                                description: '将长期投资目标拆解为可执行的阶段性任务，明确收益预期与风险容忍度',
                                tags: ['分解', '目标', '阶段']
                            },
                            {
                                title: '资产配置规划',
                                description: '基于投资目标与市场环境，规划大类资产配置比例与投资策略',
                                tags: ['规划', '配比', '策略']
                            },
                            {
                                title: '目标资产比选',
                                description: '在各资产类别中筛选优质标的，通过多维度比选确定最佳组合候选',
                                tags: ['筛选', '比选', '标的']
                            },
                            {
                                title: '组合构建与交易执行',
                                description: '按既定配比执行建仓操作，完成组合构建并持续跟踪建仓进度',
                                tags: ['构建', '执行', '建仓']
                            },
                            {
                                title: '风险监控与合规审查',
                                description: '实时监控组合风险敞口与合规指标，确保投资始终在预设边界内运行',
                                tags: ['监控', '风控', '合规']
                            },
                            {
                                title: '资产价值跟踪与调仓',
                                description: '持续跟踪资产价值变化，偏离目标阈值时自动触发调仓，让组合回归目标轨道',
                                tags: ['跟踪', '调仓', '再平衡']
                            }
                        ],
                        usageTitle: {
                            eyebrow: '使用流程',
                            title: '三步开启 AI 资产管理',
                            description: '从设定目标到长期运行，只需三步。'
                        },
                        usageSteps: [
                            {
                                title: '设定投资目标',
                                description: '告诉 AI 你的风险偏好和收益预期',
                                detail: '一次设定，持续跟踪'
                            },
                            {
                                title: 'AI 自动运行',
                                description: '组合构建、动态监控、再平衡全部由 AI 自动完成',
                                detail: '无需手动干预'
                            },
                            {
                                title: '随时查看状态',
                                description: '打开面板即可查看组合表现和 AI 的调整记录',
                                detail: '过程透明可追溯'
                            }
                        ]
                    }
                ]
            },
            resources: {
                proofBarItems: ['市场输入', '结构化判断', '风控校验', '执行回流'],
                howItWorksHero: {
                    eyebrow: '工作机制',
                    title: '交易链路，透明可控',
                    description: 'D-Sense 不是黑盒信号工具，而是把输入、判断、风控和执行组织成可解释流程。',
                    panelLabel: '流程闭环',
                    panelTitle: '从输入到执行循环',
                    panelDescription: '每一步都有明确职责，帮助交易者理解动作为什么发生。'
                },
                howItWorksSteps: [
                    {
                        title: '市场输入',
                        description: '汇总行情、结构与上下文，形成可用于判断的输入。',
                        detail: '让市场观察不再分散在多个页面。'
                    },
                    {
                        title: '策略判断',
                        description: 'AI 和规则共同形成结构化建议。',
                        detail: '结论、标签与理由可一起查看。'
                    },
                    {
                        title: '风控校验',
                        description: '风险边界先判断，再决定是否允许动作。',
                        detail: '不满足条件时不进入执行。'
                    },
                    {
                        title: '执行回流',
                        description: '执行结果和状态会回到统一视图。',
                        detail: '帮助你持续追踪策略与结果。'
                    }
                ],
                howItWorksFlowSteps: [
                    {
                        title: '数据聚合',
                        description: '整合多源行情、订单簿与链上数据，形成结构化的统一输入基础',
                        tags: ['行情', '链上', '多源']
                    },
                    {
                        title: '市场感知',
                        description: 'AI 持续扫描市场状态与结构变化，识别关键信号与交易机会',
                        tags: ['感知', '扫描', '识别']
                    },
                    {
                        title: '交易规划',
                        description: '综合市场结构与风险边界，自主形成交易判断与策略规划',
                        tags: ['分析', '判断', '规划']
                    },
                    {
                        title: '风控校验',
                        description: '在用户设定的风险边界内逐项校验，确保每笔动作合规且安全',
                        tags: ['边界', '校验', '准入']
                    },
                    {
                        title: '自动执行',
                        description: 'AI 在边界内自主执行交易操作，持续追踪订单执行状态',
                        tags: ['下单', '执行', '追踪']
                    },
                    {
                        title: '状态回流',
                        description: '执行结果与市场状态持续回流至系统，进入下一轮分析迭代',
                        tags: ['反馈', '迭代', '闭环']
                    }
                ],
                howItWorksAiDecision: [
                    {
                        eyebrow: 'AI 感知',
                        title: 'AI 如何理解市场状态',
                        description: '系统将多源市场数据转化为 AI 可理解的结构化输入，通过大语言模型识别市场结构、趋势变化与异常信号，生成可读的分析结论。',
                        bullets: [
                            '整合 K 线、订单簿、链上等多维数据来源',
                            'AI 识别关键价格区间与市场结构变化',
                            '信号聚合去噪，突出高置信度的交易机会',
                            '以自然语言输出结构化的分析结论'
                        ],
                        metrics: [
                            {title: '数据维度', value: '50+', note: '技术指标与市场信号', trend: 'up'},
                            {title: '分析频率', value: '实时', note: '持续扫描市场状态', trend: 'focus'}
                        ]
                    },
                    {
                        eyebrow: 'AI 决策',
                        title: '从分析到执行，AI 如何做交易决策',
                        description: '系统构建包含市场状态、风险边界和历史数据的上下文，调用大语言模型生成结构化的交易建议，再由解析引擎转换为可执行的交易动作。',
                        bullets: [
                            '动态构建包含多维度上下文的决策 Prompt',
                            '大模型输出结构化的交易决策（方向/仓位/时机）',
                            '解析引擎将模型输出转为可执行的交易指令',
                            '风控校验通过后才提交至交易所执行'
                        ],
                        metrics: [
                            {title: '决策时延', value: '<30s', note: '从分析到生成交易建议', trend: 'up'},
                            {title: '可追溯性', value: '全链路', note: 'Prompt→输出→动作均可回溯', trend: 'focus'}
                        ]
                    }
                ],
                howItWorksBlocks: [
                    {
                        title: '输入层 — 多源数据聚合',
                        description: '整合交易所行情、链上数据、技术指标与市场舆情，构建统一的数据输入管道，经过清洗与标准化后供上层使用。',
                        tags: ['行情 API', '链上数据', '技术指标', '数据清洗'],
                        iconName: 'database'
                    },
                    {
                        title: '判断层 — AI 智能分析引擎',
                        description: '融合大语言模型与规则引擎，对市场状态进行多维度分析。动态构建 Prompt 并调用模型，输出结构化信号与交易建议。',
                        tags: ['LLM', 'Prompt 工程', '规则引擎', '信号聚合'],
                        iconName: 'brain'
                    },
                    {
                        title: '执行层 — 风控与交易链路',
                        description: '将风控校验嵌入执行链路前端，确保每笔交易都在预设边界内执行。结果实时回流，形成完整的交易闭环。',
                        tags: ['风控校验', '订单执行', '状态同步', '异常处理'],
                        iconName: 'shield'
                    }
                ],
                howItWorksSections: {
                    architecture: {
                        eyebrow: '系统架构',
                        title: '从数据到执行的六步闭环',
                        description: '每一个环节都有明确职责，AI 在边界内自主运行、持续迭代。'
                    },
                    aiDecision: {
                        eyebrow: 'AI 智能',
                        title: '市场感知与交易决策机制',
                        description: '系统如何理解市场信号，又如何将这些信号转化为可执行的交易决策。'
                    },
                    layers: {
                        eyebrow: '技术架构',
                        title: '三层架构，各司其职',
                        description: '系统由三个核心层级构成，每一层都承担明确的技术职责。'
                    },
                    demo: {
                        eyebrow: '实时演示',
                        title: '看看 AI 如何感知市场',
                        description: '模拟 AI 分析面板，展示系统如何识别与标注市场信号。'
                    }
                },
                howItWorksDemo: {
                    symbol: 'BTC/USDT',
                    symbolChange: '+2.34%',
                    symbolChangeUp: true,
                    price: '67,432.50',
                    priceChange: '+1,542.80',
                    priceChangeUp: true,
                    signalTags: [
                        {label: '结构突破', type: 'signal'},
                        {label: '量能放大', type: 'signal'},
                        {label: '波动率偏高', type: 'risk'},
                        {label: 'AI 偏多', type: 'info'}
                    ],
                    aiJudgmentTitle: 'AI 判断',
                    aiJudgmentValue: '偏多 · 中等信心',
                    aiJudgmentNote: '价格突破近期阻力，量能配合',
                    keyLevelTitle: '关键位',
                    keyLevelValue: '67,200 / 68,500',
                    keyLevelNote: '上方阻力 68,500，下方支撑 67,200'
                },
                howItWorksMetrics: [
                    {title: '处理步骤', value: '6步', note: '从数据聚合到状态回流的完整闭环', trend: 'focus'},
                    {title: '决策时延', value: '<30s', note: '从分析到生成交易建议', trend: 'up'},
                    {title: '数据维度', value: '50+', note: '技术指标与市场信号', trend: 'up'}
                ],
                howItWorksCta: {
                    eyebrow: '开始使用',
                    title: '理解机制，开始体验',
                    description: '了解系统如何工作后，通过 POC 验证是否适合你的交易方式。'
                },
                riskHero: {
                    eyebrow: '风控安全',
                    title: '边界先行，动作有度',
                    description: '风控不是附属功能，而是系统是否值得长期使用的前提。',
                    panelLabel: '风险前置',
                    panelTitle: '让边界先于动作，让安全先于效率',
                    panelDescription: '当风险不清晰时，系统应该先收缩动作，而不是放大风险。'
                },
                riskGuardItems: [
                    {
                        title: '风险模式先行',
                        description: '先定义偏好和边界，再决定是否允许执行。',
                        bullets: ['保守/谨慎/稳健/积极/激进 五档可选', '阈值前置，动作前自动校验', '不符合条件不进入执行']
                    },
                    {
                        title: '止盈止损与仓位控制',
                        description: '把回撤与暴露控制在可接受范围内。',
                        bullets: ['仓位上限自动约束', '止盈止损联动触发', '避免无边界加仓']
                    },
                    {
                        title: '权限与密钥隔离',
                        description: '用最小权限原则处理敏感操作。',
                        bullets: ['API 权限分层管理', '密钥加密隔离存储', '异常优先回退']
                    }
                ],
                riskMatrixItems: [
                    {
                        title: '执行前校验',
                        description: '每笔动作发出前，系统先校验风险条件是否满足。',
                        tags: ['前置校验', '条件判断'],
                        iconName: 'shield'
                    },
                    {
                        title: '权限边界',
                        description: '接口和账户能力被约束在必要范围内，防止越权操作。',
                        tags: ['权限管控', '安全隔离'],
                        iconName: 'layers'
                    },
                    {
                        title: '持续回流',
                        description: '执行结果与风险状态持续回到统一视图，形成监控闭环。',
                        tags: ['状态回流', '持续追踪'],
                        iconName: 'waypoints'
                    }
                ],
                riskSections: {
                    guard: {
                        eyebrow: '风控规则',
                        title: '三道防线，层层把关',
                        description: '从风险偏好到仓位管理再到权限控制，逐层收紧安全边界。'
                    },
                    matrix: {
                        eyebrow: '执行保障',
                        title: '每一步都在规则内运行',
                        description: '系统在执行链路的每个节点嵌入校验逻辑，确保动作始终在预设边界内。'
                    }
                },
                riskCta: {
                    eyebrow: '开始使用',
                    title: '让风控走在交易前面',
                    description: '了解风险边界如何保护你的每一笔交易。'
                },
                riskMetrics: [
                    {title: '风控层级', value: '3道', note: '模式→仓位→权限逐层收紧', trend: 'focus'},
                    {title: '校验时机', value: '执行前', note: '动作发出前自动校验', trend: 'up'},
                    {title: '权限粒度', value: '分层', note: 'API 权限最小化管理', trend: 'stable'}
                ]
            },
            pocPage: {
                hero: {
                    eyebrow: 'POC',
                    title: '通过 POC，更直观看见产品如何工作',
                    description: '这里不讲空泛概念，而是把辅助交易与自动交易的典型场景、价值与交互结构展示给你。',
                    tags: ['辅助交易', '自动交易', '场景展示', '功能演示'],
                    panelLabel: 'POC 入口',
                    panelTitle: '两个方向，两种使用体验',
                    panelDescription: '分别查看辅助交易和自动交易如何承接不同的使用目标。',
                    assistText: '先看 POC，再判断是否适合你的交易方式。',
                    topCardLabel: '方向一',
                    topCardTitle: '辅助交易',
                    topCardText: '看清市场、形成判断、准备动作',
                    bottomCardLabel: '方向二',
                    bottomCardTitle: '自动交易',
                    bottomCardText: '让规则持续执行，让结果持续回流'
                },
                entries: [
                    {
                        title: '辅助交易 POC',
                        slogan: '更快看见，更稳判断',
                        description: '查看从市场观察、信号理解到交易辅助建议的完整演示。',
                        tags: ['市场观察', 'AI 解释', '风险提醒'],
                        path: '/site/poc/assist-trading'
                    },
                    {
                        title: '自动交易 POC',
                        slogan: '规则先行，执行不停',
                        description: '查看策略如何在边界内持续执行，并同步结果与状态。',
                        tags: ['规则执行', '风控前置', '结果回流'],
                        path: '/site/poc/auto-trading'
                    },
                    {
                        title: '形态识别 POC',
                        slogan: '每根K线，都被看清',
                        description: '查看K线形态自动识别与AI深度分析的完整演示。',
                        tags: ['K线形态', 'AI分析', '回测验证'],
                        path: '/site/poc/pattern'
                    }
                ],
                features: [
                    {
                        title: '场景导向展示',
                        description: '不只展示页面，而是讲清楚页面解决什么问题。',
                        tags: ['场景', '价值']
                    },
                    {title: '流程导向表达', description: '看到模块之间的先后关系和信息流。', tags: ['流程', '信息流']},
                    {title: '边界导向说明', description: '在演示时同步说明限制条件和风险边界。', tags: ['边界', '风控']}
                ],
                faqIntro: {
                    eyebrow: '补充说明',
                    title: '看 POC 之前，先知道这些边界',
                    description: '通过少量高频问答，把使用前最重要的信息说清楚。'
                },
                faqItems: [
                    {
                        question: '这套系统适合谁？',
                        answer: '适合希望减少盯盘、提高判断效率、并愿意理解风险边界的个人交易者。'
                    },
                    {
                        question: '系统会自动执行交易吗？',
                        answer: '支持半自动和自动化执行，但是否启用，应由你自己根据风险理解做决定。'
                    },
                    {
                        question: '为什么还要看 POC？',
                        answer: 'POC 用来帮助你先理解产品逻辑、适用场景和边界，再决定是否开始体验。'
                    },
                    {question: '如何开始使用？', answer: '已有邀请码可直接注册，没有邀请码可先在联系页提交信息。'}
                ]
            },
            assistPocPage: {
                hero: {
                    eyebrow: '辅助交易 POC',
                    title: '把握市场，而非盲目决策',
                    description: '它帮助交易者在高波动环境里更快完成观察、判断与动作准备，但最终控制权仍在用户手中。',
                    tags: ['市场信号', 'AI 辅助', '风险提醒', '行动建议'],
                    panelLabel: '核心口号',
                    panelTitle: '更快看见，更稳判断',
                    panelDescription: '先用系统帮助你看清机会，再决定是否行动。',
                    assistText: '辅助交易强调帮助判断，而不是替代判断。',
                    topCardLabel: '使用场景',
                    topCardTitle: '盘前 / 盘中',
                    topCardText: '适合高频观察与快速形成交易判断',
                    bottomCardLabel: '结果',
                    bottomCardTitle: '更少犹豫',
                    bottomCardText: '把观察、理解和准备动作放进同一流程'
                },
                features: [
                    {title: '市场信号聚合', description: '集中看关键标签和市场变化。', tags: ['行情', '标签']},
                    {title: 'AI 辅助判断', description: '快速获得结构化解释与建议。', tags: ['AI', '解释']},
                    {title: '风险前置提醒', description: '在动作前看到重要限制条件。', tags: ['风险', '提醒']},
                    {title: '行动建议输出', description: '把判断结果组织成更易行动的形式。', tags: ['建议', '执行前']},
                ],
                flowSteps: [
                    {
                        title: '观察市场',
                        description: '集中查看关键市场变化与上下文。',
                        detail: '帮助你快速完成第一轮过滤。'
                    },
                    {title: '理解信号', description: 'AI 帮助组织原因、标签与倾向。', detail: '让结论更容易被理解。'},
                    {title: '确认边界', description: '先看风险提醒和执行限制。', detail: '避免只看机会不看代价。'},
                    {title: '准备动作', description: '形成更清晰的下一步动作计划。', detail: '由你决定是否真的进入执行。'}
                ],
                stories: [
                    {
                        eyebrow: '价值一',
                        title: '帮助你更快看见机会',
                        description: '把分散的观察过程拉回同一工作台，减少切换成本。',
                        bullets: ['减少分散式看盘', '让信息更快收束', '帮助快速进入判断状态'],
                        metrics: [
                            {title: '观察方式', value: '统一', note: '关键输入集中展示', trend: 'stable'},
                            {title: '反应速度', value: '更快', note: '减少切换与犹豫', trend: 'up'}
                        ]
                    },
                    {
                        eyebrow: '价值二',
                        title: '让判断更容易解释',
                        description: '不只是给信号，还把判断依据和风险一起表达出来。',
                        bullets: ['信号与理由一起看', '风险和机会同时表达', '让判断更容易复盘'],
                        metrics: [
                            {title: '输出形式', value: '结构化', note: '标签、说明、建议联动', trend: 'up'},
                            {title: '可复盘性', value: '更强', note: '更容易追溯判断过程', trend: 'focus'}
                        ]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: '核心能力',
                        title: '从信号到行动的完整链路',
                        description: '不靠堆砌功能，而是把辅助交易的关键能力组织成可用的系统路径。'
                    },
                    flow: {
                        eyebrow: '典型流程',
                        title: '每天打开工作台，四步完成判断准备',
                        description: '从观察到决策，系统帮你把分散的步骤收拢为一条可重复的工作流。'
                    },
                    stories: {
                        eyebrow: '价值说明',
                        title: '辅助交易解决的两个核心问题',
                        description: '信息过载时如何更快看见机会，判断复杂时如何更容易解释。'
                    },
                    demo: {
                        eyebrow: '实时演示',
                        title: '交易工作台展示',
                        description: '从实时行情、持仓到历史交易，完整展示交易工作台的核心数据面板。'
                    },
                    cta: {
                        primaryLabel: '获取邀请码',
                        primaryTo: '/site/assist-trading'
                    }
                }
            },
            autoPocPage: {
                hero: {
                    eyebrow: '自动交易 POC',
                    title: '定好目标，AI替你交易',
                    description: '只需设定你的风险承受能力和目标盈利率，AI自动匹配机会、管理仓位、优化资金利用率——不需要编写策略，不需要盯盘。',
                    tags: ['风险偏好', '目标收益', '资金利用', 'AI自主交易'],
                    panelLabel: '核心理念',
                    panelTitle: '你定目标，AI负责执行',
                    panelDescription: '交易不需要复杂的策略编程。告诉AI你能承受多少风险、想要多少回报，它会自动找到最优路径。',
                    assistText: '设定好你的风险偏好和收益目标，剩下的交给AI。',
                    topCardLabel: '适用场景',
                    topCardTitle: '简单直接',
                    topCardText: '不需要懂编程、不需要写策略，适合所有交易者',
                    bottomCardLabel: '核心优势',
                    bottomCardTitle: 'AI自主决策',
                    bottomCardText: 'AI实时分析市场，在你设定的边界内自主寻找最优交易机会'
                },
                features: [
                    {
                        title: '风险偏好设定',
                        description: '设定你的最大可承受亏损比例，AI严格在此边界内运行。',
                        tags: ['风控', '安心']
                    },
                    {
                        title: '目标收益管理',
                        description: '设定预期盈利率，AI自动寻找匹配该目标的市场机会。',
                        tags: ['目标', '收益']
                    },
                    {
                        title: '资金利用优化',
                        description: 'AI智能分配每笔交易的资金比例，最大化资金使用效率。',
                        tags: ['效率', '仓位']
                    },
                    {
                        title: 'AI自主交易执行',
                        description: '7×24持续监控市场，自动完成择时、开仓、平仓全流程。',
                        tags: ['自动', '全天候']
                    },
                ],
                flowSteps: [
                    {
                        title: '设定风险边界',
                        description: '确定你能承受的最大回撤和单笔亏损上限。',
                        detail: 'AI的所有决策都在此边界内执行'
                    },
                    {
                        title: '设定收益目标',
                        description: '设定期望的年化或季度盈利率目标。',
                        detail: 'AI以此为基准匹配合适的交易机会'
                    },
                    {
                        title: 'AI智能匹配',
                        description: 'AI实时分析市场，匹配符合你风险收益偏好的机会。',
                        detail: '结合资金利用率智能分配仓位'
                    },
                    {
                        title: '自动执行与优化',
                        description: 'AI自主完成交易执行，持续根据市场变化调整策略。',
                        detail: '每笔交易可追溯，收益清晰可见'
                    }
                ],
                stories: [
                    {
                        eyebrow: '价值一',
                        title: '毋需策略，只需目标',
                        description: '你不需要编写任何交易策略。告诉AI你的风险底线和收益期望，它会自动找到最适合的交易路径。',
                        bullets: ['零编程门槛，设定即运行', 'AI自动寻优匹配机会', '无需盯盘，全天候自主运行'],
                        metrics: [
                            {title: '上手难度', value: '极低', note: '只需设定两个参数即可开始', trend: 'up'},
                            {title: '决策方式', value: 'AI驱动', note: 'AI替代人工判断与执行', trend: 'stable'}
                        ]
                    },
                    {
                        eyebrow: '价值二',
                        title: '每一分钱都高效运转',
                        description: 'AI不仅帮你交易，更帮你管理资金——动态调整仓位比例，让你的资金利用率始终处于最优状态。',
                        bullets: ['智能仓位管理，动态分配资金', '实时调整资金利用率', '让闲置资金也能产生价值'],
                        metrics: [
                            {title: '资金效率', value: '最优', note: 'AI持续优化资金分配比例', trend: 'focus'},
                            {title: '收益来源', value: '透明', note: '每一笔盈亏可追溯可复盘', trend: 'up'}
                        ]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: '核心能力',
                        title: 'AI自主交易的四大能力',
                        description: '不需要编写策略，只需要设定偏好，AI替你完成一切。'
                    },
                    flow: {
                        eyebrow: '使用流程',
                        title: '四步设定，AI即刻运行',
                        description: '从设定偏好的那一刻起，AI就开始为你工作。'
                    },
                    stories: {
                        eyebrow: '核心价值',
                        title: '简单，但强大',
                        description: '把复杂的交易，变成简单的选择题。'
                    },
                    demo: {
                        eyebrow: '交互演示',
                        title: 'AI自动交易演示',
                        description: '配置您的API Key和风险偏好，体验AI自动交易的完整流程。'
                    }
                }
            },
            patternPocPage: {
                hero: {
                    eyebrow: '形态识别 POC',
                    title: 'AI驱动K线形态识别分析',
                    description: '从检测到分析，完整展示AI对K线形态的识别、回测与交易机会评估能力。',
                    tags: ['K线形态', 'AI分析', '回测', '交易信号'],
                    panelLabel: '核心能力',
                    panelTitle: '让每一根K线的形态都被看见',
                    panelDescription: '基于上百种K线形态检测算法，结合AI深度分析，辅助您发现潜在交易机会。',
                    assistText: '不需要手动翻图，系统自动标注形态并提供AI分析。',
                    topCardLabel: '形态检测',
                    topCardTitle: '自动识别',
                    topCardText: '系统自动检测K线形态，支持1m到1D多种周期',
                    bottomCardLabel: 'AI分析',
                    bottomCardTitle: '深度解读',
                    bottomCardText: 'AI对形态进行综合分析，给出入场、止损、目标位建议'
                },
                features: [
                    {
                        title: '多形态检测',
                        description: '支持锤子线、吞没形态、启明星等上百种经典K线形态的自动检测。',
                        tags: ['形态', '覆盖']
                    },
                    {
                        title: 'AI深度分析',
                        description: '对接大模型对形态进行多维度分析，包括入场价格、目标价位和止损建议。',
                        tags: ['AI', '分析']
                    },
                    {
                        title: '历史回测',
                        description: '对每种形态进行历史回测，统计胜率、盈亏比等关键指标，辅助决策。',
                        tags: ['回测', '数据']
                    },
                    {
                        title: '区域识别',
                        description: '自动识别支撑阻力区、盘整箱体等区域形态，辅助判断关键价格区间。',
                        tags: ['区域', '支撑阻力']
                    },
                ],
                flowSteps: [
                    {
                        title: '选择品种',
                        description: '选择目标交易对，系统自动获取K线数据。',
                        detail: '支持永续合约和现货，覆盖BTC、ETH等主流币种'
                    },
                    {
                        title: '形态检测',
                        description: '系统自动扫描K线，识别并标记所有形态信号。',
                        detail: '支持1m到1D多种周期，实时更新检测结果'
                    },
                    {
                        title: 'AI分析',
                        description: '对识别到的形态进行AI辅助分析，提供交易参考。',
                        detail: '给出入场、止损、目标位和风险等级建议'
                    },
                    {
                        title: '回测验证',
                        description: '查看形态的历史表现数据，辅助判断信号可靠性。',
                        detail: '统计胜率、盈亏比，查看历史盈亏记录'
                    }
                ],
                stories: [
                    {
                        eyebrow: '价值一',
                        title: '让形态识别更全面',
                        description: '上百种形态自动检测，从简单的锤子线到复杂的启明星，系统帮你发现可能遗漏的信号。',
                        bullets: ['自动扫描，无需手动翻图', '覆盖上百种经典形态', '支持多周期检测'],
                        metrics: [
                            {title: '检测效率', value: '自动', note: '实时监测，减少人工成本', trend: 'up'},
                            {title: '覆盖范围', value: '全面', note: '数百种形态自动识别', trend: 'focus'}
                        ]
                    },
                    {
                        eyebrow: '价值二',
                        title: '让分析更有深度',
                        description: 'AI不仅告诉你形态类型，还分析形态的可信度、潜在盈亏比，并给出具体的交易建议。',
                        bullets: ['AI多维度分析形态信号', '给出入场、止损、目标价位', '风险评估辅助决策'],
                        metrics: [
                            {title: '分析深度', value: '全面', note: '多维度综合分析', trend: 'up'},
                            {title: '参考价值', value: '实用', note: '具体价格建议和风险提示', trend: 'focus'}
                        ]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: '核心能力',
                        title: '从检测到分析，完整形态识别链路',
                        description: '不只是检测形态，而是把识别、分析和历史验证组织为完整的工具链条。'
                    },
                    flow: {
                        eyebrow: '典型流程',
                        title: '四步完成形态识别与分析',
                        description: '从选择品种到回测验证，系统帮你完成形态识别的完整工作流。'
                    },
                    stories: {
                        eyebrow: '价值说明',
                        title: '形态识别解决的三个核心问题',
                        description: '手动翻图效率低、信号遗漏、分析不深入——形态识别帮你扫除这些障碍。'
                    },
                    demo: {
                        eyebrow: '交互演示',
                        title: 'K线形态识别演示',
                        description: '通过K线形态识别，快速发现市场结构变化与交易机会。'
                    },
                    cta: {
                        primaryLabel: '获取邀请码',
                    }
                }
            },
            membershipPage: {
                hero: {
                    eyebrow: '会员体系',
                    title: '为不同阶段的投资者，匹配恰好的能力',
                    description: '从免费体验到专家级全自动交易，四档会员覆盖从洞察、执行到配置的完整路径。',
                    panelLabel: '在线开通',
                    panelTitle: '在线开通，即刻生效',
                    panelDescription: '在线提交开通请求，选择心仪等级并支付后即时生效，随时可在后台查看订阅状态与到期时间。'
                },
                sectionTitles: {
                    compare: {
                        eyebrow: '等级对比',
                        title: '四档会员，按需选择',
                        description: '定价与功能矩阵一览，DCA 计划上限随等级提升。'
                    },
                    features: {
                        eyebrow: '付费功能',
                        title: '每项能力，解决一个具体问题',
                        description: 'DCA 助手 / 对冲 / 形态识别 / 自动交易，按等级解锁。'
                    },
                    cta: {
                        eyebrow: '开通引导',
                        title: '准备好升级了吗？',
                        description: '在线选择等级并支付，开通即时生效，马上解锁对应能力。'
                    }
                },
                priceLabels: {monthly: '月付', yearly: '年付'},
                tiers: [
                    {
                        name: '免费', tagline: '体验核心功能', monthlyPrice: '0', yearlyPriceMonthly: '0',
                        features: [
                            {label: '订阅/观看报告', enabled: true},
                            {label: 'DCA 助手', enabled: false},
                            {label: '对冲交易', enabled: false},
                            {label: '形态识别快捷交易', enabled: false},
                            {label: '自动交易', enabled: false}
                        ]
                    },
                    {
                        name: '初级', tagline: '计划式自动买入', monthlyPrice: '5.9U', yearlyPriceMonthly: '3.9U/月',
                        features: [
                            {label: '订阅/观看报告', enabled: true},
                            {label: 'DCA 助手', enabled: true, note: '≤3 计划'},
                            {label: '对冲交易', enabled: false},
                            {label: '形态识别快捷交易', enabled: false},
                            {label: '自动交易', enabled: false}
                        ]
                    },
                    {
                        name: '专业', tagline: '交易与形态进阶', monthlyPrice: '15.9U', yearlyPriceMonthly: '9.9U/月',
                        highlight: true,
                        features: [
                            {label: '订阅/观看报告', enabled: true},
                            {label: 'DCA 助手', enabled: true, note: '≤10 计划'},
                            {label: '对冲交易', enabled: true},
                            {label: '形态识别快捷交易', enabled: true},
                            {label: '自动交易', enabled: false}
                        ]
                    },
                    {
                        name: '专家', tagline: 'AI 全自动交易', monthlyPrice: '59.9U', yearlyPriceMonthly: '29.9U/月',
                        features: [
                            {label: '订阅/观看报告', enabled: true},
                            {label: 'DCA 助手', enabled: true, note: '≤15 计划'},
                            {label: '对冲交易', enabled: true},
                            {label: '形态识别快捷交易', enabled: true},
                            {label: '自动交易', enabled: true}
                        ]
                    }
                ],
                featureIntros: [
                    {
                        title: 'DCA 助手',
                        description: '按品种与指标条件建计划，系统监控已收盘 K 线，边沿触发自动市价买入现货。单次或循环，暂停恢复自由控制。',
                        tags: ['计划式', '条件触发', '全自动买入']
                    },
                    {
                        title: '对冲交易',
                        description: '单腿/跨品种对冲，保护腿自动重挂，止盈止损与敞口管理一体化。',
                        tags: ['单腿对冲', '保护腿', '敞口管理']
                    },
                    {
                        title: '形态识别快捷交易',
                        description: 'AI 识别关键形态，一键快捷下单，让判断更快落地。',
                        tags: ['形态识别', '快捷下单']
                    },
                    {
                        title: '自动交易',
                        description: '设定风险底线，AI 在边界内自主判断决策、持续交易。',
                        tags: ['AI 决策', '边界内自主', '持续交易']
                    }
                ],
                faqIntro: {
                    eyebrow: 'FAQ',
                    title: '常见问题',
                    description: '关于会员与开通的常见疑问。'
                },
                faqItems: [
                    {
                        question: '如何开通会员？',
                        answer: '在价格页选择目标等级并完成支付，会员即时生效；到期前系统会提醒续费，可随时在会员中心查看订阅状态。'
                    },
                    {
                        question: '会员到期后会怎样？',
                        answer: '到期自动降级至下一有效订阅等级（无则免费）；超出新等级上限的 DCA 计划会自动暂停并站内通知，续费升级后手动恢复。'
                    },
                    {
                        question: 'DCA 计划上限如何计算？',
                        answer: '按等级映射：初级 3 个、专业 10 个、专家 15 个。仅统计未删除的计划，暂停与运行中的均计入。'
                    },
                    {
                        question: '年付有什么优惠？',
                        answer: '年付按折算月价计费：初级 3.9U/月、专业 9.9U/月、专家 29.9U/月，比月付更划算。'
                    }
                ]
            },
            contactPage: {
                hero: {
                    eyebrow: '开始使用',
                    title: '获取邀请码，开始体验 D-Sense',
                    description: '如果你还没有邀请码，可以先提交信息，我们会根据需求与阶段安排后续体验。',
                    tags: ['邀请码', '申请体验', '开始使用'],
                    panelLabel: '开始方式',
                    panelTitle: '有邀请码就注册，没有邀请码就先申请',
                    panelDescription: '把获取邀请码和开始体验组织成清晰路径。',
                    assistText: '先看清获取方式，再决定是否提交信息。',
                    topCardLabel: '开始更快',
                    topCardTitle: '两种入口',
                    topCardText: '直接注册，或先获取邀请码',
                    bottomCardLabel: '适用方式',
                    bottomCardTitle: '邀请制体验',
                    bottomCardText: '通过邀请码控制体验节奏'
                },
                contactModes: [
                    {title: '已有邀请码，直接注册', description: '拿到邀请码即可直接完成注册。', tags: ['注册', '开始']},
                    {
                        title: '没有邀请码，先申请',
                        description: '先提交信息，再进入邀请码获取流程。',
                        tags: ['邀请码', '申请']
                    },
                    {
                        title: '适合长期使用的交易者',
                        description: '更适合希望建立稳定工作流的个人交易者。',
                        tags: ['个人', '长期']
                    },
                ],
                applicationSteps: [
                    {title: '提交信息', description: '留下基本信息和使用意向。', detail: '补充交易场景与关注点。'},
                    {
                        title: '确认需求',
                        description: '确认方向和需求后进入发码阶段。',
                        detail: '重点确认风险理解和使用预期。'
                    },
                    {title: '获取邀请码', description: '收到邀请码后进入注册流程。', detail: '已有邀请码可直接注册。'},
                    {
                        title: '注册开始使用',
                        description: '完成注册后即可进入系统。',
                        detail: '登录后继续查看 POC 与产品能力。'
                    }
                ],
                faqIntro: {
                    eyebrow: '补充说明',
                    title: '先把常见问题看清',
                    description: '先看清注册方式和邀请码逻辑。'
                },
                faqItems: [
                    {
                        question: '为什么采用邀请码方式？',
                        answer: '当前公开站采用邀请制体验，便于控制节奏并保持高质量反馈。'
                    },
                    {question: '没有邀请码也能开始吗？', answer: '可以，先在联系页提交信息，我们会根据阶段安排后续体验。'},
                    {question: '注册后能做什么？', answer: '登录后可查看产品结构、体验系统入口，并继续了解交易工作流。'},
                    {question: '官网支持哪些语言？', answer: '本轮官网、登录页和注册页支持简体中文、繁体中文和英文。'}
                ]
            }
        },
        'fr-FR': {
            primaryCta: {label: 'S\'inscrire', path: '/register'},
            secondaryCta: {label: 'S\'abonner maintenant', path: '/membership'},
            waitlistCta: {label: 'Obtenir un code d\'invitation', path: '/site/contact'},
            tertiaryCta: {label: 'Se connecter', path: '/login'},
            siteNavItems: [
                {label: 'Accueil', path: '/'},
                {label: 'Trading Assisté', path: '/site/assist-trading'},
                {label: 'Trading Automatique', path: '/site/auto-trading'},
                {label: 'Allocation d\'Actifs', path: '/site/asset-allocation'},
                {
                    label: 'Ressources',
                    children: [
                        {label: 'Fonctionnement', path: '/site/how-it-works'},
                        {label: 'Sécurité & Risques', path: '/site/risk-control'}
                    ]
                },
                {
                    label: 'POC',
                    children: [
                        {label: 'Trading Assisté', path: '/site/poc/assist-trading'},
                        {label: 'Trading Automatique', path: '/site/poc/auto-trading'},
                        {label: 'Reconnaissance de Patterns', path: '/site/poc/pattern'}
                    ]
                },
                {label: 'Tarifs', path: '/site/membership'},
                {label: 'Contact', path: '/site/contact'},
            ],
            footerNavItems: [
                {label: 'Accueil', path: '/'},
                {label: 'Trading Assisté', path: '/site/assist-trading'},
                {label: 'Trading Automatique', path: '/site/auto-trading'},
                {label: 'Allocation d\'Actifs', path: '/site/asset-allocation'},
                {label: 'Fonctionnement', path: '/site/how-it-works'},
                {label: 'Sécurité & Risques', path: '/site/risk-control'},
                {label: 'Vue d\'ensemble POC', path: '/site/poc'},
                {label: 'Trading Assisté', path: '/site/poc/assist-trading'},
                {label: 'Trading Automatique', path: '/site/poc/auto-trading'},
                {label: 'Reconnaissance de Patterns', path: '/site/poc/pattern'},
                {label: 'Tarifs', path: '/site/membership'},
                {label: 'Contact', path: '/site/contact'}
            ],
            heroMetrics: [
                {
                    title: 'Opportunités',
                    value: 'Insight',
                    note: 'Identification assistée des signaux clés et des changements de structure',
                    trend: 'up'
                },
                {
                    title: 'Croissance',
                    value: 'Alignement',
                    note: 'Stratégie alignée sur les objectifs, suivi continu de l\'exécution',
                    trend: 'stable'
                },
                {
                    title: 'Sécurité',
                    value: 'Protection',
                    note: 'Conformité et contrôle des risques, investissement maîtrisé en permanence',
                    trend: 'focus'
                }
            ],
            home: {
                hero: {
                    eyebrow: 'Aller loin en toute sérénité',
                    title: 'Construisez votre propre BlackRock',
                    description: 'Votre gestionnaire d\'actifs IA qui évolue avec vous. Faites tourner un système de trading et de gestion d\'actifs durable selon vos règles, votre rythme et vos limites.',
                    tags: ['Trading Assisté', 'Trading Automatique', 'Allocation d\'Actifs', 'Risque Maîtrisé'],
                    panelLabel: 'La gestion d\'actifs devrait être aussi simple',
                    panelTitle: 'L\'IA fait le travail pénible, vous prenez les décisions',
                    panelDescription: 'Construction de portefeuille, allocation des risques, exécution de stratégie — des capacités institutionnelles désormais systématisées par l\'IA.',
                    assistText: 'Aider les traders à développer des capacités de décision et d\'exécution cohérentes sur le long terme grâce à un système plus clair.',
                    topCardLabel: 'Vision',
                    topCardTitle: 'Cohérence durable',
                    topCardText: 'Aider les traders à adopter une exécution stable, révisable et durable',
                    bottomCardLabel: 'Mode d\'accès',
                    bottomCardTitle: 'Accès sur invitation',
                    bottomCardText: 'Par défaut en français, possibilité de basculer en anglais'
                },
                section: {
                    eyebrow: 'Nos Solutions',
                    title: 'Trois axes, un seul écosystème',
                    description: 'De l\'information à l\'exécution en passant par l\'allocation, découvrez le parcours systématisé par l\'IA.'
                },
                products: [
                    {
                        key: 'assist-trading',
                        label: 'Trading Assisté',
                        slogan: 'Voir vite pour décider mieux',
                        description: 'Les marchés changent en un instant. Le Trading Assisté vous aide à discerner rapidement et à saisir chaque opportunité avec confiance.',
                        tags: [],
                        bullets: [],
                        metrics: [],
                        ctaLabel: '',
                        ctaTo: '/site/assist-trading'
                    },
                    {
                        key: 'auto-trading',
                        label: 'Trading Automatique',
                        slogan: 'Vos objectifs, l\'IA les réalise',
                        description: 'Rendez le trading aussi simple que de définir vos préférences d\'investissement. Fixez votre tolérance au risque, l\'IA décide et exécute en toute autonomie dans vos limites.',
                        tags: [],
                        bullets: [],
                        metrics: [],
                        ctaLabel: '',
                        ctaTo: '/site/auto-trading'
                    },
                    {
                        key: 'asset-allocation',
                        label: 'Allocation d\'Actifs',
                        slogan: 'Ignorez les détails, concentrez-vous sur vos objectifs',
                        description: 'Gestion d\'actifs IA : vous décidez du résultat, le système gère la répartition et le rééquilibrage automatiquement.',
                        tags: ['Objectifs', 'Allocation', 'Gestion IA'],
                        bullets: ['Axé résultats — concentrez-vous sur l\'objectif, pas sur le processus', 'Gestion active — corrections automatiques des écarts', 'Optimisation continue — s\'adapte aux changements du marché'],
                        metrics: [
                            {
                                title: 'Perspective',
                                value: 'Portefeuille',
                                note: 'Maîtrise globale de la structure du portefeuille',
                                trend: 'stable'
                            },
                            {
                                title: 'Objectif',
                                value: 'Temps',
                                note: 'Suivi continu des objectifs à long terme',
                                trend: 'up'
                            },
                            {
                                title: 'Contrôle des Risques',
                                value: 'Couverture',
                                note: 'Gestion automatique de l\'exposition au risque',
                                trend: 'focus'
                            },
                            {
                                title: 'Traçabilité',
                                value: 'Transparence',
                                note: 'Processus de décision IA consultable',
                                trend: 'stable'
                            }
                        ],
                        ctaLabel: '',
                        ctaTo: '/site/asset-allocation'
                    }
                ],
                proofItems: [
                    {
                        title: 'Proposition de valeur orientée marque',
                        description: 'Présenter d\'abord la valeur et la direction, puis les détails fonctionnels.',
                        tags: ['Marque', 'Site']
                    },
                    {
                        title: 'Organisation systémique des capacités',
                        description: 'Converger le Trading Assisté, le Trading Automatique et l\'Allocation d\'Actifs en un seul écosystème.',
                        tags: ['Système', 'Organisation']
                    },
                    {
                        title: 'Le risque avant l\'action',
                        description: 'Placer les limites clairement avant l\'exécution.',
                        tags: ['Risque', 'Limites']
                    }
                ],
                exploreTitle: {
                    eyebrow: 'Pour aller plus loin',
                    title: 'Comprendre le mécanisme, puis explorer le POC',
                    description: 'Les ressources expliquent le fonctionnement du système, les POC montrent comment le produit se concrétise.'
                },
                exploreCards: [
                    {
                        eyebrow: 'Ressources',
                        title: 'Fonctionnement et sécurité des risques',
                        description: 'Comprendre comment le système organise les entrées, le jugement, l\'exécution et le contrôle des limites.',
                        actionLabel: 'Voir les ressources',
                        path: '/site/how-it-works'
                    },
                    {
                        eyebrow: 'POC',
                        title: 'Démonstrations Trading Assisté et Automatique',
                        description: 'Visualiser la valeur et le flux des deux axes principaux à travers la structure POC.',
                        actionLabel: 'Accéder au POC',
                        path: '/site/poc'
                    }
                ],
                ctaEyebrow: 'Accès anticipé',
                ctaTitle: 'Prêt à découvrir D-Sense ?',
                ctaDescription: 'Jetez les bases de votre réussite. Inscrivez-vous pour accéder à l\'expérience complète.'
            },
            productPage: {
                hero: {
                    eyebrow: 'Fonctionnalités',
                    title: 'Trois capacités couvrant le parcours clé du jugement à l\'allocation',
                    description: 'Pas une accumulation de fonctionnalités, mais des capacités qui impactent réellement vos résultats de trading, organisées en un système utilisable.',
                    tags: ['Trading Assisté', 'Trading Automatique', 'Allocation d\'Actifs', 'Espace de travail unifié'],
                    panelLabel: 'Matrice Produit',
                    panelTitle: 'Un seul produit, trois capacités essentielles',
                    panelDescription: 'Observer, juger, exécuter et visualiser vos actifs dans un même système.',
                    assistText: 'Cette page développe en détail les trois axes présentés sur l\'accueil.',
                    topCardLabel: 'Valeur',
                    topCardTitle: 'Moins de changements, moins de ruptures',
                    topCardText: 'Garder le jugement et l\'exécution dans le même flux de travail',
                    bottomCardLabel: 'Expérience',
                    bottomCardTitle: 'Narration de marque',
                    bottomCardText: 'Le site s\'articule autour de la direction, du scénario et de la valeur'
                },
                directions: [
                    {
                        eyebrow: 'Trading Assisté',
                        title: 'Ouvrez l\'espace de travail, l\'IA a déjà repéré les signaux clés',
                        description: 'Chaque jour, le système organise les changements de structure du marché, les zones de prix clés et les analyses IA. Vous ne partez pas de zéro — vous commencez par les signaux pour former votre jugement plus rapidement.',
                        bullets: ['Signaux clés agrégés visibles dès l\'ouverture', 'L\'IA a déjà identifié les structures et les points de risque', 'Jugement plus efficace, moins de pression de surveillance'],
                        metrics: [
                            {
                                title: 'Contexte',
                                value: 'Pré-séance / En séance',
                                note: 'Observation assistée et formation du jugement',
                                trend: 'stable'
                            },
                            {
                                title: 'Format de sortie',
                                value: 'Structuré',
                                note: 'Recommandations, tags, points de risque lisibles',
                                trend: 'up'
                            }
                        ],
                        featuresTitle: {
                            eyebrow: 'Fonctionnalités clés',
                            title: 'Quatre étapes, de l\'insight à l\'exécution',
                            description: 'Une chaîne complète : agrégation des données, agrégation des signaux, vérification des limites et exécution rapide.'
                        },
                        flowSteps: [
                            {
                                title: 'Agrégation des données',
                                description: 'Intégration des cotations, carnets d\'ordres et données on-chain en entrée structurée',
                                tags: ['Cotations', 'Carnets d\'ordres', 'On-chain']
                            },
                            {
                                title: 'Agrégation des signaux',
                                description: 'Validation croisée multi-dimensionnelle des signaux, filtrage du bruit pour mettre en avant les informations clés',
                                tags: ['Signaux', 'Validation croisée', 'Filtrage']
                            },
                            {
                                title: 'Vérification des limites',
                                description: 'Détermination du périmètre opérationnel selon les règles de risque et l\'état du compte',
                                tags: ['Risque', 'État du compte', 'Limites']
                            },
                            {
                                title: 'Exécution rapide',
                                description: 'Exécution en un clic des stratégies prédéfinies, sans changement de page fréquent',
                                tags: ['Un clic', 'Stratégie', 'Efficacité']
                            }
                        ],
                        usageTitle: {
                            eyebrow: 'Processus',
                            title: 'Trois étapes pour être opérationnel',
                            description: 'De l\'ouverture de l\'espace de travail au lancement d\'un trade, seulement trois étapes.'
                        },
                        usageSteps: [
                            {
                                title: 'Ouvrir l\'espace de travail',
                                description: 'Accédez à l\'espace de travail, données de marché et indicateurs prêts',
                                detail: 'Pas de recherche manuelle de graphiques'
                            },
                            {
                                title: 'Consulter / Sélectionner un actif',
                                description: 'Parcourez rapidement les signaux et niveaux clés de chaque actif',
                                detail: 'Concentrez-vous sur les actifs actifs'
                            },
                            {
                                title: 'Ouvrir une position en un clic',
                                description: 'Exécutez l\'ouverture directement après confirmation des limites',
                                detail: 'Moins d\'étapes'
                            }
                        ]
                    },
                    {
                        eyebrow: 'Accessible à tous, profitable à tous',
                        cardEyebrow: 'Fintech pour tous',
                        title: 'Trading Automatique IA, dès le départ',
                        cardTitle: 'Trading Automatique IA sans connaissances préalables',
                        description: 'Il suffit de définir votre appétence au risque, l\'IA trade pour vous en continu.',
                        cardDescription: 'Pas besoin d\'écrire de stratégies ni de surveiller les écrans. Définissez votre tolérance au risque, l\'IA sélectionne les actifs adaptés et itère les transactions en continu. Libérez votre attention des marchés.',
                        bullets: ['Zéro barrière — pas besoin de savoir trader ou coder', 'Définissez le risque — dites à l\'IA votre tolérance, elle s\'occupe du reste', 'Tout automatique — 7j/24, fonctionnement continu, lâchez prise'],
                        metrics: [
                            {
                                title: 'Niveau requis',
                                value: 'Zéro',
                                note: 'Seulement définir votre tolérance au risque',
                                trend: 'up'
                            },
                            {
                                title: 'Mode de fonctionnement',
                                value: 'Full auto',
                                note: 'L\'IA sélectionne les actifs et exécute les transactions',
                                trend: 'focus'
                            }
                        ],
                        featuresTitle: {
                            eyebrow: 'Fonctionnalités clés',
                            title: 'Boucle en six étapes, l\'IA itère en autonomie',
                            description: 'De l\'agrégation des données au retour d\'état, l\'IA juge et itère en continu dans les limites définies.'
                        },
                        flowSteps: [
                            {
                                title: 'Agrégation des données',
                                description: 'Agrégation multi-source des données de marché pour une base d\'entrée unifiée',
                                tags: ['Multi-source', 'Agrégation', 'Entrée']
                            },
                            {
                                title: 'Perception du marché',
                                description: 'L\'IA scanne en continu l\'état du marché et les changements structurels pour identifier les opportunités',
                                tags: ['Perception', 'Structure', 'Analyse']
                            },
                            {
                                title: 'Planification des transactions',
                                description: 'Synthèse de la structure du marché et des limites de risque pour former un jugement autonome',
                                tags: ['Analyse', 'Jugement', 'Décision']
                            },
                            {
                                title: 'Validation des risques',
                                description: 'Vérification dans les limites de risque définies par l\'utilisateur avant d\'autoriser l\'exécution',
                                tags: ['Limites', 'Validation', 'Autorisation']
                            },
                            {
                                title: 'Exécution automatique',
                                description: 'L\'IA exécute les transactions en autonomie dans les limites définies',
                                tags: ['Ordre', 'Exécution', 'Suivi']
                            },
                            {
                                title: 'Retour d\'état',
                                description: 'Les résultats d\'exécution et l\'état remontent en continu pour la prochaine itération',
                                tags: ['Feedback', 'État', 'Itération']
                            }
                        ]
                    },
                    {
                        eyebrow: 'Allocation d\'Actifs',
                        title: 'L\'IA gère automatiquement l\'intégralité de votre allocation d\'actifs',
                        description: 'Définissez vos objectifs et votre appétence au risque. De la construction du portefeuille au rééquilibrage dynamique, l\'IA gère tout. Vous vous concentrez sur les résultats.',
                        bullets: ['Axé résultats — concentrez-vous sur l\'objectif, pas sur le processus', 'Gestion active — corrections automatiques des écarts', 'Optimisation continue — s\'adapte aux changements du marché'],
                        metrics: [
                            {
                                title: 'Perspective',
                                value: 'Portefeuille',
                                note: 'Maîtrise globale de la structure du portefeuille',
                                trend: 'stable'
                            },
                            {
                                title: 'Objectif',
                                value: 'Temps',
                                note: 'Suivi continu des objectifs à long terme',
                                trend: 'up'
                            },
                            {
                                title: 'Contrôle des Risques',
                                value: 'Couverture',
                                note: 'Gestion automatique de l\'exposition au risque',
                                trend: 'focus'
                            },
                            {
                                title: 'Traçabilité',
                                value: 'Transparence',
                                note: 'Processus de décision IA consultable',
                                trend: 'stable'
                            }
                        ],
                        featuresTitle: {
                            eyebrow: 'Fonctionnalités clés',
                            title: 'Six étapes, l\'IA gère intégralement votre allocation',
                            description: 'De la décomposition des objectifs au rééquilibrage dynamique, l\'IA gère l\'intégralité de votre stratégie d\'allocation.'
                        },
                        flowSteps: [
                            {
                                title: 'Décomposition des objectifs',
                                description: 'Décomposer les objectifs d\'investissement à long terme en étapes réalisables avec des attentes de rendement et une tolérance au risque claires',
                                tags: ['Décomposition', 'Objectifs', 'Étapes']
                            },
                            {
                                title: 'Planification de l\'allocation',
                                description: 'Planifier les ratios d\'allocation par classe d\'actifs et la stratégie d\'investissement selon les objectifs et l\'environnement de marché',
                                tags: ['Planification', 'Ratios', 'Stratégie']
                            },
                            {
                                title: 'Sélection des actifs cibles',
                                description: 'Filtrer les actifs de qualité dans chaque catégorie et déterminer les meilleurs candidats par comparaison multi-dimensionnelle',
                                tags: ['Filtrage', 'Comparaison', 'Actifs']
                            },
                            {
                                title: 'Construction et exécution du portefeuille',
                                description: 'Exécuter la construction des positions selon les ratios définis et suivre la progression',
                                tags: ['Construction', 'Exécution', 'Position']
                            },
                            {
                                title: 'Surveillance des risques et conformité',
                                description: 'Surveiller en temps réel l\'exposition au risque et les indicateurs de conformité pour garantir que l\'investissement reste dans les limites définies',
                                tags: ['Surveillance', 'Risque', 'Conformité']
                            },
                            {
                                title: 'Suivi de valeur et rééquilibrage',
                                description: 'Suivre en continu la valeur des actifs, déclencher automatiquement le rééquilibrage en cas d\'écart des seuils pour maintenir le cap',
                                tags: ['Suivi', 'Rééquilibrage', 'Ajustement']
                            }
                        ],
                        usageTitle: {
                            eyebrow: 'Processus',
                            title: 'Trois étapes pour lancer la gestion IA',
                            description: 'De la définition des objectifs à l\'exécution à long terme, seulement trois étapes.'
                        },
                        usageSteps: [
                            {
                                title: 'Définir les objectifs d\'investissement',
                                description: 'Indiquez à l\'IA votre appétence au risque et vos attentes de rendement',
                                detail: 'Défini une fois, suivi en continu'
                            },
                            {
                                title: 'Exécution automatique par l\'IA',
                                description: 'Construction du portefeuille, surveillance dynamique, rééquilibrage — tout est automatisé par l\'IA',
                                detail: 'Aucune intervention manuelle requise'
                            },
                            {
                                title: 'Consulter l\'état à tout moment',
                                description: 'Ouvrez le tableau de bord pour voir la performance du portefeuille et l\'historique des ajustements IA',
                                detail: 'Processus transparent et traçable'
                            }
                        ]
                    }
                ]
            },
            resources: {
                proofBarItems: ['Entrées marché', 'Jugement structuré', 'Validation des risques', 'Retour d\'exécution'],
                howItWorksHero: {
                    eyebrow: 'Fonctionnement',
                    title: 'Pipeline de trading transparent et contrôlé',
                    description: 'D-Sense n\'est pas une boîte noire. Il organise les entrées, le jugement, le contrôle des risques et l\'exécution en un flux explicable.',
                    panelLabel: 'Boucle de traitement',
                    panelTitle: 'De l\'entrée à l\'exécution en cycle',
                    panelDescription: 'Chaque étape a un rôle clair pour aider le trader à comprendre pourquoi une action se produit.'
                },
                howItWorksSteps: [
                    {
                        title: 'Entrées marché',
                        description: 'Agréger les cotations, la structure et le contexte pour former des entrées exploitables.',
                        detail: 'Fini l\'éparpillement de l\'observation sur plusieurs pages.'
                    },
                    {
                        title: 'Jugement stratégique',
                        description: 'L\'IA et les règles produisent ensemble des recommandations structurées.',
                        detail: 'Conclusions, tags et raisons visibles ensemble.'
                    },
                    {
                        title: 'Validation des risques',
                        description: 'Les limites de risque sont vérifiées avant d\'autoriser toute action.',
                        detail: 'Pas d\'exécution si les conditions ne sont pas remplies.'
                    },
                    {
                        title: 'Retour d\'exécution',
                        description: 'Les résultats et l\'état d\'exécution reviennent dans une vue unifiée.',
                        detail: 'Suivi continu de la stratégie et des résultats.'
                    }
                ],
                howItWorksFlowSteps: [
                    {
                        title: 'Agrégation des données',
                        description: 'Intégration multi-source des cotations, carnets d\'ordres et données on-chain en une base d\'entrée structurée unifiée',
                        tags: ['Cotations', 'On-chain', 'Multi-source']
                    },
                    {
                        title: 'Perception du marché',
                        description: 'L\'IA scanne en continu l\'état du marché et les changements structurels pour identifier les signaux clés et opportunités',
                        tags: ['Perception', 'Analyse', 'Identification']
                    },
                    {
                        title: 'Planification des transactions',
                        description: 'Synthèse de la structure du marché et des limites de risque pour former un jugement autonome et une planification stratégique',
                        tags: ['Analyse', 'Jugement', 'Planification']
                    },
                    {
                        title: 'Validation des risques',
                        description: 'Validation point par point dans les limites de risque définies par l\'utilisateur pour garantir la conformité et la sécurité',
                        tags: ['Limites', 'Validation', 'Autorisation']
                    },
                    {
                        title: 'Exécution automatique',
                        description: 'L\'IA exécute les transactions en autonomie dans les limites définies et suit l\'état des ordres',
                        tags: ['Ordre', 'Exécution', 'Suivi']
                    },
                    {
                        title: 'Retour d\'état',
                        description: 'Les résultats d\'exécution et l\'état du marché remontent en continu dans le système pour la prochaine itération',
                        tags: ['Feedback', 'Itération', 'Boucle']
                    }
                ],
                howItWorksAiDecision: [
                    {
                        eyebrow: 'Perception IA',
                        title: 'Comment l\'IA comprend l\'état du marché',
                        description: 'Le système transforme les données multi-source en entrées structurées compréhensibles par l\'IA. Via un LLM, il identifie la structure du marché, les tendances et les signaux anormaux, puis génère des conclusions analytiques lisibles.',
                        bullets: [
                            'Intégration des données K-line, carnets d\'ordres, on-chain et autres sources',
                            'L\'IA identifie les zones de prix clés et les changements de structure',
                            'Agrégation des signaux et filtrage du bruit pour mettre en avant les opportunités à haute confiance',
                            'Sortie structurée des conclusions analytiques en langage naturel'
                        ],
                        metrics: [
                            {
                                title: 'Dimensions de données',
                                value: '50+',
                                note: 'Indicateurs techniques et signaux de marché',
                                trend: 'up'
                            },
                            {
                                title: 'Fréquence d\'analyse',
                                value: 'Temps réel',
                                note: 'Scan continu de l\'état du marché',
                                trend: 'focus'
                            }
                        ]
                    },
                    {
                        eyebrow: 'Décision IA',
                        title: 'De l\'analyse à l\'exécution — comment l\'IA prend des décisions de trading',
                        description: 'Le système construit un contexte riche intégrant l\'état du marché, les limites de risque et les données historiques. Il appelle le LLM pour générer des propositions de trading structurées, puis le moteur d\'analyse les convertit en actions exécutables.',
                        bullets: [
                            'Construction dynamique de prompts décisionnels multi-contextes',
                            'Le LLM génère des décisions de trading structurées (direction/taille/timing)',
                            'Le moteur d\'analyse transforme la sortie du modèle en instructions exécutables',
                            'Soumission à l\'échange uniquement après validation des risques'
                        ],
                        metrics: [
                            {
                                title: 'Latence décisionnelle',
                                value: '<30s',
                                note: 'De l\'analyse à la recommandation de trading',
                                trend: 'up'
                            },
                            {
                                title: 'Traçabilité',
                                value: 'Chaîne complète',
                                note: 'Prompt→Sortie→Action entièrement traçable',
                                trend: 'focus'
                            }
                        ]
                    }
                ],
                howItWorksBlocks: [
                    {
                        title: 'Couche d\'entrée — Agrégation multi-source',
                        description: 'Intégration des cotations, données on-chain, indicateurs techniques et sentiment de marché dans un pipeline de données unifié, nettoyé et normalisé pour les couches supérieures.',
                        tags: ['API Marché', 'Données on-chain', 'Indicateurs', 'Pipeline données'],
                        iconName: 'database'
                    },
                    {
                        title: 'Couche de jugement — Moteur d\'analyse IA',
                        description: 'Fusion du LLM et du moteur de règles pour une analyse multi-dimensionnelle. Construction dynamique de prompts et appel au modèle pour générer des signaux structurés et des recommandations de trading.',
                        tags: ['LLM', 'Prompt Engineering', 'Moteur de règles', 'Signaux'],
                        iconName: 'brain'
                    },
                    {
                        title: 'Couche d\'exécution — Risque et pipeline de trading',
                        description: 'La validation des risques est intégrée en amont du pipeline d\'exécution pour que chaque transaction reste dans les limites définies. Les résultats remontent en temps réel, formant une boucle complète.',
                        tags: ['Validation risques', 'Exécution ordres', 'Synchro état', 'Gestion exceptions'],
                        iconName: 'shield'
                    }
                ],
                howItWorksSections: {
                    architecture: {
                        eyebrow: 'Architecture système',
                        title: 'Boucle en six étapes : de la donnée à l\'exécution',
                        description: 'Chaque étape a un rôle clair. L\'IA opère en autonomie dans les limites définies et itère en continu.'
                    },
                    aiDecision: {
                        eyebrow: 'Intelligence IA',
                        title: 'Mécanisme de perception et de décision de trading',
                        description: 'Comment le système comprend les signaux de marché et les transforme en décisions de trading exécutables.'
                    },
                    layers: {
                        eyebrow: 'Architecture technique',
                        title: 'Trois couches, chacune dédiée',
                        description: 'Le système repose sur trois couches principales, chacune avec des responsabilités techniques claires.'
                    },
                    demo: {
                        eyebrow: 'Démo en direct',
                        title: 'Voyez comment l\'IA perçoit le marché',
                        description: 'Un panneau d\'analyse IA simulé montrant comment le système identifie et annote les signaux de marché.'
                    }
                },
                howItWorksDemo: {
                    symbol: 'BTC/USDT',
                    symbolChange: '+2.34%',
                    symbolChangeUp: true,
                    price: '67,432.50',
                    priceChange: '+1,542.80',
                    priceChangeUp: true,
                    signalTags: [
                        {label: 'Rupture de structure', type: 'signal'},
                        {label: 'Volume en hausse', type: 'signal'},
                        {label: 'Volatilité élevée', type: 'risk'},
                        {label: 'IA haussière', type: 'info'}
                    ],
                    aiJudgmentTitle: 'Jugement IA',
                    aiJudgmentValue: 'Haussier · Confiance moyenne',
                    aiJudgmentNote: 'Percée de la résistance récente avec volume',
                    keyLevelTitle: 'Niveaux clés',
                    keyLevelValue: '67,200 / 68,500',
                    keyLevelNote: 'Résistance 68,500, Support 67,200'
                },
                howItWorksMetrics: [
                    {
                        title: 'Étapes',
                        value: '6',
                        note: 'Boucle complète de l\'agrégation au retour d\'état',
                        trend: 'focus'
                    },
                    {
                        title: 'Latence décisionnelle',
                        value: '<30s',
                        note: 'De l\'analyse à la recommandation de trading',
                        trend: 'up'
                    },
                    {
                        title: 'Dimensions de données',
                        value: '50+',
                        note: 'Indicateurs techniques et signaux de marché',
                        trend: 'up'
                    }
                ],
                howItWorksCta: {
                    eyebrow: 'Pour commencer',
                    title: 'Comprendre le mécanisme, puis explorer',
                    description: 'Après avoir compris le fonctionnement, vérifiez via le POC si cela correspond à votre style de trading.'
                },
                riskHero: {
                    eyebrow: 'Sécurité & Risques',
                    title: 'Les limites d\'abord, les actions dans la mesure',
                    description: 'Le contrôle des risques n\'est pas un accessoire. C\'est un prérequis pour un système qui mérite d\'être utilisé sur le long terme.',
                    panelLabel: 'Risque prioritaire',
                    panelTitle: 'Les limites avant l\'action, la sécurité avant l\'efficacité',
                    panelDescription: 'Quand le risque n\'est pas clair, le système doit réduire l\'action plutôt que d\'amplifier l\'exposition.'
                },
                riskGuardItems: [
                    {
                        title: 'Mode risque prioritaire',
                        description: 'Définir d\'abord les préférences et les limites, puis décider de l\'exécution.',
                        bullets: ['Cinq niveaux : Conservateur / Prudent / Modéré / Actif / Agressif', 'Seuils vérifiés avant chaque action', 'Pas d\'exécution si les conditions ne sont pas remplies']
                    },
                    {
                        title: 'Take-profit, stop-loss et contrôle des positions',
                        description: 'Maintenir le drawdown et l\'exposition dans des limites acceptables.',
                        bullets: ['Plafond de position automatique', 'Take-profit et stop-loss liés', 'Éviter l\'ajout sans limite']
                    },
                    {
                        title: 'Isolement des permissions et des clés API',
                        description: 'Traiter les opérations sensibles avec le principe du moindre privilège.',
                        bullets: ['Permissions API hiérarchisées', 'Stockage isolé et chiffré des clés', 'Repli prioritaire en cas d\'anomalie']
                    }
                ],
                riskMatrixItems: [
                    {
                        title: 'Validation pré-exécution',
                        description: 'Chaque action est validée contre les conditions de risque avant d\'être envoyée.',
                        tags: ['Pré-validation', 'Vérification'],
                        iconName: 'shield'
                    },
                    {
                        title: 'Périmètre des permissions',
                        description: 'Les capacités des API et des comptes sont limitées au strict nécessaire.',
                        tags: ['Contrôle accès', 'Isolement sécurité'],
                        iconName: 'layers'
                    },
                    {
                        title: 'Retour continu',
                        description: 'Les résultats d\'exécution et l\'état des risques remontent dans une vue unifiée, formant une boucle de surveillance.',
                        tags: ['Retour d\'état', 'Traçabilité'],
                        iconName: 'waypoints'
                    }
                ],
                riskSections: {
                    guard: {
                        eyebrow: 'Règles de risque',
                        title: 'Trois lignes de défense',
                        description: 'De l\'appétence au risque à la gestion des positions puis au contrôle des permissions, chaque niveau resserre la frontière de sécurité.'
                    },
                    matrix: {
                        eyebrow: 'Garantie d\'exécution',
                        title: 'Chaque étape respecte les règles',
                        description: 'Le système intègre une logique de validation à chaque nœud du pipeline d\'exécution pour garantir que les actions restent dans les limites définies.'
                    }
                },
                riskCta: {
                    eyebrow: 'Pour commencer',
                    title: 'Placez le risque avant le trading',
                    description: 'Découvrez comment les limites de risque protègent chacune de vos transactions.'
                },
                riskMetrics: [
                    {
                        title: 'Niveaux de risque',
                        value: '3',
                        note: 'Mode → Position → Permission, resserrement progressif',
                        trend: 'focus'
                    },
                    {
                        title: 'Moment de validation',
                        value: 'Pré-exécution',
                        note: 'Validation automatique avant chaque action',
                        trend: 'up'
                    },
                    {
                        title: 'Granularité des permissions',
                        value: 'Hiérarchisée',
                        note: 'Gestion minimale des permissions API',
                        trend: 'stable'
                    }
                ]
            },
            pocPage: {
                hero: {
                    eyebrow: 'POC',
                    title: 'Voyez concrètement comment le produit fonctionne via le POC',
                    description: 'Ici, pas de concepts vagues. Découvrez les scénarios typiques, la valeur et la structure interactive du Trading Assisté et du Trading Automatique.',
                    tags: ['Trading Assisté', 'Trading Automatique', 'Scénarios', 'Démos'],
                    panelLabel: 'Entrée POC',
                    panelTitle: 'Deux axes, deux expériences',
                    panelDescription: 'Découvrez comment le Trading Assisté et le Trading Automatique répondent à différents objectifs.',
                    assistText: 'Commencez par le POC pour juger si cela correspond à votre style de trading.',
                    topCardLabel: 'Axe 1',
                    topCardTitle: 'Trading Assisté',
                    topCardText: 'Lire le marché, former un jugement, préparer l\'action',
                    bottomCardLabel: 'Axe 2',
                    bottomCardTitle: 'Trading Automatique',
                    bottomCardText: 'Les règles s\'exécutent, les résultats remontent'
                },
                entries: [
                    {
                        title: 'POC Trading Assisté',
                        slogan: 'Voir plus vite, juger plus sûrement',
                        description: 'Parcourez la démonstration complète de l\'observation du marché à la recommandation de trading assisté.',
                        tags: ['Observation marché', 'Explication IA', 'Alertes risque'],
                        path: '/site/poc/assist-trading'
                    },
                    {
                        title: 'POC Trading Automatique',
                        slogan: 'Les règles d\'abord, l\'exécution continue',
                        description: 'Découvrez comment les stratégies s\'exécutent en continu dans les limites définies avec retour d\'état.',
                        tags: ['Exécution règles', 'Risque préalable', 'Retour résultats'],
                        path: '/site/poc/auto-trading'
                    },
                    {
                        title: 'POC Reconnaissance de Patterns',
                        slogan: 'Chaque bougie est analysée',
                        description: 'Découvrez la démonstration complète de reconnaissance automatique des motifs de bougies avec analyse IA approfondie.',
                        tags: ['Motifs bougies', 'Analyse IA', 'Backtest'],
                        path: '/site/poc/pattern'
                    }
                ],
                features: [
                    {
                        title: 'Présentation orientée scénarios',
                        description: 'Montrer quel problème chaque page résout, pas seulement l\'interface.',
                        tags: ['Scénarios', 'Valeur']
                    },
                    {
                        title: 'Navigation orientée processus',
                        description: 'Visualiser les relations et flux d\'information entre les modules.',
                        tags: ['Processus', 'Flux']
                    },
                    {
                        title: 'Explications orientées limites',
                        description: 'Présenter les conditions et limites de risque en même temps que la démo.',
                        tags: ['Limites', 'Risque']
                    }
                ],
                faqIntro: {
                    eyebrow: 'Complément',
                    title: 'Avant le POC, ces limites à connaître',
                    description: 'Quelques questions-réponses essentielles pour clarifier l\'essentiel avant l\'utilisation.'
                },
                faqItems: [
                    {
                        question: 'À qui s\'adresse ce système ?',
                        answer: 'Aux traders individuels qui souhaitent réduire le temps d\'écran, améliorer leur efficacité décisionnelle et comprendre les limites de risque.'
                    },
                    {
                        question: 'Le système exécute-t-il automatiquement les transactions ?',
                        answer: 'Il supporte les modes semi-automatique et automatique. L\'activation dépend de votre propre compréhension du risque.'
                    },
                    {
                        question: 'Pourquoi consulter le POC d\'abord ?',
                        answer: 'Le POC vous aide à comprendre la logique produit, les scénarios d\'utilisation et les limites avant de décider d\'essayer.'
                    },
                    {
                        question: 'Comment commencer ?',
                        answer: 'Avec un code d\'invitation, inscrivez-vous directement. Sans code, soumettez vos informations via la page Contact.'
                    }
                ]
            },
            assistPocPage: {
                hero: {
                    eyebrow: 'POC Trading Assisté',
                    title: 'Maîtrisez le marché sans décisions aveugles',
                    description: 'Il aide les traders à observer, juger et préparer leurs actions plus rapidement dans des environnements volatils, tout en gardant le contrôle final.',
                    tags: ['Signaux marché', 'Assistance IA', 'Alertes risque', 'Recommandations'],
                    panelLabel: 'Slogan clé',
                    panelTitle: 'Voir plus vite, juger plus sûrement',
                    panelDescription: 'Utilisez le système pour mieux voir les opportunités, puis décidez d\'agir.',
                    assistText: 'Le Trading Assisté soutient le jugement, il ne le remplace pas.',
                    topCardLabel: 'Contexte',
                    topCardTitle: 'Pré-séance / En séance',
                    topCardText: 'Idéal pour l\'observation fréquente et la formation rapide de jugements',
                    bottomCardLabel: 'Résultat',
                    bottomCardTitle: 'Moins d\'hésitation',
                    bottomCardText: 'Observation, compréhension et préparation d\'action dans un même flux'
                },
                features: [
                    {
                        title: 'Agrégation des signaux',
                        description: 'Concentrer les étiquettes clés et les changements de marché.',
                        tags: ['Cotations', 'Étiquettes']
                    },
                    {
                        title: 'Jugement assisté par IA',
                        description: 'Obtenir rapidement des analyses et recommandations structurées.',
                        tags: ['IA', 'Explication']
                    },
                    {
                        title: 'Alertes risque préalables',
                        description: 'Voir les conditions importantes avant d\'agir.',
                        tags: ['Risque', 'Alertes']
                    },
                    {
                        title: 'Recommandations actionnables',
                        description: 'Organiser les conclusions sous forme d\'actions claires.',
                        tags: ['Recommandations', 'Pré-exécution']
                    },
                ],
                flowSteps: [
                    {
                        title: 'Observer le marché',
                        description: 'Visualiser les changements clés du marché et leur contexte.',
                        detail: 'Effectuer un premier filtre rapide.'
                    },
                    {
                        title: 'Comprendre les signaux',
                        description: 'L\'IA organise les raisons, étiquettes et tendances.',
                        detail: 'Des conclusions plus faciles à comprendre.'
                    },
                    {
                        title: 'Confirmer les limites',
                        description: 'Consulter d\'abord les alertes risque et restrictions.',
                        detail: 'Éviter de voir l\'opportunité sans le coût.'
                    },
                    {
                        title: 'Préparer l\'action',
                        description: 'Formuler un plan d\'action clair pour la suite.',
                        detail: 'C\'est vous qui décidez d\'exécuter ou non.'
                    }
                ],
                stories: [
                    {
                        eyebrow: 'Valeur 1',
                        title: 'Repérer les opportunités plus rapidement',
                        description: 'Rassembler les observations dispersées dans un même espace de travail pour réduire les coûts de changement de contexte.',
                        bullets: ['Moins d\'éparpillement visuel', 'Convergence plus rapide des informations', 'Entrée plus rapide en phase de jugement'],
                        metrics: [
                            {
                                title: 'Mode d\'observation',
                                value: 'Unifié',
                                note: 'Entrées clés centralisées',
                                trend: 'stable'
                            },
                            {
                                title: 'Rapidité de réaction',
                                value: 'Plus rapide',
                                note: 'Moins de changements et d\'hésitations',
                                trend: 'up'
                            }
                        ]
                    },
                    {
                        eyebrow: 'Valeur 2',
                        title: 'Un jugement plus facile à expliquer',
                        description: 'Ne pas se limiter aux signaux, mais exprimer aussi les justifications et les risques.',
                        bullets: ['Signaux et raisons ensemble', 'Risques et opportunités exprimés simultanément', 'Jugement plus facile à réviser'],
                        metrics: [
                            {
                                title: 'Format de sortie',
                                value: 'Structuré',
                                note: 'Étiquettes, explications, recommandations liées',
                                trend: 'up'
                            },
                            {
                                title: 'Révisabilité',
                                value: 'Renforcée',
                                note: 'Processus de jugement plus facile à retracer',
                                trend: 'focus'
                            }
                        ]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: 'Fonctionnalités clés',
                        title: 'Du signal à l\'action, une chaîne complète',
                        description: 'Pas d\'empilement de fonctionnalités, mais une organisation systémique des capacités clés du Trading Assisté.'
                    },
                    flow: {
                        eyebrow: 'Processus type',
                        title: 'Chaque jour, quatre étapes pour préparer votre jugement',
                        description: 'De l\'observation à la décision, le système transforme des étapes dispersées en un flux de travail reproductible.'
                    },
                    stories: {
                        eyebrow: "Proposition de valeur",
                        title: "Les deux problèmes clés résolus par le Trading Assisté",
                        description: "Comment voir les opportunités plus vite en situation de surcharge, et comment rendre les jugements complexes plus faciles à expliquer."
                    },
                    demo: {
                        eyebrow: 'Démo en direct',
                        title: 'Présentation de l\'espace de travail de trading',
                        description: 'Des cotations en temps réel aux positions et transactions historiques, une vue complète des panneaux de données essentiels de l\'espace de travail.'
                    },
                    cta: {
                        primaryLabel: 'Obtenir un code d\'invitation',
                        primaryTo: '/site/assist-trading'
                    }
                }
            },
            autoPocPage: {
                hero: {
                    eyebrow: 'POC Trading Automatique',
                    title: 'Fixez vos objectifs, l\'IA trade pour vous',
                    description: 'Il suffit de définir votre tolérance au risque et votre objectif de rendement. L\'IA trouve les opportunités, gère les positions et optimise l\'utilisation du capital — sans code, sans surveillance d\'écran.',
                    tags: ['Appétence risque', 'Objectif rendement', 'Utilisation capital', 'Trading IA autonome'],
                    panelLabel: 'Concept clé',
                    panelTitle: 'Vous fixez les objectifs, l\'IA exécute',
                    panelDescription: 'Le trading n\'a pas besoin de programmation complexe. Dites à l\'IA votre tolérance au risque et vos attentes de rendement — elle trouve le chemin optimal automatiquement.',
                    assistText: 'Définissez votre appétence au risque et vos objectifs de rendement. L\'IA s\'occupe du reste.',
                    topCardLabel: 'Contexte idéal',
                    topCardTitle: 'Simple et direct',
                    topCardText: 'Pas besoin de coder ni d\'écrire de stratégies — pour tous les traders',
                    bottomCardLabel: 'Avantage clé',
                    bottomCardTitle: 'Décisions IA autonomes',
                    bottomCardText: 'L\'IA analyse le marché en temps réel et trouve les meilleures opportunités dans vos limites'
                },
                features: [
                    {
                        title: 'Définition de l\'appétence au risque',
                        description: 'Définissez votre perte maximale acceptable. L\'IA opère strictement dans cette limite.',
                        tags: ['Risque', 'Sérénité']
                    },
                    {
                        title: 'Gestion des objectifs de rendement',
                        description: 'Fixez votre taux de rendement attendu. L\'IA trouve automatiquement les opportunités correspondantes.',
                        tags: ['Objectif', 'Rendement']
                    },
                    {
                        title: 'Optimisation du capital',
                        description: 'L\'IA alloue intelligemment le capital de chaque transaction pour maximiser l\'efficacité.',
                        tags: ['Efficacité', 'Position']
                    },
                    {
                        title: 'Exécution autonome par l\'IA',
                        description: 'Surveillance 7j/24 du marché, exécution automatique du timing, de l\'entrée et de la sortie.',
                        tags: ['Automatique', '24/7']
                    },
                ],
                flowSteps: [
                    {
                        title: 'Définir les limites de risque',
                        description: 'Déterminez votre drawdown maximum et votre perte maximale par transaction.',
                        detail: 'Toutes les décisions IA restent dans ces limites'
                    },
                    {
                        title: 'Définir les objectifs de rendement',
                        description: 'Fixez votre objectif de rendement annualisé ou trimestriel.',
                        detail: 'L\'IA utilise cette référence pour trouver les opportunités adaptées'
                    },
                    {
                        title: 'Matching intelligent IA',
                        description: 'L\'IA analyse le marché en temps réel et trouve les opportunités adaptées à votre profil.',
                        detail: 'Allocation intelligente des positions selon l\'utilisation du capital'
                    },
                    {
                        title: 'Exécution et optimisation automatiques',
                        description: 'L\'IA exécute les transactions en autonomie et ajuste la stratégie en continu.',
                        detail: 'Chaque transaction est traçable, les gains sont clairement visibles'
                    }
                ],
                stories: [
                    {
                        eyebrow: 'Valeur 1',
                        title: 'Pas de stratégie, seulement des objectifs',
                        description: 'Vous n\'avez besoin d\'écrire aucune stratégie. Dites à l\'IA votre tolérance au risque et vos attentes de rendement — elle trouve le meilleur chemin automatiquement.',
                        bullets: ['Zéro codage, configurer et c\'est parti', 'L\'IA optimise et trouve les opportunités', 'Pas de surveillance, fonctionnement 24/7 autonome'],
                        metrics: [
                            {
                                title: 'Facilité',
                                value: 'Minimale',
                                note: 'Deux paramètres suffisent pour commencer',
                                trend: 'up'
                            },
                            {
                                title: 'Mode décisionnel',
                                value: 'Piloté IA',
                                note: 'L\'IA remplace le jugement et l\'exécution manuels',
                                trend: 'stable'
                            }
                        ]
                    },
                    {
                        eyebrow: 'Valeur 2',
                        title: 'Chaque euro travaille efficacement',
                        description: 'L\'IA ne se contente pas de trader — elle gère votre capital en ajustant dynamiquement la taille des positions pour une utilisation optimale.',
                        bullets: ['Gestion intelligente des positions, allocation dynamique', 'Ajustement en temps réel de l\'utilisation du capital', 'Les fonds inactifs aussi génèrent de la valeur'],
                        metrics: [
                            {
                                title: 'Efficacité du capital',
                                value: 'Optimale',
                                note: 'L\'IA optimise en continu la répartition du capital',
                                trend: 'focus'
                            },
                            {
                                title: 'Source des gains',
                                value: 'Transparente',
                                note: 'Chaque gain et perte est traçable et révisable',
                                trend: 'up'
                            }
                        ]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: 'Fonctionnalités clés',
                        title: 'Les quatre capacités du trading autonome IA',
                        description: 'Pas besoin d\'écrire de stratégies. Définissez simplement vos préférences, l\'IA fait tout.'
                    },
                    flow: {
                        eyebrow: 'Processus',
                        title: 'Quatre étapes, l\'IA démarre immédiatement',
                        description: 'Dès que vous définissez vos préférences, l\'IA commence à travailler pour vous.'
                    },
                    stories: {
                        eyebrow: 'Valeur clé',
                        title: 'Simple, mais puissant',
                        description: 'Transformer le trading complexe en choix simples.'
                    },
                    demo: {
                        eyebrow: 'Démo interactive',
                        title: 'Démonstration de trading automatique IA',
                        description: 'Configurez votre clé API et votre appétence au risque pour découvrir le processus complet du trading automatique IA.'
                    }
                }
            },
            patternPocPage: {
                hero: {
                    eyebrow: 'POC Reconnaissance de Patterns',
                    title: 'Analyse de motifs de bougies pilotée par IA',
                    description: 'De la détection à l\'analyse, découvrez la capacité complète de l\'IA à identifier, backtester et évaluer les opportunités de trading sur les motifs de bougies.',
                    tags: ['Motifs bougies', 'Analyse IA', 'Backtest', 'Signaux trading'],
                    panelLabel: 'Fonctionnalité clé',
                    panelTitle: 'Chaque motif de bougie est visible',
                    panelDescription: 'Basé sur des centaines d\'algorithmes de détection de motifs, combinés à une analyse IA approfondie pour vous aider à découvrir des opportunités potentielles.',
                    assistText: 'Pas de recherche manuelle. Le système annoté automatiquement les motifs et fournit une analyse IA.',
                    topCardLabel: 'Détection',
                    topCardTitle: 'Identification auto',
                    topCardText: 'Détection automatique des motifs sur plusieurs périodes (1m à 1D)',
                    bottomCardLabel: 'Analyse IA',
                    bottomCardTitle: 'Analyse approfondie',
                    bottomCardText: 'L\'IA analyse les motifs et fournit des recommandations d\'entrée, stop-loss et objectifs'
                },
                features: [
                    {
                        title: 'Détection multi-motifs',
                        description: 'Détection automatique de centaines de motifs classiques : marteau, engloutissement, étoile du matin, etc.',
                        tags: ['Motifs', 'Couverture']
                    },
                    {
                        title: 'Analyse IA approfondie',
                        description: 'Analyse multi-dimensionnelle via LLM incluant prix d\'entrée, prix cible et recommandations de stop-loss.',
                        tags: ['IA', 'Analyse']
                    },
                    {
                        title: 'Backtest historique',
                        description: 'Backtest de chaque motif pour voir le taux de réussite, le ratio gain/perte et d\'autres indicateurs clés.',
                        tags: ['Backtest', 'Données']
                    },
                    {
                        title: 'Reconnaissance de zones',
                        description: 'Identification automatique des zones de support/résistance et des ranges de consolidation.',
                        tags: ['Zones', 'Support/Résistance']
                    },
                ],
                flowSteps: [
                    {
                        title: 'Choisir un actif',
                        description: 'Sélectionnez la paire de trading cible, le système récupère automatiquement les données K-line.',
                        detail: 'Supporte les perpétuels et le spot, couvre BTC, ETH et autres grandes cryptos'
                    },
                    {
                        title: 'Détection des motifs',
                        description: 'Le système scanne les bougies, identifie et marque tous les signaux de motifs.',
                        detail: 'Supporte les périodes 1m à 1D, mise à jour en temps réel'
                    },
                    {
                        title: 'Analyse IA',
                        description: 'Analyse assistée par IA des motifs détectés avec recommandations de trading.',
                        detail: 'Fournit entrée, stop-loss, objectifs de prix et niveau de risque'
                    },
                    {
                        title: 'Validation backtest',
                        description: 'Consultez les données historiques du motif pour évaluer la fiabilité du signal.',
                        detail: 'Taux de réussite, ratio gain/perte, historique des gains et pertes'
                    }
                ],
                stories: [
                    {
                        eyebrow: 'Valeur 1',
                        title: 'Une reconnaissance de motifs plus complète',
                        description: 'Des centaines de motifs détectés automatiquement, du simple marteau à l\'étoile du matin complexe. Le système trouve les signaux que vous pourriez manquer.',
                        bullets: ['Scan automatique, pas de recherche manuelle', 'Couverture de centaines de motifs classiques', 'Détection multi-périodes'],
                        metrics: [
                            {
                                title: 'Efficacité de détection',
                                value: 'Auto',
                                note: 'Surveillance en temps réel, zéro effort manuel',
                                trend: 'up'
                            },
                            {
                                title: 'Couverture',
                                value: 'Complète',
                                note: 'Centaines de motifs reconnus automatiquement',
                                trend: 'focus'
                            }
                        ]
                    },
                    {
                        eyebrow: 'Valeur 2',
                        title: 'Une analyse plus approfondie',
                        description: 'L\'IA ne se contente pas d\'identifier le motif — elle analyse sa fiabilité, le ratio gain/perte potentiel et fournit des recommandations de trading concrètes.',
                        bullets: ['Analyse multi-dimensionnelle des signaux par IA', 'Recommandations d\'entrée, stop-loss et objectifs', 'Évaluation des risques pour la décision'],
                        metrics: [
                            {
                                title: 'Profondeur d\'analyse',
                                value: 'Complète',
                                note: 'Analyse multi-dimensionnelle complète',
                                trend: 'up'
                            },
                            {
                                title: 'Valeur pratique',
                                value: 'Concrète',
                                note: 'Recommandations de prix spécifiques et alertes risque',
                                trend: 'focus'
                            }
                        ]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: 'Fonctionnalités clés',
                        title: 'De la détection à l\'analyse, une chaîne complète',
                        description: 'Pas seulement détecter des motifs, mais organiser la reconnaissance, l\'analyse et la validation historique en une chaîne d\'outils complète.'
                    },
                    flow: {
                        eyebrow: 'Processus type',
                        title: 'Quatre étapes pour une reconnaissance de motifs complète',
                        description: 'Du choix de l\'actif à la validation backtest, le système gère l\'intégralité du flux de reconnaissance de motifs.'
                    },
                    stories: {
                        eyebrow: 'Proposition de valeur',
                        title: 'Les trois problèmes résolus par la reconnaissance de motifs',
                        description: 'Recherche manuelle inefficace, signaux manqués, analyse superficielle — la reconnaissance de motifs élimine ces obstacles.'
                    },
                    demo: {
                        eyebrow: 'Démo interactive',
                        title: 'Démonstration de reconnaissance de motifs de bougies',
                        description: 'Découvrez rapidement les changements de structure du marché et les opportunités de trading grâce à la reconnaissance des motifs de bougies.'
                    },
                    cta: {
                        primaryLabel: 'Obtenir un code d\'invitation',
                    }
                }
            },
            membershipPage: {
                hero: {
                    eyebrow: 'Adhésion',
                    title: 'Une capacité adaptée à chaque étape de votre investissement',
                    description: 'De l\'expérience gratuite au trading entièrement automatisé niveau expert, quatre formules couvrent tout le parcours, de l\'insight à l\'exécution et à l\'allocation.',
                    panelLabel: 'Souscription en ligne',
                    panelTitle: 'Souscrivez en ligne, effet immédiat',
                    panelDescription: 'Choisissez votre formule, payez en ligne et votre abonnement prend effet immédiatement. Suivez votre statut et sa date d\'expiration depuis votre espace.'
                },
                sectionTitles: {
                    compare: {
                        eyebrow: 'Comparatif',
                        title: 'Quatre formules, choisissez la vôtre',
                        description: 'Tarifs et matrice de fonctionnalités en un coup d\'œil. Le nombre de plans DCA augmente avec la formule.'
                    },
                    features: {
                        eyebrow: 'Fonctionnalités payantes',
                        title: 'Chaque capacité résout un problème concret',
                        description: 'Assistant DCA, couverture, reconnaissance de patterns et trading automatique se débloquent par formule.'
                    },
                    cta: {
                        eyebrow: 'Guide d\'ouverture',
                        title: 'Prêt à passer au niveau supérieur ?',
                        description: 'Choisissez votre formule et payez en ligne : l\'abonnement prend effet immédiatement.'
                    }
                },
                priceLabels: {monthly: 'Mensuel', yearly: 'Annuel'},
                tiers: [
                    {
                        name: 'Gratuit', tagline: 'Fonctions essentielles', monthlyPrice: '0', yearlyPriceMonthly: '0',
                        features: [
                            {label: 'Consulter les rapports', enabled: true},
                            {label: 'Assistant DCA', enabled: false},
                            {label: 'Couverture', enabled: false},
                            {label: 'Trading rapide par patterns', enabled: false},
                            {label: 'Trading automatique', enabled: false}
                        ]
                    },
                    {
                        name: 'Starter',
                        tagline: 'Achats planifiés',
                        monthlyPrice: '5,9U',
                        yearlyPriceMonthly: '3,9U/mois',
                        features: [
                            {label: 'Consulter les rapports', enabled: true},
                            {label: 'Assistant DCA', enabled: true, note: '≤3 plans'},
                            {label: 'Couverture', enabled: false},
                            {label: 'Trading rapide par patterns', enabled: false},
                            {label: 'Trading automatique', enabled: false}
                        ]
                    },
                    {
                        name: 'Pro',
                        tagline: 'Trading et patterns avancés',
                        monthlyPrice: '15,9U',
                        yearlyPriceMonthly: '9,9U/mois',
                        highlight: true,
                        features: [
                            {label: 'Consulter les rapports', enabled: true},
                            {label: 'Assistant DCA', enabled: true, note: '≤10 plans'},
                            {label: 'Couverture', enabled: true},
                            {label: 'Trading rapide par patterns', enabled: true},
                            {label: 'Trading automatique', enabled: false}
                        ]
                    },
                    {
                        name: 'Expert',
                        tagline: 'Trading 100% automatique par IA',
                        monthlyPrice: '59,9U',
                        yearlyPriceMonthly: '29,9U/mois',
                        features: [
                            {label: 'Consulter les rapports', enabled: true},
                            {label: 'Assistant DCA', enabled: true, note: '≤15 plans'},
                            {label: 'Couverture', enabled: true},
                            {label: 'Trading rapide par patterns', enabled: true},
                            {label: 'Trading automatique', enabled: true}
                        ]
                    }
                ],
                featureIntros: [
                    {
                        title: 'Assistant DCA',
                        description: 'Créez des plans à partir d\'un symbole et de conditions sur les indicateurs ; le système surveille les bougies clôturées et achète au marché à chaque déclenchement. Ponctuel ou récurrent.',
                        tags: ['Basé sur des plans', 'Déclenchements conditionnels', 'Achat automatique']
                    },
                    {
                        title: 'Couverture',
                        description: 'Couverture à jambe unique ou multi-actifs, re-montage automatique de la jambe de protection, gestion unifiée des TP/SL et de l\'exposition.',
                        tags: ['Couverture à jambe unique', 'Jambe de protection', 'Gestion d\'exposition']
                    },
                    {
                        title: 'Trading rapide par patterns',
                        description: 'L\'IA reconnaît les figures clés et permet des ordres en un clic pour concrétiser vos décisions plus vite.',
                        tags: ['Reconnaissance de patterns', 'Ordres rapides']
                    },
                    {
                        title: 'Trading automatique',
                        description: 'Définissez vos limites de risque et laissez l\'IA décider et trader de façon autonome à l\'intérieur de ces bornes.',
                        tags: ['Décisions IA', 'Autonomie bornée', 'Trading continu']
                    }
                ],
                faqIntro: {
                    eyebrow: 'FAQ',
                    title: 'Questions fréquentes',
                    description: 'Les questions courantes sur l\'adhésion et l\'ouverture.'
                },
                faqItems: [
                    {
                        question: 'Comment souscrire ?',
                        answer: 'Choisissez votre formule sur la page tarifs et payez en ligne : votre abonnement prend effet immédiatement. Un rappel vous est envoyé avant l\'expiration et vous gérez votre abonnement depuis votre espace membre.'
                    },
                    {
                        question: 'Que se passe-t-il à l\'expiration ?',
                        answer: 'Vous êtes rétrogradé vers la prochaine formule active (ou gratuit). Les plans DCA dépassant la nouvelle limite sont mis en pause automatiquement avec une notification in-app ; restaurez-les manuellement après renouvellement.'
                    },
                    {
                        question: 'Comment la limite de plans DCA est-elle calculée ?',
                        answer: 'Par formule : Starter 3 plans, Pro 10, Expert 15. Seuls les plans non supprimés comptent, en pause comme en cours.'
                    },
                    {
                        question: 'Quel est l\'avantage annuel ?',
                        answer: 'La facturation annuelle réduit le prix mensuel équivalent : Starter 3,9U/mois, Pro 9,9U/mois, Expert 29,9U/mois.'
                    }
                ]
            },
            contactPage: {
                hero: {
                    eyebrow: '开始使用',
                    title: '获取邀请码，开始体验 D-Sense',
                    description: '如果你还没有邀请码，可以先提交信息，我们会根据需求与阶段安排后续体验。',
                    tags: ['邀请码', '申请体验', '开始使用'],
                    panelLabel: '开始方式',
                    panelTitle: '有邀请码就注册，没有邀请码就先申请',
                    panelDescription: '把获取邀请码和开始体验组织成清晰路径。',
                    assistText: '先看清获取方式，再决定是否提交信息。',
                    topCardLabel: '开始更快',
                    topCardTitle: '两种入口',
                    topCardText: '直接注册，或先获取邀请码',
                    bottomCardLabel: '适用方式',
                    bottomCardTitle: '邀请制体验',
                    bottomCardText: '通过邀请码控制体验节奏'
                },
                contactModes: [
                    {title: '已有邀请码，直接注册', description: '拿到邀请码即可直接完成注册。', tags: ['注册', '开始']},
                    {
                        title: '没有邀请码，先申请',
                        description: '先提交信息，再进入邀请码获取流程。',
                        tags: ['邀请码', '申请']
                    },
                    {
                        title: '适合长期使用的交易者',
                        description: '更适合希望建立稳定工作流的个人交易者。',
                        tags: ['个人', '长期']
                    },
                ],
                applicationSteps: [
                    {title: '提交信息', description: '留下基本信息和使用意向。', detail: '补充交易场景与关注点。'},
                    {
                        title: '确认需求',
                        description: '确认方向和需求后进入发码阶段。',
                        detail: '重点确认风险理解和使用预期。'
                    },
                    {title: '获取邀请码', description: '收到邀请码后进入注册流程。', detail: '已有邀请码可直接注册。'},
                    {
                        title: '注册开始使用',
                        description: '完成注册后即可进入系统。',
                        detail: '登录后继续查看 POC 与产品能力。'
                    }
                ],
                faqIntro: {
                    eyebrow: '补充说明',
                    title: '先把常见问题看清',
                    description: '先看清注册方式和邀请码逻辑。'
                },
                faqItems: [
                    {
                        question: '为什么采用邀请码方式？',
                        answer: '当前公开站采用邀请制体验，便于控制节奏并保持高质量反馈。'
                    },
                    {question: '没有邀请码也能开始吗？', answer: '可以，先在联系页提交信息，我们会根据阶段安排后续体验。'},
                    {question: '注册后能做什么？', answer: '登录后可查看产品结构、体验系统入口，并继续了解交易工作流。'},
                    {
                        question: '官网支持哪些语言？',
                        answer: '本轮官网、登录页和注册页支持简体中文、繁体中文、英文、韩语、日语和法语。'
                    }
                ]
            }
        },
        'ja-JP': {
            primaryCta: {label: '今すぐ登録', path: '/register'},
            secondaryCta: {label: '今すぐ開通', path: '/membership'},
            waitlistCta: {label: '招待コードを取得', path: '/site/contact'},
            tertiaryCta: {label: 'ログイン', path: '/login'},
            siteNavItems: [
                {label: 'ホーム', path: '/'},
                {label: 'アシストトレード', path: '/site/assist-trading'},
                {label: '自動トレード', path: '/site/auto-trading'},
                {label: 'アセットアロケーション', path: '/site/asset-allocation'},
                {
                    label: 'リソース',
                    children: [
                        {label: '動作メカニズム', path: '/site/how-it-works'},
                        {label: 'リスク管理・セキュリティ', path: '/site/risk-control'}
                    ]
                },
                {
                    label: 'POC',
                    children: [
                        {label: 'アシストトレード', path: '/site/poc/assist-trading'},
                        {label: '自動トレード', path: '/site/poc/auto-trading'},
                        {label: 'パターン認識', path: '/site/poc/pattern'}
                    ]
                },
                {label: '料金', path: '/site/membership'},
                {label: 'お問い合わせ', path: '/site/contact'},
            ],
            footerNavItems: [
                {label: 'ホーム', path: '/'},
                {label: 'アシストトレード', path: '/site/assist-trading'},
                {label: '自動トレード', path: '/site/auto-trading'},
                {label: 'アセットアロケーション', path: '/site/asset-allocation'},
                {label: '動作メカニズム', path: '/site/how-it-works'},
                {label: 'リスク管理・セキュリティ', path: '/site/risk-control'},
                {label: 'POC概要', path: '/site/poc'},
                {label: 'アシストトレード', path: '/site/poc/assist-trading'},
                {label: '自動トレード', path: '/site/poc/auto-trading'},
                {label: 'パターン認識', path: '/site/poc/pattern'},
                {label: '料金', path: '/site/membership'},
                {label: 'お問い合わせ', path: '/site/contact'}
            ],
            heroMetrics: [
                {title: '市場機会', value: 'インサイト', note: '重要なシグナルと構造変化をAIが補助認識', trend: 'up'},
                {title: '資産成長', value: 'マッチング', note: '戦略と目標を整合、実行を継続追跡', trend: 'stable'},
                {
                    title: '資産の安全性',
                    value: 'ガーディアン',
                    note: 'コンプライアンスとリスク管理を両立、投資を完全管理',
                    trend: 'focus'
                }
            ],
            home: {
                hero: {
                    eyebrow: '安定して遠くへ',
                    title: 'あなた自身のブラックロックを築く',
                    description: '共に成長するAI資産管理パートナー。あなたのルール、ペース、境界で持続可能なトレードと資産管理システムを運用します。',
                    tags: ['アシストトレード', '自動トレード', 'アセットアロケーション', 'リスク優先'],
                    panelLabel: '資産管理はもっとシンプルであるべき',
                    panelTitle: 'AIが面倒な処理を担当、あなたは決断に集中',
                    panelDescription: 'ポートフォリオ構築、リスク配分、戦略実行——かつて機関投資家向けだった資産管理能力を、今AIがシステム化して提供します。',
                    assistText: 'より明確なシステムで、トレーダーの長期的で一貫性のある意思決定と実行力を構築します。',
                    topCardLabel: 'ビジョン',
                    topCardTitle: '長期的な一貫性',
                    topCardText: 'トレーダーが安定して振り返り可能な持続可能な実行方法を構築',
                    bottomCardLabel: '体験方法',
                    bottomCardTitle: '招待コードでの体験',
                    bottomCardText: 'デフォルトは簡体字中国語、繁体字中国語と英語に切替可能'
                },
                section: {
                    eyebrow: 'プロダクトの方向性',
                    title: '3つのプロダクト、1つの能力体系',
                    description: '情報、実行からアロケーションまで、AIのシステム化された実装経路を提示します。'
                },
                products: [
                    {
                        key: 'assist-trading',
                        label: 'アシストトレード',
                        slogan: '洞察が速ければ、判断も速い',
                        description: '相場は瞬息万変。アシストトレードが即座に状況を洞察し、あらゆるチャンスを確実に掴みます。',
                        tags: [],
                        bullets: [],
                        metrics: [],
                        ctaLabel: '',
                        ctaTo: '/site/assist-trading'
                    },
                    {
                        key: 'auto-trading',
                        label: '自動トレード',
                        slogan: 'あなたの望み、AIの行く先',
                        description: '投資設定のようにシンプルに。リスクの許容範囲を設定すれば、AIがその境界内で自律的に判断し、継続的に取引します。',
                        tags: [],
                        bullets: [],
                        metrics: [],
                        ctaLabel: '',
                        ctaTo: '/site/auto-trading'
                    },
                    {
                        key: 'asset-allocation',
                        label: 'アセットアロケーション',
                        slogan: '細部は無視して、目標に集中',
                        description: 'AI資産管理：あなたは望む結果だけを考えればよく、配分比率やリバランスのタイミングはシステムが自動実行します。',
                        tags: ['目標', 'アロケーション', 'AI管理'],
                        bullets: ['結果志向 — プロセスではなく目標に注目', '能動的管理 — 逸脱は自動修正', '継続的最適化 — 市場変化に即応'],
                        metrics: [
                            {
                                title: '視点',
                                value: 'ポートフォリオ',
                                note: 'ポートフォリオ構造を全体掌握',
                                trend: 'stable'
                            },
                            {title: '目標', value: '時間', note: '長期目標を継続追跡', trend: 'up'},
                            {
                                title: 'リスク管理',
                                value: 'ヘッジ',
                                note: 'リスクエクスポージャーを自動管理',
                                trend: 'focus'
                            },
                            {
                                title: '全リンク追跡',
                                value: '透明性',
                                note: 'AIの判断プロセスを確認可能',
                                trend: 'stable'
                            }
                        ],
                        ctaLabel: '',
                        ctaTo: '/site/asset-allocation'
                    }
                ],
                proofItems: [
                    {
                        title: 'ブランド志向のプロダクト表現',
                        description: '価値と方向性を先に伝え、機能詳細は後で。',
                        tags: ['ブランド', '公式サイト']
                    },
                    {
                        title: 'システム化された能力組織',
                        description: 'アシストトレード、自動トレード、アセットアロケーションを同一体系に収束。',
                        tags: ['体系', '組織']
                    },
                    {
                        title: 'リスクはアクションに先んじる',
                        description: '境界と制限を実行の前に明確に定義。',
                        tags: ['リスク管理', '境界']
                    }
                ],
                exploreTitle: {
                    eyebrow: 'さらに詳しく',
                    title: 'まずは仕組みを理解し、次にPOCを',
                    description: 'リソースでシステムの動作を説明し、POCでプロダクトの実装を示します。'
                },
                exploreCards: [
                    {
                        eyebrow: 'リソース',
                        title: '動作メカニズムとリスク管理',
                        description: 'システムがどのように入力、判断、実行、境界制御を組織するかを理解。',
                        actionLabel: 'リソースを見る',
                        path: '/site/how-it-works'
                    },
                    {
                        eyebrow: 'POC',
                        title: 'アシストトレードと自動トレードのデモ',
                        description: 'POC構成を通じて、2つのコアな方向性の価値と流れを直感的に理解。',
                        actionLabel: 'POCへ',
                        path: '/site/poc'
                    }
                ],
                ctaEyebrow: '先行体験',
                ctaTitle: 'D-Senseを体験する準備はできましたか？',
                ctaDescription: 'あなたのローマを築くために。登録してフルトレード体験を始めましょう。'
            },
            productPage: {
                hero: {
                    eyebrow: 'プロダクト機能',
                    title: '3つの能力で、判断からアロケーションまでの重要経路をカバー',
                    description: '単に機能を積み上げるのではなく、本当に取引結果に影響を与える能力を実用的なシステムとして組織化します。',
                    tags: ['アシストトレード', '自動トレード', 'アセットアロケーション', '統一ワークスペース'],
                    panelLabel: 'プロダクトマトリックス',
                    panelTitle: '1つのプロダクトで3つの重要な能力を支える',
                    panelDescription: '同じシステム内で観察、判断、実行、資産の全体像を実現。',
                    assistText: 'プロダクトページはホームの3つの方向性を完全に展開します。',
                    topCardLabel: '価値',
                    topCardTitle: '少ない切替、少ない断絶',
                    topCardText: '判断と実行を可能な限り同じワークフローに維持',
                    bottomCardLabel: '体験',
                    bottomCardTitle: 'ブランド化された表現',
                    bottomCardText: '公式サイトのストーリーは方向性、シナリオ、価値を軸に構成'
                },
                directions: [
                    {
                        eyebrow: 'アシストトレード',
                        title: 'セッション前にワークスペースを開くと、AIがすでに重要なシグナルをマーク',
                        description: '毎日ワークスペースを開けば、システムが市場構造の変化、重要な価格帯、AIの判断を整理しています。ゼロからチャートを探す必要はなく、シグナルから直接確認でき、より速く自分の判断を形成できます。',
                        bullets: ['ワークスペースを開くと集約された重要シグナルが表示', 'AIが構造とリスクポイントを既にマーク', '判断効率が向上、チャート監視の負担が軽減'],
                        metrics: [
                            {
                                title: '適用シーン',
                                value: 'セッション前/中',
                                note: '観察と判断形成を補助',
                                trend: 'stable'
                            },
                            {title: '出力方式', value: '構造化', note: '提案、タグ、リスクポイントが可読', trend: 'up'}
                        ],
                        featuresTitle: {
                            eyebrow: 'コア能力',
                            title: '4つのステップでインサイトから取引へ',
                            description: 'データ集約、シグナル集約、境界判断からクイック取引までの完全な能力チェーン。'
                        },
                        flowSteps: [
                            {
                                title: 'データ集約',
                                description: '相場、オーダーブック、オンチェーンデータを統合し、構造化されたインプットを形成',
                                tags: ['相場', 'オーダーブック', 'オンチェーン']
                            },
                            {
                                title: 'シグナル集約',
                                description: '多次元シグナルをクロス検証し、ノイズを除去して重要な情報を強調',
                                tags: ['シグナル', 'クロス検証', 'ノイズ除去']
                            },
                            {
                                title: '境界判断',
                                description: 'リスク管理ルールとアカウント状態に基づき、現在の操作可能範囲を判断',
                                tags: ['リスク管理', 'アカウント状態', '境界']
                            },
                            {
                                title: 'クイック取引',
                                description: '定義済み戦略をワンクリックで実行、ページを頻繁に切り替える必要なし',
                                tags: ['ワンクリック実行', '戦略', '効率']
                            }
                        ],
                        usageTitle: {
                            eyebrow: '利用フロー',
                            title: '3ステップで準備完了',
                            description: 'ワークスペースを開いてから取引開始まで、たった3ステップ。'
                        },
                        usageSteps: [
                            {
                                title: 'ワークスペースを開く',
                                description: 'ワークスペースに移動、相場データとテクニカル指標は準備完了',
                                detail: '手動でチャートを探す必要なし'
                            },
                            {
                                title: '銘柄を確認・選択',
                                description: '各銘柄のシグナルと重要価格帯をすばやく閲覧',
                                detail: '動きのある銘柄にフォーカス'
                            },
                            {
                                title: 'ワンクリック建玉',
                                description: '境界確認後、直接建玉操作を実行',
                                detail: '操作ステップを削減'
                            }
                        ]
                    },
                    {
                        eyebrow: 'すべての人に、すべての人が享受可能',
                        cardEyebrow: 'フィンテックを個人にも',
                        title: 'AI自動トレード、ゼロからスタート',
                        cardTitle: 'ゼロベースAI自動トレード',
                        description: 'リスク選好を設定するだけで、AIが継続的に自動取引を実行します。',
                        cardDescription: '戦略記述もチャート監視も不要。リスク許容度を設定するだけで、AIが適切な資産に自動マッチングし、継続的に取引を反復改善。市場からあなたのエネルギーを解放します。',
                        bullets: ['ゼロ障壁 - 戦略不要、取引未経験でも使用可能', 'リスク設定 - AIにリスク選好を伝えるだけ、残りはAIに任せる', '完全自動 - 24時間365日継続稼働、完全に手放せる'],
                        metrics: [
                            {
                                title: '操作ハードル',
                                value: 'ゼロベース',
                                note: 'リスク許容度を設定するだけ',
                                trend: 'up'
                            },
                            {
                                title: '運用モード',
                                value: '完全自動',
                                note: 'AIが資産を継続マッチングし取引を実行',
                                trend: 'focus'
                            }
                        ],
                        featuresTitle: {
                            eyebrow: 'コア能力',
                            title: '6ステップのクローズドループ、AIが自律的に取引を反復',
                            description: 'データ集約から状態フィードバックまで、AIが境界内で自律判断し継続的に取引を反復。'
                        },
                        flowSteps: [
                            {
                                title: 'データ集約',
                                description: '複数ソースの市場データを集約し、統一されたインプット基盤を形成',
                                tags: ['マルチソース', '集約', 'インプット']
                            },
                            {
                                title: '市場感知',
                                description: 'AIが市場状態と構造変化を継続スキャンし、取引機会を識別',
                                tags: ['感知', '構造', 'スキャン']
                            },
                            {
                                title: '取引計画',
                                description: '市場構造とリスク境界を総合し、自律的に取引判断を形成',
                                tags: ['分析', '判断', '意思決定']
                            },
                            {
                                title: 'リスク検証',
                                description: 'ユーザー設定のリスク境界内で実行可否を検証',
                                tags: ['境界', '検証', '許可']
                            },
                            {
                                title: '自動実行',
                                description: 'AIが境界内で自律的に取引操作を実行',
                                tags: ['発注', '実行', '追跡']
                            },
                            {
                                title: '状態フィードバック',
                                description: '実行結果と状態が継続的にフィードバックされ、次の反復へ',
                                tags: ['フィードバック', '状態', '反復']
                            }
                        ]
                    },
                    {
                        eyebrow: 'アセットアロケーション',
                        title: 'AIがあなたの資産アロケーション全体を自動管理',
                        description: '目標とリスク選好を設定するだけで、ポートフォリオ構築から動的リバランスまで、AIが全てを担当。結果にだけ注目してください。',
                        bullets: ['結果志向 — プロセスではなく目標に注目', '能動的管理 — 逸脱は自動修正', '継続的最適化 — 市場変化に即応'],
                        metrics: [
                            {
                                title: '視点',
                                value: 'ポートフォリオ',
                                note: 'ポートフォリオ構造を全体掌握',
                                trend: 'stable'
                            },
                            {title: '目標', value: '時間', note: '長期目標を継続追跡', trend: 'up'},
                            {
                                title: 'リスク管理',
                                value: 'ヘッジ',
                                note: 'リスクエクスポージャーを自動管理',
                                trend: 'focus'
                            },
                            {
                                title: '全リンク追跡',
                                value: '透明性',
                                note: 'AIの判断プロセスを確認可能',
                                trend: 'stable'
                            }
                        ],
                        featuresTitle: {
                            eyebrow: 'コア能力',
                            title: '6つのステップでAIがあなたのアロケーションを完全管理',
                            description: '目標分解から動的リバランスまで、AIが境界内であなたの資産アロケーション戦略を完全管理。'
                        },
                        flowSteps: [
                            {
                                title: '目標分解',
                                description: '長期投資目標を実行可能な段階的タスクに分解し、期待リターンとリスク許容度を明確化',
                                tags: ['分解', '目標', 'フェーズ']
                            },
                            {
                                title: '資産アロケーション計画',
                                description: '投資目標と市場環境に基づき、大分類資産の配分比率と投資戦略を計画',
                                tags: ['計画', '配分', '戦略']
                            },
                            {
                                title: '目標資産の比較選定',
                                description: '各資産カテゴリーで優良銘柄をスクリーニングし、多次元比較で最適なポートフォリオ候補を確定',
                                tags: ['スクリーニング', '比較', '銘柄']
                            },
                            {
                                title: 'ポートフォリオ構築と取引実行',
                                description: '既定の配分比率で建玉操作を実行、ポートフォリオ構築を完了し進捗を継続追跡',
                                tags: ['構築', '実行', '建玉']
                            },
                            {
                                title: 'リスク監視とコンプライアンス審査',
                                description: 'ポートフォリオのリスクエクスポージャーとコンプライアンス指標をリアルタイム監視、投資が常に既定境界内で運用されることを確保',
                                tags: ['監視', 'リスク管理', 'コンプライアンス']
                            },
                            {
                                title: '資産価値追跡とリバランス',
                                description: '資産価値の変化を継続追跡、目標閾値から逸脱した場合に自動でリバランスを発動、ポートフォリオを目標軌道に復帰',
                                tags: ['追跡', 'リバランス', '再調整']
                            }
                        ],
                        usageTitle: {
                            eyebrow: '利用フロー',
                            title: '3ステップでAI資産管理を開始',
                            description: '目標設定から長期運用まで、たった3ステップ。'
                        },
                        usageSteps: [
                            {
                                title: '投資目標の設定',
                                description: 'AIにあなたのリスク選好とリターン期待を伝える',
                                detail: '一度設定、継続追跡'
                            },
                            {
                                title: 'AI自動運用',
                                description: 'ポートフォリオ構築、動的監視、リバランスのすべてをAIが自動実行',
                                detail: '手動操作不要'
                            },
                            {
                                title: 'いつでも状態確認',
                                description: 'パネルを開けばポートフォリオのパフォーマンスとAIの調整記録を確認',
                                detail: 'プロセスは透明で追跡可能'
                            }
                        ]
                    }
                ]
            },
            resources: {
                proofBarItems: ['市場インプット', '構造化判断', 'リスク検証', '実行フィードバック'],
                howItWorksHero: {
                    eyebrow: '動作メカニズム',
                    title: '取引チェーン、透明で制御可能',
                    description: 'D-Senseはブラックボックスのシグナルツールではなく、インプット、判断、リスク管理、実行を説明可能なプロセスとして組織化します。',
                    panelLabel: 'プロセスクローズドループ',
                    panelTitle: 'インプットから実行のサイクルへ',
                    panelDescription: '各ステップに明確な役割があり、トレーダーがアクションの理由を理解できるようにします。'
                },
                howItWorksSteps: [
                    {
                        title: '市場インプット',
                        description: '相場、構造、コンテキストを集約し、判断に使えるインプットを形成。',
                        detail: '市場観察を複数ページに分散させない。'
                    },
                    {
                        title: '戦略判断',
                        description: 'AIとルールが協調して構造化された提案を形成。',
                        detail: '結論、タグ、理由を一緒に確認可能。'
                    },
                    {
                        title: 'リスク検証',
                        description: 'リスク境界を先に判断し、アクションの可否を決定。',
                        detail: '条件を満たさない場合は実行に移さない。'
                    },
                    {
                        title: '実行フィードバック',
                        description: '実行結果と状態が統一ビューに戻る。',
                        detail: '戦略と結果を継続追跡。'
                    }
                ],
                howItWorksFlowSteps: [
                    {
                        title: 'データ集約',
                        description: '複数ソースの相場、オーダーブック、オンチェーンデータを統合し、構造化された統一インプット基盤を形成',
                        tags: ['相場', 'オンチェーン', 'マルチソース']
                    },
                    {
                        title: '市場感知',
                        description: 'AIが市場状態と構造変化を継続スキャンし、重要なシグナルと取引機会を識別',
                        tags: ['感知', 'スキャン', '識別']
                    },
                    {
                        title: '取引計画',
                        description: '市場構造とリスク境界を総合し、自律的に取引判断と戦略計画を形成',
                        tags: ['分析', '判断', '計画']
                    },
                    {
                        title: 'リスク検証',
                        description: 'ユーザー設定のリスク境界内で項目ごとに検証し、各アクションのコンプライアンスと安全性を確保',
                        tags: ['境界', '検証', '許可']
                    },
                    {
                        title: '自動実行',
                        description: 'AIが境界内で自律的に取引操作を実行し、注文実行状態を継続追跡',
                        tags: ['発注', '実行', '追跡']
                    },
                    {
                        title: '状態フィードバック',
                        description: '実行結果と市場状態がシステムに継続フィードバックされ、次の分析反復へ',
                        tags: ['フィードバック', '反復', 'クローズドループ']
                    }
                ],
                howItWorksAiDecision: [
                    {
                        eyebrow: 'AI感知',
                        title: 'AIが市場状態をどう理解するか',
                        description: 'システムは複数ソースの市場データをAIが理解可能な構造化インプットに変換し、大規模言語モデルを通じて市場構造、トレンド変化、異常シグナルを識別し、可読性のある分析結論を生成します。',
                        bullets: [
                            'Kライン、オーダーブック、オンチェーン等多様なデータソースを統合',
                            'AIが重要な価格帯と市場構造変化を識別',
                            'シグナル集約によるノイズ除去、高確信度の取引機会を抽出',
                            '自然言語で構造化された分析結論を出力'
                        ],
                        metrics: [
                            {title: 'データ次元', value: '50+', note: 'テクニカル指標と市場シグナル', trend: 'up'},
                            {title: '分析頻度', value: 'リアルタイム', note: '市場状態を継続スキャン', trend: 'focus'}
                        ]
                    },
                    {
                        eyebrow: 'AI意思決定',
                        title: '分析から実行へ、AIが取引判断を行う仕組み',
                        description: 'システムは市場状態、リスク境界、履歴データを含むコンテキストを構築し、大規模言語モデルを呼び出して構造化された取引提案を生成、解析エンジンが実行可能な取引アクションに変換します。',
                        bullets: [
                            '多次元コンテキストを含む意思決定用Promptを動的構築',
                            '大規模モデルが構造化された取引判断を出力（方向/ポジション/タイミング）',
                            '解析エンジンがモデル出力を実行可能な取引命令に変換',
                            'リスク検証通過後に取引所へ提出して実行'
                        ],
                        metrics: [
                            {title: '判断遅延', value: '<30s', note: '分析から取引提案の生成まで', trend: 'up'},
                            {
                                title: '追跡可能性',
                                value: '全リンク',
                                note: 'Prompt→出力→アクション全てが遡及可能',
                                trend: 'focus'
                            }
                        ]
                    }
                ],
                howItWorksBlocks: [
                    {
                        title: '入力層 — マルチソースデータ集約',
                        description: '取引所の相場、オンチェーンデータ、テクニカル指標、市場センチメントを統合し、統一されたデータ入力パイプラインを構築、クリーニングと標準化を経て上位層に提供。',
                        tags: ['相場API', 'オンチェーンデータ', 'テクニカル指標', 'データクリーニング'],
                        iconName: 'database'
                    },
                    {
                        title: '判断層 — AIインテリジェント分析エンジン',
                        description: '大規模言語モデルとルールエンジンを融合し、市場状態を多次元分析。動的にPromptを構築しモデルを呼び出して、構造化シグナルと取引提案を出力。',
                        tags: ['LLM', 'Promptエンジニアリング', 'ルールエンジン', 'シグナル集約'],
                        iconName: 'brain'
                    },
                    {
                        title: '実行層 — リスク管理と取引チェーン',
                        description: 'リスク検証を実行チェーン前に組み込み、全ての取引が既定境界内で実行されることを確保。結果はリアルタイムでフィードバックされ、完全な取引クローズドループを形成。',
                        tags: ['リスク検証', '注文実行', '状態同期', '異常処理'],
                        iconName: 'shield'
                    }
                ],
                howItWorksSections: {
                    architecture: {
                        eyebrow: 'システムアーキテクチャ',
                        title: 'データから実行までの6ステップクローズドループ',
                        description: '各プロセスに明確な役割があり、AIが境界内で自律運用、継続的反復。'
                    },
                    aiDecision: {
                        eyebrow: 'AIインテリジェンス',
                        title: '市場感知と取引判断メカニズム',
                        description: 'システムが市場シグナルをどのように理解し、実行可能な取引判断に変換するか。'
                    },
                    layers: {
                        eyebrow: '技術アーキテクチャ',
                        title: '3層アーキテクチャ、各層が役割を果たす',
                        description: 'システムは3つのコア階層で構成され、各層が明確な技術的役割を担う。'
                    },
                    demo: {
                        eyebrow: 'ライブデモ',
                        title: 'AIが市場をどう感知するか見る',
                        description: 'AI分析パネルのシミュレーションで、システムが市場シグナルを識別・マークする方法を表示。'
                    }
                },
                howItWorksDemo: {
                    symbol: 'BTC/USDT',
                    symbolChange: '+2.34%',
                    symbolChangeUp: true,
                    price: '67,432.50',
                    priceChange: '+1,542.80',
                    priceChangeUp: true,
                    signalTags: [
                        {label: '構造的ブレイク', type: 'signal'},
                        {label: '出来高拡大', type: 'signal'},
                        {label: '高ボラティリティ', type: 'risk'},
                        {label: 'AI強気', type: 'info'}
                    ],
                    aiJudgmentTitle: 'AI判断',
                    aiJudgmentValue: '強気・中程度の確信度',
                    aiJudgmentNote: '価格が直近レジスタンスをブレイク、出来高も伴う',
                    keyLevelTitle: '重要価格帯',
                    keyLevelValue: '67,200 / 68,500',
                    keyLevelNote: '上方レジスタンス 68,500、下方サポート 67,200'
                },
                howItWorksMetrics: [
                    {
                        title: '処理ステップ',
                        value: '6ステップ',
                        note: 'データ集約から状態フィードバックまでの完全クローズドループ',
                        trend: 'focus'
                    },
                    {title: '判断遅延', value: '<30s', note: '分析から取引提案の生成まで', trend: 'up'},
                    {title: 'データ次元', value: '50+', note: 'テクニカル指標と市場シグナル', trend: 'up'}
                ],
                howItWorksCta: {
                    eyebrow: '利用開始',
                    title: '仕組みを理解して体験開始',
                    description: 'システムの動作を理解した後、POCを通じてあなたの取引スタイルに合うか検証してください。'
                },
                riskHero: {
                    eyebrow: 'リスク管理・セキュリティ',
                    title: '境界を先に、行動に節度を',
                    description: 'リスク管理は付属機能ではなく、システムが長期利用に値するかの前提条件です。',
                    panelLabel: 'リスク優先',
                    panelTitle: '境界を行動より先に、安全を効率より先に',
                    panelDescription: 'リスクが明確でない時は、システムはリスクを拡大するのではなく、まず行動を縮小すべきです。'
                },
                riskGuardItems: [
                    {
                        title: 'リスクモード優先',
                        description: '先に選好と境界を定義し、その後実行の可否を判断。',
                        bullets: ['保守/慎重/安定/積極/積極的 の5段階から選択可能', '閾値を事前設定、アクション前に自動検証', '条件不適合時は実行不可']
                    },
                    {
                        title: '利益確定・損切りとポジション管理',
                        description: 'ドローダウンとエクスポージャーを許容範囲内に制御。',
                        bullets: ['ポジション上限を自動制約', '利益確定・損切りの連動発動', '無制限なポジション追加を防止']
                    },
                    {
                        title: '権限とAPIキーの分離',
                        description: '最小権限の原則で機密操作を処理。',
                        bullets: ['API権限の階層管理', 'APIキーを暗号化して分離保存', '異常時は優先的に安全側にフォールバック']
                    }
                ],
                riskMatrixItems: [
                    {
                        title: '実行前検証',
                        description: '各アクション発行前に、システムがリスク条件を満たしているか検証。',
                        tags: ['事前検証', '条件判断'],
                        iconName: 'shield'
                    },
                    {
                        title: '権限境界',
                        description: 'インターフェースとアカウント能力を必要最小範囲に制限し、権限外操作を防止。',
                        tags: ['権限管理', 'セキュリティ分離'],
                        iconName: 'layers'
                    },
                    {
                        title: '継続的フィードバック',
                        description: '実行結果とリスク状態が継続的に統一ビューに戻り、監視クローズドループを形成。',
                        tags: ['状態フィードバック', '継続追跡'],
                        iconName: 'waypoints'
                    }
                ],
                riskSections: {
                    guard: {
                        eyebrow: 'リスク管理ルール',
                        title: '3層の防御線、重層的なチェック',
                        description: 'リスク選好からポジション管理、権限制御へと、段階的に安全境界を強化。'
                    },
                    matrix: {
                        eyebrow: '実行保証',
                        title: 'すべてのステップがルール内で動作',
                        description: 'システムは実行チェーンの各ノードに検証ロジックを組み込み、アクションが常に既定境界内にあることを確保。'
                    }
                },
                riskCta: {
                    eyebrow: '利用開始',
                    title: 'リスク管理を取引より先に',
                    description: 'リスク境界があなたの取引をどう守るかを理解しましょう。'
                },
                riskMetrics: [
                    {title: 'リスク管理階層', value: '3層', note: 'モード→ポジション→権限の段階的強化', trend: 'focus'},
                    {title: '検証タイミング', value: '実行前', note: 'アクション発行前に自動検証', trend: 'up'},
                    {title: '権限粒度', value: '階層化', note: 'API権限の最小化管理', trend: 'stable'}
                ]
            },
            pocPage: {
                hero: {
                    eyebrow: 'POC',
                    title: 'POCを通じて、プロダクトの動作をより直感的に理解',
                    description: 'ここでは漠然とした概念ではなく、アシストトレードと自動トレードの典型的なシナリオ、価値、インタラクション構造を紹介します。',
                    tags: ['アシストトレード', '自動トレード', 'シナリオ展示', '機能デモ'],
                    panelLabel: 'POCエントリ',
                    panelTitle: '2つの方向性、2つの使用体験',
                    panelDescription: 'アシストトレードと自動トレードがそれぞれ異なる使用目標にどう応えるかを確認。',
                    assistText: '先にPOCを見て、あなたの取引スタイルに合うか判断してください。',
                    topCardLabel: '方向性一',
                    topCardTitle: 'アシストトレード',
                    topCardText: '市場を読み解き、判断を形成、行動を準備',
                    bottomCardLabel: '方向性二',
                    bottomCardTitle: '自動トレード',
                    bottomCardText: 'ルールを継続実行し、結果を継続フィードバック'
                },
                entries: [
                    {
                        title: 'アシストトレード POC',
                        slogan: 'より早く見極め、より安定した判断',
                        description: '市場観察、シグナル理解から取引補助提案までの完全なデモを確認。',
                        tags: ['市場観察', 'AI説明', 'リスク通知'],
                        path: '/site/poc/assist-trading'
                    },
                    {
                        title: '自動トレード POC',
                        slogan: 'ルール優先、実行は止まらない',
                        description: '戦略が境界内で継続実行され、結果と状態が同期される仕組みを確認。',
                        tags: ['ルール実行', 'リスク優先', '結果フィードバック'],
                        path: '/site/poc/auto-trading'
                    },
                    {
                        title: 'パターン認識 POC',
                        slogan: 'すべてのローソク足が見抜かれる',
                        description: 'Kライン形態の自動認識とAI深層分析の完全なデモを確認。',
                        tags: ['Kライン形態', 'AI分析', 'バックテスト検証'],
                        path: '/site/poc/pattern'
                    }
                ],
                features: [
                    {
                        title: 'シナリオ志向の展示',
                        description: '単にページを表示するのではなく、そのページが何を解決するのかを明確に説明。',
                        tags: ['シナリオ', '価値']
                    },
                    {
                        title: 'フロー志向の表現',
                        description: 'モジュール間の前後関係と情報の流れを提示。',
                        tags: ['フロー', '情報フロー']
                    },
                    {
                        title: '境界志向の説明',
                        description: 'デモ時に制限条件とリスク境界を一緒に説明。',
                        tags: ['境界', 'リスク管理']
                    }
                ],
                faqIntro: {
                    eyebrow: '補足説明',
                    title: 'POCを見る前に、これらの境界を知っておく',
                    description: '少数の高頻度Q&Aで、使用前に最も重要な情報を明確にします。'
                },
                faqItems: [
                    {
                        question: 'このシステムは誰に向いていますか？',
                        answer: 'チャート監視を減らし、判断効率を高め、リスク境界を理解したい個人トレーダーに適しています。'
                    },
                    {
                        question: 'システムは自動で取引を実行しますか？',
                        answer: '半自動および自動実行をサポートしていますが、有効にするかどうかは、あなた自身がリスクを理解した上で判断すべきです。'
                    },
                    {
                        question: 'なぜPOCを見る必要があるのですか？',
                        answer: 'POCは製品ロジック、適用シナリオ、境界を先に理解し、その後に体験を開始するか判断するために役立ちます。'
                    },
                    {
                        question: 'どうやって使い始めればいいですか？',
                        answer: '招待コードをお持ちの方は直接登録、お持ちでない方はお問い合わせページから情報を送信してください。'
                    }
                ]
            },
            assistPocPage: {
                hero: {
                    eyebrow: 'アシストトレード POC',
                    title: '市場を把握し、盲目的な判断をしない',
                    description: '高ボラティリティ環境でもトレーダーが迅速に観察、判断、行動準備を完了できるよう支援しますが、最終的なコントロールはユーザーの手にあります。',
                    tags: ['市場シグナル', 'AI補助', 'リスク通知', 'アクション提案'],
                    panelLabel: 'コアスローガン',
                    panelTitle: 'より早く見極め、より安定した判断',
                    panelDescription: 'まずシステムでチャンスを明確にし、それから行動するか判断します。',
                    assistText: 'アシストトレードは判断の補助を重視し、判断の代替ではありません。',
                    topCardLabel: '使用シーン',
                    topCardTitle: 'セッション前 / セッション中',
                    topCardText: '高頻度の観察と迅速な取引判断形成に最適',
                    bottomCardLabel: '結果',
                    bottomCardTitle: '迷いの軽減',
                    bottomCardText: '観察、理解、行動準備を同一フローに統合'
                },
                features: [
                    {
                        title: '市場シグナル集約',
                        description: '重要なタグと市場変化を一箇所に集約。',
                        tags: ['相場', 'タグ']
                    },
                    {title: 'AI補助判断', description: '構造化された説明と提案を迅速に取得。', tags: ['AI', '説明']},
                    {title: 'リスク事前通知', description: '行動前に重要な制限条件を確認。', tags: ['リスク', '通知']},
                    {
                        title: 'アクション提案出力',
                        description: '判断結果をより行動しやすい形に整理。',
                        tags: ['提案', '実行前']
                    },
                ],
                flowSteps: [
                    {
                        title: '市場を観察',
                        description: '主要な市場変化とコンテキストを集中的に確認。',
                        detail: '最初のフィルタリングを迅速に完了。'
                    },
                    {
                        title: 'シグナルを理解',
                        description: 'AIが理由、タグ、傾向を整理。',
                        detail: '結論をより理解しやすく。'
                    },
                    {
                        title: '境界を確認',
                        description: 'まずリスク通知と実行制限を確認。',
                        detail: 'チャンスだけを見て代償を見落とさない。'
                    },
                    {
                        title: '行動を準備',
                        description: 'より明確な次のアクション計画を形成。',
                        detail: '実際に実行に移すかはあなたが決定。'
                    }
                ],
                stories: [
                    {
                        eyebrow: '価値一',
                        title: 'より早くチャンスを見極める',
                        description: '分散した観察プロセスを一つのワークスペースに集約し、切り替えコストを削減。',
                        bullets: ['分散型のチャート監視を削減', '情報をより速く収束', '迅速な判断状態への移行を支援'],
                        metrics: [
                            {title: '観察方法', value: '統一', note: '重要な入力を集中表示', trend: 'stable'},
                            {title: '反応速度', value: 'より速い', note: '切り替えと迷いを削減', trend: 'up'}
                        ]
                    },
                    {
                        eyebrow: '価値二',
                        title: '判断をより説明しやすく',
                        description: 'シグナルだけでなく、判断根拠とリスクも一緒に表現。',
                        bullets: ['シグナルと理由を一緒に確認', 'リスクとチャンスを同時に表現', '判断の振り返りを容易に'],
                        metrics: [
                            {title: '出力形式', value: '構造化', note: 'タグ、説明、提案が連動', trend: 'up'},
                            {
                                title: '振り返り性',
                                value: 'より強い',
                                note: '判断プロセスをより追跡しやすい',
                                trend: 'focus'
                            }
                        ]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: 'コア能力',
                        title: 'シグナルからアクションへの完全チェーン',
                        description: '機能を積み上げるのではなく、アシストトレードの重要な能力を実用的なシステムパスとして組織化。'
                    },
                    flow: {
                        eyebrow: '標準フロー',
                        title: '毎日ワークスペースを開き、4ステップで判断準備完了',
                        description: '観察から意思決定まで、システムが分散したステップを反復可能なワークフローに集約。'
                    },
                    stories: {
                        eyebrow: '価値説明',
                        title: 'アシストトレードが解決する2つのコア問題',
                        description: '情報過多の時にいかに素早くチャンスを見つけるか、判断が複雑な時にいかに容易に説明するか。'
                    },
                    demo: {
                        eyebrow: 'ライブデモ',
                        title: 'トレーディングワークステーション展示',
                        description: 'リアルタイム相場、ポジション、取引履歴まで、トレーディングワークステーションのコアデータパネルを完全表示。'
                    },
                    cta: {
                        primaryLabel: '招待コードを取得',
                        primaryTo: '/site/assist-trading'
                    }
                }
            },
            autoPocPage: {
                hero: {
                    eyebrow: '自動トレード POC',
                    title: '目標を設定すれば、AIが代わりに取引',
                    description: 'リスク許容度と目標利益率を設定するだけで、AIが自動で機会をマッチング、ポジション管理、資金効率を最適化——戦略の記述もチャート監視も不要です。',
                    tags: ['リスク選好', '目標収益', '資金活用', 'AI自律取引'],
                    panelLabel: 'コアコンセプト',
                    panelTitle: 'あなたが目標を決め、AIが実行を担当',
                    panelDescription: '取引に複雑な戦略プログラミングは不要。リスク許容度と希望リターンをAIに伝えるだけで、最適な経路を自動で見つけます。',
                    assistText: 'リスク選好と収益目標を設定すれば、残りはAIにお任せ。',
                    topCardLabel: '適用シーン',
                    topCardTitle: 'シンプルでダイレクト',
                    topCardText: 'プログラミングも戦略記述も不要、すべてのトレーダーに適合',
                    bottomCardLabel: 'コアアドバンテージ',
                    bottomCardTitle: 'AI自律意思決定',
                    bottomCardText: 'AIが市場をリアルタイム分析、設定された境界内で自律的に最適な取引機会を探索'
                },
                features: [
                    {
                        title: 'リスク選好設定',
                        description: '最大許容損失率を設定。AIはこの境界内で厳密に運用。',
                        tags: ['リスク管理', '安心']
                    },
                    {
                        title: '目標収益管理',
                        description: '期待利益率を設定。AIがその目標に合った市場機会を自動検索。',
                        tags: ['目標', '収益']
                    },
                    {
                        title: '資金活用最適化',
                        description: 'AIが各取引の資金配分をインテリジェントに最適化し、資金使用効率を最大化。',
                        tags: ['効率', 'ポジション']
                    },
                    {
                        title: 'AI自律取引実行',
                        description: '24時間365日市場を継続監視、 timing、建玉、決済の全フローを自動完了。',
                        tags: ['自動', '年中無休']
                    },
                ],
                flowSteps: [
                    {
                        title: 'リスク境界の設定',
                        description: '許容可能な最大ドローダウンと1回あたりの最大損失額を設定。',
                        detail: 'AIのすべての判断はこの境界内で実行'
                    },
                    {
                        title: '収益目標の設定',
                        description: '期待する年率または四半期の利益率目標を設定。',
                        detail: 'AIはこれを基準に適切な取引機会をマッチング'
                    },
                    {
                        title: 'AIインテリジェントマッチング',
                        description: 'AIが市場をリアルタイム分析し、あなたのリスク収益選好に合った機会をマッチング。',
                        detail: '資金活用率と連携してインテリジェントにポジション配分'
                    },
                    {
                        title: '自動実行と最適化',
                        description: 'AIが自律的に取引を実行し、市場変化に応じて戦略を継続調整。',
                        detail: 'すべての取引は追跡可能、収益は明確に可視化'
                    }
                ],
                stories: [
                    {
                        eyebrow: '価値一',
                        title: '戦略不要、目標だけ',
                        description: '取引戦略を記述する必要はありません。リスク許容度と収益期待値をAIに伝えるだけで、最適な取引経路を自動で見つけます。',
                        bullets: ['ゼロプログラミング、設定すれば即実行', 'AIが自動最適化で機会をマッチング', 'チャート監視不要、年中無休で自律運用'],
                        metrics: [
                            {
                                title: '習得難易度',
                                value: '非常に低い',
                                note: '2つのパラメータを設定するだけ',
                                trend: 'up'
                            },
                            {
                                title: '判断方式',
                                value: 'AI駆動',
                                note: 'AIが人手による判断と実行を代替',
                                trend: 'stable'
                            }
                        ]
                    },
                    {
                        eyebrow: '価値二',
                        title: 'すべての資金を効率的に運用',
                        description: 'AIは取引だけでなく資金管理も支援——ポジション比率を動的に調整し、資金活用率を常に最適な状態に維持。',
                        bullets: ['インテリジェントポジション管理、動的資金配分', '資金活用率をリアルタイム調整', 'アイドル資金も価値を生み出す'],
                        metrics: [
                            {title: '資金効率', value: '最適', note: 'AIが資金配分比率を継続最適化', trend: 'focus'},
                            {title: '収益源泉', value: '透明', note: 'すべての損益が追跡・検証可能', trend: 'up'}
                        ]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: 'コア能力',
                        title: 'AI自律取引の4つの能力',
                        description: '戦略を記述する必要はなく、選好を設定するだけでAIがすべてを代行。'
                    },
                    flow: {
                        eyebrow: '利用フロー',
                        title: '4ステップの設定でAIが即座に稼働',
                        description: '選好を設定したその瞬間から、AIがあなたのために働き始めます。'
                    },
                    stories: {
                        eyebrow: 'コアバリュー',
                        title: 'シンプル、しかし強力',
                        description: '複雑な取引を、シンプルな選択に変える。'
                    },
                    demo: {
                        eyebrow: 'インタラクティブデモ',
                        title: 'AI自動取引デモ',
                        description: 'API Keyとリスク選好を設定して、AI自動取引の完全な流れを体験してください。'
                    }
                }
            },
            patternPocPage: {
                hero: {
                    eyebrow: 'パターン認識 POC',
                    title: 'AI駆動のKライン形態認識分析',
                    description: '検出から分析まで、AIによるKライン形態の認識、バックテスト、取引機会評価能力を完全に紹介。',
                    tags: ['Kライン形態', 'AI分析', 'バックテスト', '取引シグナル'],
                    panelLabel: 'コア能力',
                    panelTitle: 'すべてのローソク足の形態を見える化',
                    panelDescription: '数百種類のKライン形態検出アルゴリズムに基づき、AI深層分析を組み合わせ、潜在的な取引機会の発見を支援。',
                    assistText: '手動でチャートをめくる必要はなく、システムが自動で形態をマークしAI分析を提供。',
                    topCardLabel: '形態検出',
                    topCardTitle: '自動認識',
                    topCardText: 'システムが自動でKライン形態を検出、1分足から日足までの多様な時間枠に対応',
                    bottomCardLabel: 'AI分析',
                    bottomCardTitle: '深層解釈',
                    bottomCardText: 'AIが形態を総合分析し、エントリー、ストップロス、目標価格の提案を提供'
                },
                features: [
                    {
                        title: 'マルチ形態検出',
                        description: 'ハンマー、包み足、モーニングスターなど数百種類の古典的Kライン形態を自動検出。',
                        tags: ['形態', 'カバレッジ']
                    },
                    {
                        title: 'AI深層分析',
                        description: '大規模モデルと連携し、エントリー価格、目標価格、ストップロス提案を含む多次元分析を実行。',
                        tags: ['AI', '分析']
                    },
                    {
                        title: 'ヒストリカルバックテスト',
                        description: '各形態の過去パフォーマンスをバックテストし、勝率、損益比率などの重要指標を統計。',
                        tags: ['バックテスト', 'データ']
                    },
                    {
                        title: 'ゾーン認識',
                        description: 'サポート/レジスタンスゾーン、レンジ相場などの領域形態を自動認識し、重要価格帯の判断を補助。',
                        tags: ['ゾーン', 'サポート/レジスタンス']
                    },
                ],
                flowSteps: [
                    {
                        title: '銘柄の選択',
                        description: '対象トレードペアを選択、システムが自動でKラインデータを取得。',
                        detail: '無期限先物と現物に対応、BTC、ETHなど主要通貨をカバー'
                    },
                    {
                        title: '形態検出',
                        description: 'システムが自動でKラインをスキャンし、全形態シグナルを識別・マーク。',
                        detail: '1分足から日足までの多様な時間枠に対応、検出結果をリアルタイム更新'
                    },
                    {
                        title: 'AI分析',
                        description: '検出された形態に対してAI補助分析を実行し、取引参考情報を提供。',
                        detail: 'エントリー、ストップロス、目標価格、リスクレベルの提案を提供'
                    },
                    {
                        title: 'バックテスト検証',
                        description: '形態の過去パフォーマンスデータを確認し、シグナルの信頼性を補助判断。',
                        detail: '勝率、損益比率、過去の損益記録を表示'
                    }
                ],
                stories: [
                    {
                        eyebrow: '価値一',
                        title: '形態認識をより包括的に',
                        description: '数百種類の形態を自動検出。シンプルなハンマーから複雑なモーニングスターまで、見逃しがちなシグナルも発見。',
                        bullets: ['自動スキャン、手動でのチャート探索不要', '数百種類の古典的形態をカバー', 'マルチ時間枠検出に対応'],
                        metrics: [
                            {title: '検出効率', value: '自動', note: 'リアルタイム監視、人件費削減', trend: 'up'},
                            {title: 'カバレッジ範囲', value: '網羅的', note: '数百種類の形態を自動認識', trend: 'focus'}
                        ]
                    },
                    {
                        eyebrow: '価値二',
                        title: '分析をより深く',
                        description: 'AIは形態の種類だけでなく、信頼度、潜在損益比率を分析し、具体的な取引提案を提供。',
                        bullets: ['AIが多次元で形態シグナルを分析', 'エントリー、ストップロス、目標価格を提示', 'リスク評価が意思決定を補助'],
                        metrics: [
                            {title: '分析深度', value: '網羅的', note: '多次元の総合分析', trend: 'up'},
                            {title: '参考価値', value: '実用的', note: '具体的な価格提案とリスク通知', trend: 'focus'}
                        ]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: 'コア能力',
                        title: '検出から分析まで、完全な形態認識チェーン',
                        description: '単なる形態検出ではなく、認識、分析、履歴検証を完全なツールチェーンとして組織化。'
                    },
                    flow: {
                        eyebrow: '標準フロー',
                        title: '4ステップで形態認識と分析を完了',
                        description: '銘柄選択からバックテスト検証まで、システムが形態認識の完全なワークフローを完了。'
                    },
                    stories: {
                        eyebrow: '価値説明',
                        title: '形態認識が解決する3つのコア問題',
                        description: '手動チャート確認の非効率、シグナルの見落とし、分析の浅さ——形態認識がこれらの障壁を除去。'
                    },
                    demo: {
                        eyebrow: 'インタラクティブデモ',
                        title: 'Kライン形態認識デモ',
                        description: 'Kライン形態認識を通じて、市場構造の変化と取引機会を素早く発見します。'
                    },
                    cta: {
                        primaryLabel: '招待コードを取得',
                    }
                }
            },
            membershipPage: {
                hero: {
                    eyebrow: 'メンバーシップ',
                    title: '投資の各段階に、ちょうどよい機能を',
                    description: '無料体験からエキスパート級の完全自動取引まで、4つのプランで洞察から執行・配分までフルカバーします。',
                    panelLabel: 'オンライン開通',
                    panelTitle: 'オンラインで開通、すぐに有効',
                    panelDescription: 'プランを選んでオンラインでお支払いいただくと、すぐに有効になります。サブスクリプションの状態と有効期限は管理画面でいつでも確認できます。'
                },
                sectionTitles: {
                    compare: {
                        eyebrow: 'プラン比較',
                        title: '4つのプランから選択',
                        description: '料金と機能マトリクスを一覧。DCAプラン上限はプランに応じて拡大します。'
                    },
                    features: {
                        eyebrow: '有料機能',
                        title: '各機能は具体的な課題を解決します',
                        description: 'DCAアシスタント / ヘッジ / パターン認識 / 自動トレードをプラン別に解放します。'
                    },
                    cta: {
                        eyebrow: '開通ガイド',
                        title: 'アップグレードの準備はできていますか？',
                        description: 'プランを選んでオンラインでお支払いいただくと、すぐに開通して対象機能が使えます。'
                    }
                },
                priceLabels: {monthly: '月払い', yearly: '年払い'},
                tiers: [
                    {
                        name: '無料', tagline: '基本機能を体験', monthlyPrice: '0', yearlyPriceMonthly: '0',
                        features: [
                            {label: 'レポート購読・閲覧', enabled: true},
                            {label: 'DCAアシスタント', enabled: false},
                            {label: 'ヘッジ取引', enabled: false},
                            {label: 'パターン認識クイック取引', enabled: false},
                            {label: '自動トレード', enabled: false}
                        ]
                    },
                    {
                        name: 'スターター',
                        tagline: '計画的な自動買付',
                        monthlyPrice: '5.9U',
                        yearlyPriceMonthly: '3.9U/月',
                        features: [
                            {label: 'レポート購読・閲覧', enabled: true},
                            {label: 'DCAアシスタント', enabled: true, note: '最大3プラン'},
                            {label: 'ヘッジ取引', enabled: false},
                            {label: 'パターン認識クイック取引', enabled: false},
                            {label: '自動トレード', enabled: false}
                        ]
                    },
                    {
                        name: 'プロ',
                        tagline: '取引とパターンを強化',
                        monthlyPrice: '15.9U',
                        yearlyPriceMonthly: '9.9U/月',
                        highlight: true,
                        features: [
                            {label: 'レポート購読・閲覧', enabled: true},
                            {label: 'DCAアシスタント', enabled: true, note: '最大10プラン'},
                            {label: 'ヘッジ取引', enabled: true},
                            {label: 'パターン認識クイック取引', enabled: true},
                            {label: '自動トレード', enabled: false}
                        ]
                    },
                    {
                        name: 'エキスパート',
                        tagline: 'AI完全自動取引',
                        monthlyPrice: '59.9U',
                        yearlyPriceMonthly: '29.9U/月',
                        features: [
                            {label: 'レポート購読・閲覧', enabled: true},
                            {label: 'DCAアシスタント', enabled: true, note: '最大15プラン'},
                            {label: 'ヘッジ取引', enabled: true},
                            {label: 'パターン認識クイック取引', enabled: true},
                            {label: '自動トレード', enabled: true}
                        ]
                    }
                ],
                featureIntros: [
                    {
                        title: 'DCAアシスタント',
                        description: '銘柄と指標条件でプランを作成。システムが確定済みローソク足を監視し、条件成立で自動成行買い。1回または繰り返し実行。',
                        tags: ['プラン型', '条件トリガー', '自動買付']
                    },
                    {
                        title: 'ヘッジ取引',
                        description: '単脚・クロス銘柄ヘッジ、保護レッグ自動再装着、利確・損切り・エクスポージャー管理を一元化。',
                        tags: ['単脚ヘッジ', '保護レッグ', 'エクスポージャー管理']
                    },
                    {
                        title: 'パターン認識クイック取引',
                        description: 'AIが重要パターンを認識し、ワンクリック注文で判断を素早く実行。',
                        tags: ['パターン認識', 'クイック注文']
                    },
                    {
                        title: '自動トレード',
                        description: 'リスク下限を設定すれば、AIが境界内で自律的に判断・取引を継続します。',
                        tags: ['AI判断', '境界内の自律', '継続取引']
                    }
                ],
                faqIntro: {
                    eyebrow: 'FAQ',
                    title: 'よくある質問',
                    description: 'メンバーシップと開通に関するよくある質問。'
                },
                faqItems: [
                    {
                        question: 'メンバーシップはどうやって開通しますか？',
                        answer: '料金ページでプランを選択してオンラインでお支払いいただくと、すぐに開通します。期限前にお知らせが届き、メンバーシップページでいつでも管理できます。'
                    },
                    {
                        question: 'メンバーシップの期限が切れるとどうなりますか？',
                        answer: '次に有効な契約プランへ自動降格します（なければ無料）。新プランの上限を超えるDCAプランは自動一時停止しアプリ内通知が届きます。更新後は手動で再開してください。'
                    },
                    {
                        question: 'DCAプランの上限はどう計算されますか？',
                        answer: 'プラン別: スターター3件、プロ10件、エキスパート15件。削除されていないプランのみ対象で、一時停止中も実行中もカウントされます。'
                    },
                    {
                        question: '年払いの割引は？',
                        answer: '年払いは月換算単価が安くなります: スターター月3.9U、プロ月9.9U、エキスパート月29.9U。'
                    }
                ]
            },
            contactPage: {
                hero: {
                    eyebrow: 'Pour commencer',
                    title: 'Obtenez un code d\'invitation et découvrez D-Sense',
                    description: 'Si vous n\'avez pas encore de code d\'invitation, soumettez vos informations et nous organiserons la suite selon vos besoins et le calendrier de déploiement.',
                    tags: ['Code invitation', 'Demander accès', 'Pour commencer'],
                    panelLabel: 'Comment commencer',
                    panelTitle: 'Code d\'invitation ? Inscrivez-vous. Pas de code ? Faites une demande.',
                    panelDescription: 'Un parcours clair pour obtenir un code d\'invitation et démarrer l\'expérience.',
                    assistText: 'Comprenez d\'abord le mode d\'accès, puis décidez si vous souhaitez soumettre vos informations.',
                    topCardLabel: 'Démarrage rapide',
                    topCardTitle: 'Deux points d\'entrée',
                    topCardText: 'Inscrivez-vous directement, ou demandez d\'abord un code',
                    bottomCardLabel: 'Mode d\'accès',
                    bottomCardTitle: 'Expérience sur invitation',
                    bottomCardText: 'Le code d\'invitation permet de contrôler le rythme de l\'expérience'
                },
                contactModes: [
                    {
                        title: 'Vous avez un code ? Inscrivez-vous',
                        description: 'Avec votre code d\'invitation, inscrivez-vous directement.',
                        tags: ['Inscription', 'Démarrage']
                    },
                    {
                        title: 'Pas de code ? Faites une demande',
                        description: 'Soumettez vos informations pour entrer dans le processus d\'obtention.',
                        tags: ['Code invitation', 'Demande']
                    },
                    {
                        title: 'Conçu pour les traders long terme',
                        description: 'Idéal pour les traders individuels souhaitant établir un flux de travail stable.',
                        tags: ['Individuel', 'Long terme']
                    },
                ],
                applicationSteps: [
                    {
                        title: 'Soumettre vos informations',
                        description: 'Laissez vos coordonnées et votre intention d\'utilisation.',
                        detail: 'Précisez votre contexte de trading et vos centres d\'intérêt.'
                    },
                    {
                        title: 'Confirmation des besoins',
                        description: 'Nous confirmons votre direction et vos besoins avant d\'émettre un code.',
                        detail: 'Confirmation de la compréhension du risque et des attentes.'
                    },
                    {
                        title: 'Recevoir le code d\'invitation',
                        description: 'Vous recevez votre code et entrez dans le processus d\'inscription.',
                        detail: 'Code déjà en votre possession ? Inscrivez-vous directement.'
                    },
                    {
                        title: 'Inscription et découverte',
                        description: 'Une fois inscrit, accédez au système.',
                        detail: 'Connectez-vous pour explorer le POC et les fonctionnalités produit.'
                    }
                ],
                faqIntro: {
                    eyebrow: 'Complément',
                    title: 'D\'abord, clarifier les questions fréquentes',
                    description: 'Comprenez le mode d\'inscription et la logique des codes d\'invitation.'
                },
                faqItems: [
                    {
                        question: 'Pourquoi un système d\'invitation ?',
                        answer: 'Le site public utilise un accès sur invitation pour contrôler le rythme de déploiement et maintenir une qualité de retour élevée.'
                    },
                    {
                        question: 'Puis-je commencer sans code d\'invitation ?',
                        answer: 'Oui. Soumettez vos informations sur la page Contact et nous organiserons la suite selon le calendrier de déploiement.'
                    },
                    {
                        question: 'Que puis-je faire après inscription ?',
                        answer: 'Connectez-vous pour explorer la structure produit, accéder au système et découvrir le flux de trading.'
                    },
                    {
                        question: 'Quelles langues sont supportées ?',
                        answer: 'Cette version supporte le chinois simplifié, le chinois traditionnel, l\'anglais, le coréen, le japonais et le français.'
                    }
                ]
            }
        },
        'zh-TW': {
            primaryCta: {label: '立即註冊', path: '/register'},
            secondaryCta: {label: '立即開通', path: '/membership'},
            waitlistCta: {label: '獲取邀請碼', path: '/site/contact'},
            tertiaryCta: {label: '登入系統', path: '/login'},
            siteNavItems: [
                {label: '首頁', path: '/'},
                {label: '輔助交易', path: '/site/assist-trading'},
                {label: '自動交易', path: '/site/auto-trading'},
                {label: '資產配置', path: '/site/asset-allocation'},
                {
                    label: '資源',
                    children: [{label: '工作機制', path: '/site/how-it-works'}, {
                        label: '風控安全',
                        path: '/site/risk-control'
                    }]
                },
                {
                    label: 'POC',
                    children: [
                        {label: '輔助交易', path: '/site/poc/assist-trading'},
                        {label: '自動交易', path: '/site/poc/auto-trading'},
                        {label: '形態識別', path: '/site/poc/pattern'}
                    ]
                },
                {label: '定價', path: '/site/membership'},
                {label: '聯繫', path: '/site/contact'},
            ],
            footerNavItems: [
                {label: '首頁', path: '/'},
                {label: '輔助交易', path: '/site/assist-trading'},
                {label: '自動交易', path: '/site/auto-trading'},
                {label: '資產配置', path: '/site/asset-allocation'},
                {label: '工作機制', path: '/site/how-it-works'},
                {label: '風控安全', path: '/site/risk-control'},
                {label: 'POC 總覽', path: '/site/poc'},
                {label: '輔助交易', path: '/site/poc/assist-trading'},
                {label: '自動交易', path: '/site/poc/auto-trading'},
                {label: '形態識別', path: '/site/poc/pattern'},
                {label: '定價', path: '/site/membership'},
                {label: '聯繫', path: '/site/contact'}
            ],
            heroMetrics: [
                {title: '市場機會', value: '洞察', note: 'AI 輔助識別關鍵訊號與結構變化', trend: 'up'},
                {title: '資產增值', value: '匹配', note: '策略與目標對齊，持續追蹤執行', trend: 'stable'},
                {title: '資產安全', value: '守護', note: '合規與風控並重，投資全程可控', trend: 'focus'}
            ],
            home: {
                hero: {
                    eyebrow: '行稳致远',
                    title: '建造您自己的黑石',
                    description: '與您共同進步的AI資產管家。用您的規則、節奏和邊界，運轉可持續的交易與資管體系。',
                    tags: ['輔助交易', '自動交易', '資產配置', '風控前置'],
                    panelLabel: '資產管理本應如此簡單',
                    panelTitle: 'AI負責苦髒累，您只需專注決斷',
                    panelDescription: '組合構建、風險分配、策略執行——過去機構級的資管能力，現在 AI 為你系統化落地。',
                    assistText: '用更清晰的系統，幫助交易者建立長期一致的決策與執行能力。',
                    topCardLabel: '願景',
                    topCardTitle: '長期一致',
                    topCardText: '幫助交易者建立穩定、可回顧、可持續的執行方式',
                    bottomCardLabel: '體驗方式',
                    bottomCardTitle: '邀請碼體驗',
                    bottomCardText: '預設簡體中文，可切換繁體中文與英文'
                },
                section: {
                    eyebrow: '產品方向',
                    title: '三大產品方向，一套能力體系',
                    description: '從資訊、執行到配置，呈現 AI 的系統化落地路徑。'
                },
                products: [
                    {
                        key: 'assist-trading',
                        label: '輔助交易',
                        slogan: '洞察快，決策才能快',
                        description: '行情瞬息萬變，輔助交易幫您極速洞察，穩穩地把握住每一次機會。',
                        tags: [],
                        bullets: [],
                        metrics: [],
                        ctaLabel: '',
                        ctaTo: '/site/assist-trading'
                    },
                    {
                        key: 'auto-trading',
                        label: '自動交易',
                        slogan: '您所願，AI所往',
                        description: '讓交易像設定投資偏好一樣簡單。設定您的風險底線，AI 在邊界內自主判斷決策、持續交易。',
                        tags: [],
                        bullets: [],
                        metrics: [],
                        ctaLabel: '',
                        ctaTo: '/site/auto-trading'
                    },
                    {
                        key: 'asset-allocation',
                        label: '資產配置',
                        slogan: '忽略細節，關注您的目標',
                        description: 'AI 資產管理：您只需要關心想要什麼結果，怎麼配比、何時調倉，系統自動完成。',
                        tags: ['目標', '配置', 'AI管理'],
                        bullets: ['結果導向 — 關心目標而非過程', '主動管理 — 偏離自動校正', '持續優化 — 市場變化即調整'],
                        metrics: [
                            {title: '視角', value: '組合', note: '全局掌控組合結構', trend: 'stable'},
                            {title: '目標', value: '時間', note: '長期目標持續追蹤', trend: 'up'},
                            {title: '風險管控', value: '對沖', note: '風險敞口自動管理', trend: 'focus'},
                            {title: '全鏈路追溯', value: '透明', note: 'AI 決策過程可查', trend: 'stable'}
                        ],
                        ctaLabel: '',
                        ctaTo: '/site/asset-allocation'
                    }
                ],
                proofItems: [
                    {title: '品牌導向的產品表達', description: '先講價值與方向，再講功能細節。', tags: ['品牌', '官網']},
                    {
                        title: '系統化的能力組織',
                        description: '把輔助交易、自動交易與資產配置收斂為同一體系。',
                        tags: ['體系', '組織']
                    },
                    {title: '風險先於動作', description: '把邊界與限制明確放在執行之前。', tags: ['風控', '邊界']}
                ],
                exploreTitle: {
                    eyebrow: '繼續了解',
                    title: '先看機制，再看 POC',
                    description: '資源講清楚系統如何工作，POC 展示產品如何落地。'
                },
                exploreCards: [
                    {
                        eyebrow: '資源',
                        title: '工作機制與風控安全',
                        description: '了解系統如何組織輸入、判斷、執行與邊界控制。',
                        actionLabel: '查看資源',
                        path: '/site/how-it-works'
                    },
                    {
                        eyebrow: 'POC',
                        title: '輔助交易與自動交易演示',
                        description: '透過 POC 結構，直觀看到兩個核心方向的價值與流程。',
                        actionLabel: '進入 POC',
                        path: '/site/poc'
                    }
                ],
                ctaEyebrow: '搶先體驗',
                ctaTitle: '準備好體驗 D-Sense 了嗎？',
                ctaDescription: '為您的羅馬奠基。註冊即可開啟完整交易體驗。'
            },
            productPage: {
                hero: {
                    eyebrow: '產品功能',
                    title: '三大能力，覆蓋從判斷到配置的關鍵路徑',
                    description: '不是把更多功能堆給你，而是把真正影響交易結果的能力組織成可用系統。',
                    tags: ['輔助交易', '自動交易', '資產配置', '統一工作台'],
                    panelLabel: '產品矩陣',
                    panelTitle: '一套產品，支撐三種關鍵能力',
                    panelDescription: '在同一系統內完成觀察、判斷、執行與資產視圖。',
                    assistText: '產品頁負責把首頁三大方向完整展開。',
                    topCardLabel: '價值',
                    topCardTitle: '少切換，少斷層',
                    topCardText: '讓判斷與執行盡量留在同一套工作流中',
                    bottomCardLabel: '體驗',
                    bottomCardTitle: '品牌化呈現',
                    bottomCardText: '官網敘事圍繞方向、場景與價值組織'
                },
                directions: [
                    {
                        eyebrow: '輔助交易',
                        title: '盤前打開工作台，AI 已經標好了關鍵訊號',
                        description: '每天打開工作台，系統已經把市場結構變化、關鍵價格區間和 AI 判斷整理好。你不用從零開始翻圖，而是直接從訊號看起，更快形成自己的判斷。',
                        bullets: ['打開工作台就能看到聚合後的關鍵訊號', 'AI 已經幫你標註了結構和風險點', '判斷效率更高，盯盤壓力更小'],
                        metrics: [{
                            title: '適用場景',
                            value: '盤前/盤中',
                            note: '輔助觀察與形成判斷',
                            trend: 'stable'
                        }, {title: '輸出方式', value: '結構化', note: '建議、標籤、風險點可讀', trend: 'up'}],
                        featuresTitle: {
                            eyebrow: '核心能力',
                            title: '四個步驟，從洞察到交易',
                            description: '從數據聚合、訊號聚合、邊界判斷到快捷交易的完整能力鏈路。'
                        },
                        flowSteps: [
                            {
                                title: '數據聚合',
                                description: '整合行情、訂單簿與鏈上數據，形成結構化輸入',
                                tags: ['行情', '訂單簿', '鏈上']
                            },
                            {
                                title: '訊號聚合',
                                description: '多維度訊號交叉驗證，剔除噪音突出關鍵資訊',
                                tags: ['訊號', '交叉驗證', '降噪']
                            },
                            {
                                title: '邊界判斷',
                                description: '基於風控規則與帳戶狀態判斷當前可操作邊界',
                                tags: ['風控', '帳戶狀態', '邊界']
                            },
                            {
                                title: '快捷交易',
                                description: '一鍵執行預定義策略，無需頻繁切換頁面操作',
                                tags: ['一鍵執行', '策略', '效率']
                            }
                        ],
                        usageTitle: {
                            eyebrow: '使用流程',
                            title: '三步進入狀態',
                            description: '打開工作台到開始交易，只需三個步驟。'
                        },
                        usageSteps: [
                            {
                                title: '打開工作台',
                                description: '進入工作台，行情數據，技術指標已就緒',
                                detail: '無需手動翻圖查表'
                            },
                            {
                                title: '查看/選擇 品種',
                                description: '快速瀏覽各品種信號與關鍵位',
                                detail: '聚焦有行情的品種'
                            },
                            {title: '一鍵開倉', description: '確認邊界後直接執行開倉操作', detail: '減少操作步驟'}
                        ]
                    },
                    {
                        eyebrow: '人人可得，人人可享',
                        cardEyebrow: '金融科技，普惠個人',
                        title: 'AI 自動交易，從零開始',
                        cardTitle: '零基礎AI自動交易',
                        description: '只要設定風險偏好，AI 就能持續為你自動交易。',
                        cardDescription: '無需寫策略，無需盯盤。只要設定您的風險承受預期，AI 自動匹配適合的資產，持續迭代交易。讓您的精力從市場中釋放出來。',
                        bullets: ['零門檻 - 不用寫策略，不懂交易也能用', '設風險 - 告訴 AI 你的風險偏好，其餘交給它', '全自動 - 7×24 持續運行，徹底放手'],
                        metrics: [{
                            title: '操作門檻',
                            value: '零基礎',
                            note: '只需設定風險承受能力',
                            trend: 'up'
                        }, {title: '運行模式', value: '全自動', note: 'AI 持續匹配資產並執行交易', trend: 'focus'}],
                        featuresTitle: {
                            eyebrow: '核心能力',
                            title: '六步閉環，AI 自主迭代交易',
                            description: '從數據聚合到狀態回流，AI 在邊界內自主判斷、持續迭代交易。'
                        },
                        flowSteps: [
                            {
                                title: '數據聚合',
                                description: '聚合多源市場數據，形成統一的輸入基礎',
                                tags: ['多源', '聚合', '輸入']
                            },
                            {
                                title: '市場感知',
                                description: 'AI 持續掃描市場狀態與結構變化，識別交易機會',
                                tags: ['感知', '結構', '掃描']
                            },
                            {
                                title: '交易規劃',
                                description: '綜合市場結構與風險邊界，自主形成交易判斷',
                                tags: ['分析', '判斷', '決策']
                            },
                            {
                                title: '風控校驗',
                                description: '在用戶設定的風險邊界內校驗是否允許執行',
                                tags: ['邊界', '校驗', '准入']
                            },
                            {
                                title: '自動執行',
                                description: 'AI 在邊界內自主執行交易操作',
                                tags: ['下單', '執行', '追蹤']
                            },
                            {
                                title: '狀態回流',
                                description: '執行結果與狀態持續回流，進入下一輪迭代',
                                tags: ['反饋', '狀態', '交替']
                            }
                        ]
                    },
                    {
                        eyebrow: '資產配置',
                        title: 'AI 自動管理您的整套資產配置',
                        description: '設定您的目標和風險偏好，從組合構建到動態再平衡，AI 全權負責。您只需要關注結果。',
                        bullets: ['結果導向 — 關心目標而非過程', '主動管理 — 偏離自動校正', '持續優化 — 市場變化即調整'],
                        metrics: [{
                            title: '視角',
                            value: '組合',
                            note: '全局掌控組合結構',
                            trend: 'stable'
                        }, {title: '目標', value: '時間', note: '長期目標持續追蹤', trend: 'up'}, {
                            title: '風險管控',
                            value: '對沖',
                            note: '風險敞口自動管理',
                            trend: 'focus'
                        }, {title: '全鏈路追溯', value: '透明', note: 'AI 決策過程可查', trend: 'stable'}],
                        featuresTitle: {
                            eyebrow: '核心能力',
                            title: '六個步驟，AI 全程管理你的配置',
                            description: '從目標分解到動態調倉，AI 在邊界內全程管理你的資產配置策略。'
                        },
                        flowSteps: [
                            {
                                title: '目標分解',
                                description: '將長期投資目標拆解為可執行的階段性任務，明確收益預期與風險容忍度',
                                tags: ['分解', '目標', '階段']
                            },
                            {
                                title: '資產配置規劃',
                                description: '基於投資目標與市場環境，規劃大類資產配置比例與投資策略',
                                tags: ['規劃', '配比', '策略']
                            },
                            {
                                title: '目標資產比選',
                                description: '在各資產類別中篩選優質標的，透過多維度比選確定最佳組合候選',
                                tags: ['篩選', '比選', '標的']
                            },
                            {
                                title: '組合構建與交易執行',
                                description: '按既定配比執行建倉操作，完成組合構建並持續追蹤建倉進度',
                                tags: ['構建', '執行', '建倉']
                            },
                            {
                                title: '風險監控與合規審查',
                                description: '即時監控組合風險敞口與合規指標，確保投資始終在預設邊界內運行',
                                tags: ['監控', '風控', '合規']
                            },
                            {
                                title: '資產價值追蹤與調倉',
                                description: '持續追蹤資產價值變化，偏離目標閾值時自動觸發調倉，讓組合回歸目標軌道',
                                tags: ['追蹤', '調倉', '再平衡']
                            }
                        ],
                        usageTitle: {
                            eyebrow: '使用流程',
                            title: '三步開啟 AI 資產管理',
                            description: '從設定目標到長期運行，只需三步。'
                        },
                        usageSteps: [
                            {
                                title: '設定投資目標',
                                description: '告訴 AI 你的風險偏好和收益預期',
                                detail: '一次設定，持續追蹤'
                            },
                            {
                                title: 'AI 自動運行',
                                description: '組合構建、動態監控、再平衡全部由 AI 自動完成',
                                detail: '無需手動干預'
                            },
                            {
                                title: '隨時查看狀態',
                                description: '打開面板即可查看組合表現和 AI 的調整記錄',
                                detail: '過程透明可追溯'
                            }
                        ]
                    }
                ]
            },
            resources: {
                proofBarItems: ['市場輸入', '結構化判斷', '風控校驗', '執行回流'],
                howItWorksHero: {
                    eyebrow: '工作機制',
                    title: '交易鏈路，透明可控',
                    description: 'D-Sense 不是黑盒訊號工具，而是把輸入、判斷、風控與執行組織成可解釋流程。',
                    panelLabel: '流程閉環',
                    panelTitle: '從輸入到執行循環',
                    panelDescription: '每一步都有明確職責，幫助交易者理解動作為何發生。'
                },
                howItWorksSteps: [
                    {
                        title: '市場輸入',
                        description: '彙總行情、結構與上下文，形成可用於判斷的輸入。',
                        detail: '讓市場觀察不再分散於多個頁面。'
                    },
                    {
                        title: '策略判斷',
                        description: 'AI 與規則共同形成結構化建議。',
                        detail: '結論、標籤與理由可一起查看。'
                    },
                    {
                        title: '風控校驗',
                        description: '風險邊界先判斷，再決定是否允許動作。',
                        detail: '不滿足條件時不進入執行。'
                    },
                    {
                        title: '執行回流',
                        description: '執行結果與狀態會回到統一視圖。',
                        detail: '幫助你持續追蹤策略與結果。'
                    }
                ],
                howItWorksFlowSteps: [
                    {
                        title: '數據聚合',
                        description: '整合多源行情、訂單簿與鏈上數據，形成結構化的統一輸入基礎',
                        tags: ['行情', '鏈上', '多源']
                    },
                    {
                        title: '市場感知',
                        description: 'AI 持續掃描市場狀態與結構變化，識別關鍵訊號與交易機會',
                        tags: ['感知', '掃描', '識別']
                    },
                    {
                        title: '交易規劃',
                        description: '綜合市場結構與風險邊界，自主形成交易判斷與策略規劃',
                        tags: ['分析', '判斷', '規劃']
                    },
                    {
                        title: '風控校驗',
                        description: '在用戶設定的風險邊界內逐項校驗，確保每筆動作合規且安全',
                        tags: ['邊界', '校驗', '准入']
                    },
                    {
                        title: '自動執行',
                        description: 'AI 在邊界內自主執行交易操作，持續追蹤訂單執行狀態',
                        tags: ['下單', '執行', '追蹤']
                    },
                    {
                        title: '狀態回流',
                        description: '執行結果與市場狀態持續回流至系統，進入下一輪分析疊代',
                        tags: ['反饋', '疊代', '閉環']
                    }
                ],
                howItWorksAiDecision: [
                    {
                        eyebrow: 'AI 感知',
                        title: 'AI 如何理解市場狀態',
                        description: '系統將多源市場數據轉化為 AI 可理解的結構化輸入，透過大語言模型識別市場結構、趨勢變化與異常訊號，生成可讀的分析結論。',
                        bullets: [
                            '整合 K 線、訂單簿、鏈上等多維數據來源',
                            'AI 識別關鍵價格區間與市場結構變化',
                            '訊號聚合去噪，突出高置信度的交易機會',
                            '以自然語言輸出結構化的分析結論'
                        ],
                        metrics: [
                            {title: '數據維度', value: '50+', note: '技術指標與市場訊號', trend: 'up'},
                            {title: '分析頻率', value: '即時', note: '持續掃描市場狀態', trend: 'focus'}
                        ]
                    },
                    {
                        eyebrow: 'AI 決策',
                        title: '從分析到執行，AI 如何做交易決策',
                        description: '系統構建包含市場狀態、風險邊界和歷史數據的上下文，調用大語言模型生成結構化的交易建議，再由解析引擎轉換為可執行的交易動作。',
                        bullets: [
                            '動態構建包含多維度上下文的決策 Prompt',
                            '大模型輸出結構化的交易決策（方向/倉位/時機）',
                            '解析引擎將模型輸出轉為可執行的交易指令',
                            '風控校驗通過後才提交至交易所執行'
                        ],
                        metrics: [
                            {title: '決策時延', value: '<30s', note: '從分析到生成交易建議', trend: 'up'},
                            {title: '可追溯性', value: '全鏈路', note: 'Prompt→輸出→動作均可回溯', trend: 'focus'}
                        ]
                    }
                ],
                howItWorksBlocks: [
                    {
                        title: '輸入層 — 多源數據聚合',
                        description: '整合交易所行情、鏈上數據、技術指標與市場輿情，構建統一的數據輸入管道，經過清洗與標準化後供上層使用。',
                        tags: ['行情 API', '鏈上數據', '技術指標', '數據清洗'],
                        iconName: 'database'
                    },
                    {
                        title: '判斷層 — AI 智能分析引擎',
                        description: '融合大語言模型與規則引擎，對市場狀態進行多維度分析。動態構建 Prompt 並調用模型，輸出結構化訊號與交易建議。',
                        tags: ['LLM', 'Prompt 工程', '規則引擎', '訊號聚合'],
                        iconName: 'brain'
                    },
                    {
                        title: '執行層 — 風控與交易鏈路',
                        description: '將風控校驗嵌入執行鏈路前端，確保每筆交易都在預設邊界內執行。結果即時回流，形成完整的交易閉環。',
                        tags: ['風控校驗', '訂單執行', '狀態同步', '異常處理'],
                        iconName: 'shield'
                    }
                ],
                howItWorksSections: {
                    architecture: {
                        eyebrow: '系統架構',
                        title: '從數據到執行的六步閉環',
                        description: '每一個環節都有明確職責，AI 在邊界內自主運行、持續疊代。'
                    },
                    aiDecision: {
                        eyebrow: 'AI 智能',
                        title: '市場感知與交易決策機制',
                        description: '系統如何理解市場訊號，又如何將這些訊號轉化為可執行的交易決策。'
                    },
                    layers: {
                        eyebrow: '技術架構',
                        title: '三層架構，各司其職',
                        description: '系統由三個核心層級構成，每一層都承擔明確的技術職責。'
                    },
                    demo: {
                        eyebrow: '即時演示',
                        title: '看看 AI 如何感知市場',
                        description: '模擬 AI 分析面板，展示系統如何識別與標註市場訊號。'
                    }
                },
                howItWorksDemo: {
                    symbol: 'BTC/USDT',
                    symbolChange: '+2.34%',
                    symbolChangeUp: true,
                    price: '67,432.50',
                    priceChange: '+1,542.80',
                    priceChangeUp: true,
                    signalTags: [
                        {label: '結構突破', type: 'signal'},
                        {label: '量能放大', type: 'signal'},
                        {label: '波動率偏高', type: 'risk'},
                        {label: 'AI 偏多', type: 'info'}
                    ],
                    aiJudgmentTitle: 'AI 判斷',
                    aiJudgmentValue: '偏多 · 中等信心',
                    aiJudgmentNote: '價格突破近期阻力，量能配合',
                    keyLevelTitle: '關鍵位',
                    keyLevelValue: '67,200 / 68,500',
                    keyLevelNote: '上方阻力 68,500，下方支撐 67,200'
                },
                howItWorksMetrics: [
                    {title: '處理步驟', value: '6步', note: '從數據聚合到狀態回流的完整閉環', trend: 'focus'},
                    {title: '決策時延', value: '<30s', note: '從分析到生成交易建議', trend: 'up'},
                    {title: '數據維度', value: '50+', note: '技術指標與市場訊號', trend: 'up'}
                ],
                howItWorksCta: {
                    eyebrow: '開始使用',
                    title: '理解機制，開始體驗',
                    description: '了解系統如何工作後，通過 POC 驗證是否適合你的交易方式。'
                },
                riskHero: {
                    eyebrow: '風控安全',
                    title: '邊界先行，動作有度',
                    description: '風控不是附屬功能，而是系統是否值得長期使用的前提。',
                    panelLabel: '風險前置',
                    panelTitle: '讓邊界先於動作，讓安全先於效率',
                    panelDescription: '當風險不清晰時，系統應先收縮動作，而不是放大風險。'
                },
                riskGuardItems: [
                    {
                        title: '風險模式先行',
                        description: '先定義偏好和邊界，再決定是否允許執行。',
                        bullets: ['保守/謹慎/穩健/積極/激進 五檔可選', '閾值前置，動作前自動校驗', '不符合條件不進入執行']
                    },
                    {
                        title: '止盈止損與倉位控制',
                        description: '把回撤與暴露控制在可接受範圍內。',
                        bullets: ['倉位上限自動約束', '止盈止損聯動觸發', '避免無邊界加倉']
                    },
                    {
                        title: '權限與密鑰隔離',
                        description: '用最小權限原則處理敏感操作。',
                        bullets: ['API 權限分層管理', '密鑰加密隔離存儲', '異常優先回退']
                    }
                ],
                riskMatrixItems: [
                    {
                        title: '執行前校驗',
                        description: '每筆動作發出前，系統先校驗風險條件是否滿足。',
                        tags: ['前置校驗', '條件判斷'],
                        iconName: 'shield'
                    },
                    {
                        title: '權限邊界',
                        description: '接口與帳戶能力被約束在必要範圍內，防止越權操作。',
                        tags: ['權限管控', '安全隔離'],
                        iconName: 'layers'
                    },
                    {
                        title: '持續回流',
                        description: '執行結果與風險狀態持續回到統一視圖，形成監控閉環。',
                        tags: ['狀態回流', '持續追蹤'],
                        iconName: 'waypoints'
                    }
                ],
                riskSections: {
                    guard: {
                        eyebrow: '風控規則',
                        title: '三道防線，層層把關',
                        description: '從風險偏好到倉位管理再到權限控制，逐層收緊安全邊界。'
                    },
                    matrix: {
                        eyebrow: '執行保障',
                        title: '每一步都在規則內運行',
                        description: '系統在執行鏈路的每個節點嵌入校驗邏輯，確保動作始終在預設邊界內。'
                    }
                },
                riskCta: {
                    eyebrow: '開始使用',
                    title: '讓風控走在交易前面',
                    description: '了解風險邊界如何保護你的每一筆交易。'
                },
                riskMetrics: [
                    {title: '風控層級', value: '3道', note: '模式→倉位→權限逐層收緊', trend: 'focus'},
                    {title: '校驗時機', value: '執行前', note: '動作發出前自動校驗', trend: 'up'},
                    {title: '權限粒度', value: '分層', note: 'API 權限最小化管理', trend: 'stable'}
                ]
            },
            pocPage: {
                hero: {
                    eyebrow: 'POC',
                    title: '透過 POC，更直觀看見產品如何工作',
                    description: '這裡不講空泛概念，而是把輔助交易與自動交易的典型場景、價值與互動結構展示給你。',
                    tags: ['輔助交易', '自動交易', '場景展示', '功能演示'],
                    panelLabel: 'POC 入口',
                    panelTitle: '兩個方向，兩種使用體驗',
                    panelDescription: '分別查看輔助交易與自動交易如何承接不同使用目標。',
                    assistText: '先看 POC，再判斷是否適合你的交易方式。',
                    topCardLabel: '方向一',
                    topCardTitle: '輔助交易',
                    topCardText: '看清市場、形成判斷、準備動作',
                    bottomCardLabel: '方向二',
                    bottomCardTitle: '自動交易',
                    bottomCardText: '讓規則持續執行，讓結果持續回流'
                },
                entries: [
                    {
                        title: '輔助交易 POC',
                        slogan: '更快看見，更穩判斷',
                        description: '查看從市場觀察、訊號理解到交易輔助建議的完整演示。',
                        tags: ['市場觀察', 'AI 解釋', '風險提醒'],
                        path: '/site/poc/assist-trading'
                    },
                    {
                        title: '自動交易 POC',
                        slogan: '規則先行，執行不停',
                        description: '查看策略如何在邊界內持續執行，並同步結果與狀態。',
                        tags: ['規則執行', '風控前置', '結果回流'],
                        path: '/site/poc/auto-trading'
                    },
                    {
                        title: '形態識別 POC',
                        slogan: '每根K線，都被看清',
                        description: '查看K線形態自動識別與AI深度分析的完整演示。',
                        tags: ['K線形態', 'AI分析', '回測驗證'],
                        path: '/site/poc/pattern'
                    }
                ],
                features: [
                    {
                        title: '場景導向展示',
                        description: '不只展示頁面，而是講清楚頁面解決什麼問題。',
                        tags: ['場景', '價值']
                    },
                    {title: '流程導向表達', description: '看到模組之間的先後關係和資訊流。', tags: ['流程', '資訊流']},
                    {title: '邊界導向說明', description: '在演示時同步說明限制條件與風險邊界。', tags: ['邊界', '風控']}
                ],
                faqIntro: {
                    eyebrow: '補充說明',
                    title: '看 POC 之前，先知道這些邊界',
                    description: '透過少量高頻問答，把使用前最重要的資訊說清楚。'
                },
                faqItems: [
                    {
                        question: '這套系統適合誰？',
                        answer: '適合希望減少盯盤、提高判斷效率，並願意理解風險邊界的個人交易者。'
                    },
                    {
                        question: '系統會自動執行交易嗎？',
                        answer: '支援半自動和自動化執行，但是否啟用，應由你依照自己的風險理解決定。'
                    },
                    {
                        question: '為什麼還要看 POC？',
                        answer: 'POC 用來幫助你先理解產品邏輯、適用場景與邊界，再決定是否開始體驗。'
                    },
                    {question: '如何開始使用？', answer: '已有邀請碼可直接註冊，沒有邀請碼可先在聯絡頁提交資訊。'}
                ]
            },
            assistPocPage: {
                hero: {
                    eyebrow: '輔助交易 POC',
                    title: '輔助交易，讓你更快理解機會，而不是盲目追單',
                    description: '它幫助交易者在高波動環境裡更快完成觀察、判斷與動作準備，但最終控制權仍在用戶手中。',
                    tags: ['市場訊號', 'AI 輔助', '風險提醒', '行動建議'],
                    panelLabel: '核心口號',
                    panelTitle: '更快看見，更穩判斷',
                    panelDescription: '先用系統幫助你看清機會，再決定是否行動。',
                    assistText: '輔助交易強調幫助判斷，而不是取代判斷。',
                    topCardLabel: '使用場景',
                    topCardTitle: '盤前 / 盤中',
                    topCardText: '適合高頻觀察與快速形成交易判斷',
                    bottomCardLabel: '結果',
                    bottomCardTitle: '更少猶豫',
                    bottomCardText: '把觀察、理解與準備動作放進同一流程'
                },
                features: [
                    {title: '市場訊號聚合', description: '集中看關鍵標籤與市場變化。', tags: ['行情', '標籤']},
                    {title: 'AI 輔助判斷', description: '快速獲得結構化解釋與建議。', tags: ['AI', '解釋']},
                    {title: '風險前置提醒', description: '在動作前看到重要限制條件。', tags: ['風險', '提醒']},
                    {title: '行動建議輸出', description: '把判斷結果組織成更易行動的形式。', tags: ['建議', '執行前']}
                ],
                flowSteps: [
                    {
                        title: '觀察市場',
                        description: '集中查看關鍵市場變化與上下文。',
                        detail: '幫助你快速完成第一輪過濾。'
                    },
                    {title: '理解訊號', description: 'AI 幫助組織原因、標籤與傾向。', detail: '讓結論更容易被理解。'},
                    {title: '確認邊界', description: '先看風險提醒與執行限制。', detail: '避免只看機會不看代價。'},
                    {title: '準備動作', description: '形成更清晰的下一步動作計畫。', detail: '由你決定是否真的進入執行。'}
                ],
                stories: [
                    {
                        eyebrow: '價值一',
                        title: '幫助你更快看見機會',
                        description: '把分散的觀察過程拉回同一工作台，減少切換成本。',
                        bullets: ['減少分散式看盤', '讓資訊更快收束', '幫助快速進入判斷狀態'],
                        metrics: [{
                            title: '觀察方式',
                            value: '統一',
                            note: '關鍵輸入集中展示',
                            trend: 'stable'
                        }, {title: '反應速度', value: '更快', note: '減少切換與猶豫', trend: 'up'}]
                    },
                    {
                        eyebrow: '價值二',
                        title: '讓判斷更容易解釋',
                        description: '不只是給訊號，還把判斷依據和風險一起表達出來。',
                        bullets: ['訊號與理由一起看', '風險和機會同時表達', '讓判斷更容易回顧'],
                        metrics: [{
                            title: '輸出形式',
                            value: '結構化',
                            note: '標籤、說明、建議聯動',
                            trend: 'up'
                        }, {title: '可回顧性', value: '更強', note: '更容易追溯判斷過程', trend: 'focus'}]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: '核心能力',
                        title: '從訊號到行動的完整鏈路',
                        description: '不靠堆砌功能，而是把輔助交易的關鍵能力組織成可用的系統路徑。'
                    },
                    flow: {
                        eyebrow: '典型流程',
                        title: '每天打開工作檯，四步完成判斷準備',
                        description: '從觀察到決策，系統幫你把分散的步驟收攏為一條可重複的工作流。'
                    },
                    stories: {
                        eyebrow: '價值說明',
                        title: '輔助交易解決的兩個核心問題',
                        description: '資訊過載時如何更快看見機會，判斷複雜時如何更容易解釋。'
                    },
                    demo: {
                        eyebrow: '即時演示',
                        title: '交易工作檯展示',
                        description: '從即時行情、持倉到歷史交易，完整展示交易工作檯的核心數據面板。'
                    },
                    cta: {
                        primaryLabel: '獲取邀請碼',
                        primaryTo: '/site/assist-trading'
                    }
                }
            },
            autoPocPage: {
                hero: {
                    eyebrow: '自動交易 POC',
                    title: '定好目標，AI替你交易',
                    description: '只需設定你的風險承受能力和目標盈利率，AI自動匹配機會、管理倉位、優化資金利用率——不需要編寫策略，不需要盯盤。',
                    tags: ['風險偏好', '目標收益', '資金利用', 'AI自主交易'],
                    panelLabel: '核心理念',
                    panelTitle: '你定目標，AI負責執行',
                    panelDescription: '交易不需要複雜的策略編程。告訴AI你能承受多少風險、想要多少回報，它會自動找到最優路徑。',
                    assistText: '設定好你的風險偏好和收益目標，剩下的交給AI。',
                    topCardLabel: '適用場景',
                    topCardTitle: '簡單直接',
                    topCardText: '不需要懂編程、不需要寫策略，適合所有交易者',
                    bottomCardLabel: '核心優勢',
                    bottomCardTitle: 'AI自主決策',
                    bottomCardText: 'AI即時分析市場，在你設定的邊界內自主尋找最優交易機會'
                },
                features: [
                    {
                        title: '風險偏好設定',
                        description: '設定你的最大可承受虧損比例，AI嚴格在此邊界內運行。',
                        tags: ['風控', '安心']
                    },
                    {
                        title: '目標收益管理',
                        description: '設定預期盈利率，AI自動尋找匹配該目標的市場機會。',
                        tags: ['目標', '收益']
                    },
                    {
                        title: '資金利用優化',
                        description: 'AI智能分配每筆交易的資金比例，最大化資金使用效率。',
                        tags: ['效率', '倉位']
                    },
                    {
                        title: 'AI自主交易執行',
                        description: '7×24持續監控市場，自動完成擇時、開倉、平倉全流程。',
                        tags: ['自動', '全天候']
                    },
                ],
                flowSteps: [
                    {
                        title: '設定風險邊界',
                        description: '確定你能承受的最大回撤和單筆虧損上限。',
                        detail: 'AI的所有決策都在此邊界內執行'
                    },
                    {
                        title: '設定收益目標',
                        description: '設定期望的年化或季度盈利率目標。',
                        detail: 'AI以此為基準匹配合適的交易機會'
                    },
                    {
                        title: 'AI智能匹配',
                        description: 'AI即時分析市場，匹配符合你風險收益偏好的機會。',
                        detail: '結合資金利用率智能分配倉位'
                    },
                    {
                        title: '自動執行與優化',
                        description: 'AI自主完成交易執行，持續根據市場變化調整策略。',
                        detail: '每筆交易可追溯，收益清晰可見'
                    }
                ],
                stories: [
                    {
                        eyebrow: '價值一',
                        title: '毋需策略，只需目標',
                        description: '你不需要編寫任何交易策略。告訴AI你的風險底線和收益期望，它會自動找到最適合的交易路徑。',
                        bullets: ['零編程門檻，設定即運行', 'AI自動尋優匹配機會', '無需盯盤，全天候自主運行'],
                        metrics: [{
                            title: '上手難度',
                            value: '極低',
                            note: '只需設定兩個參數即可開始',
                            trend: 'up'
                        }, {title: '決策方式', value: 'AI驅動', note: 'AI替代人工判斷與執行', trend: 'stable'}]
                    },
                    {
                        eyebrow: '價值二',
                        title: '每一分錢都高效運轉',
                        description: 'AI不僅幫你交易，更幫你管理資金——動態調整倉位比例，讓你的資金利用率始終處於最優狀態。',
                        bullets: ['智能倉位管理，動態分配資金', '即時調整資金利用率', '讓閒置資金也能產生價值'],
                        metrics: [{
                            title: '資金效率',
                            value: '最優',
                            note: 'AI持續優化資金分配比例',
                            trend: 'focus'
                        }, {title: '收益來源', value: '透明', note: '每一筆盈虧可追溯可復盤', trend: 'up'}]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: '核心能力',
                        title: 'AI自主交易的四大能力',
                        description: '不需要編寫策略，只需要設定偏好，AI替你完成一切。'
                    },
                    flow: {
                        eyebrow: '使用流程',
                        title: '四步設定，AI即刻運行',
                        description: '從設定偏好的那一刻起，AI就開始為你工作。'
                    },
                    stories: {
                        eyebrow: '核心價值',
                        title: '簡單，但強大',
                        description: '把複雜的交易，變成簡單的選擇題。'
                    },
                    demo: {
                        eyebrow: '互動演示',
                        title: 'AI自動交易演示',
                        description: '配置您的API Key和風險偏好，體驗AI自動交易的完整流程。'
                    }
                }
            },
            patternPocPage: {
                hero: {
                    eyebrow: '形態識別 POC',
                    title: 'AI驅動K線形態識別分析',
                    description: '從檢測到分析，完整展示AI對K線形態的識別、回測與交易機會評估能力。',
                    tags: ['K線形態', 'AI分析', '回測', '交易訊號'],
                    panelLabel: '核心能力',
                    panelTitle: '讓每一根K線的形態都被看見',
                    panelDescription: '基於上百種K線形態檢測演算法，結合AI深層分析，輔助您發現潛在交易機會。',
                    assistText: '不需要手動翻圖，系統自動標註形態並提供AI分析。',
                    topCardLabel: '形態檢測',
                    topCardTitle: '自動識別',
                    topCardText: '系統自動檢測K線形態，支援1m到1D多種週期',
                    bottomCardLabel: 'AI分析',
                    bottomCardTitle: '深度解讀',
                    bottomCardText: 'AI對形態進行綜合分析，給出入場、止損、目標位建議'
                },
                features: [
                    {
                        title: '多形態檢測',
                        description: '支援錘子線、吞沒形態、啟明星等上百種經典K線形態的自動檢測。',
                        tags: ['形態', '覆蓋']
                    },
                    {
                        title: 'AI深度分析',
                        description: '對接大模型對形態進行多維度分析，包括入場價格、目標價位和止損建議。',
                        tags: ['AI', '分析']
                    },
                    {
                        title: '歷史回測',
                        description: '對每種形態進行歷史回測，統計勝率、盈虧比等關鍵指標，輔助決策。',
                        tags: ['回測', '數據']
                    },
                    {
                        title: '區域識別',
                        description: '自動識別支撐阻力區、盤整箱體等區域形態，輔助判斷關鍵價格區間。',
                        tags: ['區域', '支撐阻力']
                    },
                ],
                flowSteps: [
                    {
                        title: '選擇品種',
                        description: '選擇目標交易對，系統自動獲取K線數據。',
                        detail: '支援永續合約和現貨，覆蓋BTC、ETH等主流幣種'
                    },
                    {
                        title: '形態檢測',
                        description: '系統自動掃描K線，識別並標記所有形態訊號。',
                        detail: '支援1m到1D多種週期，即時更新檢測結果'
                    },
                    {
                        title: 'AI分析',
                        description: '對識別到的形態進行AI輔助分析，提供交易參考。',
                        detail: '給出入場、止損、目標位和風險等級建議'
                    },
                    {
                        title: '回測驗證',
                        description: '檢視形態的歷史表現數據，輔助判斷訊號可靠性。',
                        detail: '統計勝率、盈虧比，檢視歷史盈虧記錄'
                    }
                ],
                stories: [
                    {
                        eyebrow: '價值一',
                        title: '讓形態識別更全面',
                        description: '上百種形態自動檢測，從簡單的錘子線到複雜的啟明星，系統幫你發現可能遺漏的訊號。',
                        bullets: ['自動掃描，無需手動翻圖', '覆蓋上百種經典形態', '支援多週期檢測'],
                        metrics: [
                            {title: '檢測效率', value: '自動', note: '即時監測，減少人工成本', trend: 'up'},
                            {title: '覆蓋範圍', value: '全面', note: '數百種形態自動識別', trend: 'focus'}
                        ]
                    },
                    {
                        eyebrow: '價值二',
                        title: '讓分析更有深度',
                        description: 'AI不僅告訴你形態類型，還分析形態的可信度、潛在盈虧比，並給出具體的交易建議。',
                        bullets: ['AI多維度分析形態訊號', '給出入場、止損、目標價位', '風險評估輔助決策'],
                        metrics: [
                            {title: '分析深度', value: '全面', note: '多維度綜合分析', trend: 'up'},
                            {title: '參考價值', value: '實用', note: '具體價格建議和風險提示', trend: 'focus'}
                        ]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: '核心能力',
                        title: '從檢測到分析，完整形態識別鏈路',
                        description: '不只是檢測形態，而是把識別、分析和歷史驗證組織為完整的工具鏈條。'
                    },
                    flow: {
                        eyebrow: '典型流程',
                        title: '四步完成形態識別與分析',
                        description: '從選擇品種到回測驗證，系統幫你完成形態識別的完整工作流。'
                    },
                    stories: {
                        eyebrow: '價值說明',
                        title: '形態識別解決的三個核心問題',
                        description: '手動翻圖效率低、訊號遺漏、分析不深入——形態識別幫你掃除這些障礙。'
                    },
                    demo: {
                        eyebrow: '互動演示',
                        title: 'K線形態識別演示',
                        description: '透過K線形態識別，快速發現市場結構變化與交易機會。'
                    },
                    cta: {
                        primaryLabel: '獲取邀請碼',
                    }
                }
            },
            membershipPage: {
                hero: {
                    eyebrow: '會員體系',
                    title: '為不同階段的投資者，匹配恰好的能力',
                    description: '從免費體驗到專家級全自動交易，四檔會員覆蓋從洞察、執行到配置的完整路徑。',
                    panelLabel: '線上開通',
                    panelTitle: '線上開通，即刻生效',
                    panelDescription: '線上選擇等級並完成支付後立即生效，可隨時在後台查看訂閱狀態與到期時間。'
                },
                sectionTitles: {
                    compare: {
                        eyebrow: '等級對比',
                        title: '四檔會員，按需選擇',
                        description: '定價與功能矩陣一覽，DCA 計畫上限隨等級提升。'
                    },
                    features: {
                        eyebrow: '付費功能',
                        title: '每項能力，解決一個具體問題',
                        description: 'DCA 助手 / 對沖 / 形態識別 / 自動交易，按等級解鎖。'
                    },
                    cta: {
                        eyebrow: '開通引導',
                        title: '準備好升級了嗎？',
                        description: '線上選擇等級並支付，開通即刻生效，馬上解鎖對應能力。'
                    }
                },
                priceLabels: {monthly: '月付', yearly: '年付'},
                tiers: [
                    {
                        name: '免費', tagline: '體驗核心功能', monthlyPrice: '0', yearlyPriceMonthly: '0',
                        features: [
                            {label: '訂閱/觀看報告', enabled: true},
                            {label: 'DCA 助手', enabled: false},
                            {label: '對沖交易', enabled: false},
                            {label: '形態識別快捷交易', enabled: false},
                            {label: '自動交易', enabled: false}
                        ]
                    },
                    {
                        name: '初級', tagline: '計畫式自動買入', monthlyPrice: '5.9U', yearlyPriceMonthly: '3.9U/月',
                        features: [
                            {label: '訂閱/觀看報告', enabled: true},
                            {label: 'DCA 助手', enabled: true, note: '≤3 計畫'},
                            {label: '對沖交易', enabled: false},
                            {label: '形態識別快捷交易', enabled: false},
                            {label: '自動交易', enabled: false}
                        ]
                    },
                    {
                        name: '專業', tagline: '交易與形態進階', monthlyPrice: '15.9U', yearlyPriceMonthly: '9.9U/月',
                        highlight: true,
                        features: [
                            {label: '訂閱/觀看報告', enabled: true},
                            {label: 'DCA 助手', enabled: true, note: '≤10 計畫'},
                            {label: '對沖交易', enabled: true},
                            {label: '形態識別快捷交易', enabled: true},
                            {label: '自動交易', enabled: false}
                        ]
                    },
                    {
                        name: '專家', tagline: 'AI 全自動交易', monthlyPrice: '59.9U', yearlyPriceMonthly: '29.9U/月',
                        features: [
                            {label: '訂閱/觀看報告', enabled: true},
                            {label: 'DCA 助手', enabled: true, note: '≤15 計畫'},
                            {label: '對沖交易', enabled: true},
                            {label: '形態識別快捷交易', enabled: true},
                            {label: '自動交易', enabled: true}
                        ]
                    }
                ],
                featureIntros: [
                    {
                        title: 'DCA 助手',
                        description: '依品種與指標條件建立計畫，系統監控已收盤 K 線，邊緣觸發自動市價買入現貨。單次或循環，暫停恢復自由控制。',
                        tags: ['計畫式', '條件觸發', '全自動買入']
                    },
                    {
                        title: '對沖交易',
                        description: '單腿/跨品種對沖，保護腿自動重掛，止盈止損與敞口管理一體化。',
                        tags: ['單腿對沖', '保護腿', '敞口管理']
                    },
                    {
                        title: '形態識別快捷交易',
                        description: 'AI 識別關鍵形態，一鍵快捷下單，讓判斷更快落地。',
                        tags: ['形態識別', '快捷下單']
                    },
                    {
                        title: '自動交易',
                        description: '設定風險底線，AI 在邊界內自主判斷決策、持續交易。',
                        tags: ['AI 決策', '邊界內自主', '持續交易']
                    }
                ],
                faqIntro: {
                    eyebrow: 'FAQ',
                    title: '常見問題',
                    description: '關於會員與開通的常見疑問。'
                },
                faqItems: [
                    {
                        question: '如何開通會員？',
                        answer: '在價格頁選擇目標等級並完成支付，會員即刻生效；到期前系統會提醒續費，可隨時在會員中心查看訂閱狀態。'
                    },
                    {
                        question: '會員到期後會怎樣？',
                        answer: '到期自動降級至下一有效訂閱等級（無則免費）；超出新等級上限的 DCA 計畫會自動暫停並站內通知，續費升級後手動恢復。'
                    },
                    {
                        question: 'DCA 計畫上限如何計算？',
                        answer: '按等級映射：初級 3 個、專業 10 個、專家 15 個。僅統計未刪除的計畫，暫停與運行中的均計入。'
                    },
                    {
                        question: '年付有什麼優惠？',
                        answer: '年付按折算月價計費：初級 3.9U/月、專業 9.9U/月、專家 29.9U/月，比月付更划算。'
                    }
                ]
            },
            contactPage: {
                hero: {
                    eyebrow: '開始使用',
                    title: '獲取邀請碼，開始體驗 D-Sense',
                    description: '如果你還沒有邀請碼，可以先提交資訊，我們會根據需求與階段安排後續體驗。',
                    tags: ['邀請碼', '申請體驗', '開始使用'],
                    panelLabel: '開始方式',
                    panelTitle: '有邀請碼就註冊，沒有邀請碼就先申請',
                    panelDescription: '把獲取邀請碼與開始體驗組織成清晰路徑。',
                    assistText: '先看清獲取方式，再決定是否提交資訊。',
                    topCardLabel: '開始更快',
                    topCardTitle: '兩種入口',
                    topCardText: '直接註冊，或先獲取邀請碼',
                    bottomCardLabel: '適用方式',
                    bottomCardTitle: '邀請制體驗',
                    bottomCardText: '透過邀請碼控制體驗節奏'
                },
                contactModes: [
                    {title: '已有邀請碼，直接註冊', description: '拿到邀請碼即可直接完成註冊。', tags: ['註冊', '開始']},
                    {
                        title: '沒有邀請碼，先申請',
                        description: '先提交資訊，再進入邀請碼獲取流程。',
                        tags: ['邀請碼', '申請']
                    },
                    {
                        title: '適合長期使用的交易者',
                        description: '更適合希望建立穩定工作流的個人交易者。',
                        tags: ['個人', '長期']
                    }
                ],
                applicationSteps: [
                    {title: '提交資訊', description: '留下基本資訊與使用意向。', detail: '補充交易場景與關注點。'},
                    {
                        title: '確認需求',
                        description: '確認方向與需求後進入發碼階段。',
                        detail: '重點確認風險理解與使用預期。'
                    },
                    {title: '獲取邀請碼', description: '收到邀請碼後進入註冊流程。', detail: '已有邀請碼可直接註冊。'},
                    {
                        title: '註冊開始使用',
                        description: '完成註冊後即可進入系統。',
                        detail: '登入後繼續查看 POC 與產品能力。'
                    }
                ],
                faqIntro: {eyebrow: '補充說明', title: '先把常見問題看清', description: '先看清註冊方式與邀請碼邏輯。'},
                faqItems: [
                    {
                        question: '為什麼採用邀請碼方式？',
                        answer: '目前公開站採用邀請制體驗，便於控制節奏並保持高品質回饋。'
                    },
                    {question: '沒有邀請碼也能開始嗎？', answer: '可以，先在聯絡頁提交資訊，我們會根據階段安排後續體驗。'},
                    {question: '註冊後能做什麼？', answer: '登入後可查看產品結構、體驗系統入口，並繼續了解交易工作流。'},
                    {
                        question: '官網支援哪些語言？',
                        answer: '本輪官網、登入頁和註冊頁支援簡體中文、繁體中文、英文、韓語、日語和法語。'
                    }
                ]
            }
        },
        'ko-KR': {
            primaryCta: {label: '지금 가입하기', path: '/register'},
            secondaryCta: {label: '지금 바로 개통', path: '/membership'},
            waitlistCta: {label: '초대 코드 받기', path: '/site/contact'},
            tertiaryCta: {label: '로그인', path: '/login'},
            siteNavItems: [
                {label: '홈', path: '/'},
                {label: 'Copilot', path: '/site/assist-trading'},
                {label: 'Auto Copilot', path: '/site/auto-trading'},
                {label: '자산배분', path: '/site/asset-allocation'},
                {
                    label: '리소스',
                    children: [{label: '작동 방식', path: '/site/how-it-works'}, {
                        label: '위험관리 및 보안',
                        path: '/site/risk-control'
                    }]
                },
                {
                    label: 'POC',
                    children: [
                        {label: 'Copilot', path: '/site/poc/assist-trading'},
                        {label: 'Auto Copilot', path: '/site/poc/auto-trading'},
                        {label: '패턴', path: '/site/poc/pattern'}
                    ]
                },
                {label: '요금', path: '/site/membership'},
                {label: '문의하기', path: '/site/contact'},
            ],
            footerNavItems: [
                {label: '홈', path: '/'},
                {label: 'Copilot', path: '/site/assist-trading'},
                {label: 'Auto Copilot', path: '/site/auto-trading'},
                {label: '자산배분', path: '/site/asset-allocation'},
                {label: '작동 방식', path: '/site/how-it-works'},
                {label: '위험관리 및 보안', path: '/site/risk-control'},
                {label: 'POC 개요', path: '/site/poc'},
                {label: 'Copilot', path: '/site/poc/assist-trading'},
                {label: 'Auto Copilot', path: '/site/poc/auto-trading'},
                {label: '패턴', path: '/site/poc/pattern'},
                {label: '요금', path: '/site/membership'},
                {label: '문의하기', path: '/site/contact'}
            ],
            heroMetrics: [
                {
                    title: '시장 기회',
                    value: '인사이트',
                    note: 'AI 기반 시그널 및 구조 분석',
                    trend: 'up'
                },
                {
                    title: '자산 성장',
                    value: '정렬',
                    note: '전략-목표 매칭 및 지속적 추적',
                    trend: 'stable'
                },
                {
                    title: '자산 안전',
                    value: '보호',
                    note: '컴플라이언스 기반 위험 관리, 투자 완전 관리',
                    trend: 'focus'
                }
            ],
            home: {
                hero: {
                    eyebrow: '안정적으로 멀리 나아가다',
                    title: '나만의 BlackRock을 구축하세요',
                    description: '함께 성장하는 AI 자산 관리자입니다. 나만의 규칙, 리듬, 경계 내에서 지속 가능한 트레이딩 및 자산 관리 시스템을 운영하세요.',
                    tags: ['Copilot', 'Auto Copilot', '자산배분', '위험 우선'],
                    panelLabel: '자산 관리, 이보다 간단할 수 없다',
                    panelTitle: 'AI가 어려운 일을 처리하고, 당신은 결정에 집중하세요',
                    panelDescription: '포트폴리오 구성, 위험 배분, 전략 실행 — 기관급 자산 관리 능력을 이제 AI가 시스템화하여 제공합니다.',
                    assistText: '더 명확한 시스템으로 일관된 의사결정과 실행 습관을 구축하세요.',
                    topCardLabel: '비전',
                    topCardTitle: '장기적 일관성',
                    topCardText: '안정적이고 검토 가능하며 지속 가능한 실행 스타일 지원',
                    bottomCardLabel: '체험 방식',
                    bottomCardTitle: '초대 코드 기반 액세스',
                    bottomCardText: '기본 중국어 간체, 전통 중국어 및 영어 지원'
                },
                section: {
                    eyebrow: '제품 방향',
                    title: '세 가지 제품 트랙, 하나의 시스템',
                    description: '정보 수집부터 실행, 자산배분까지 AI의 체계적인 구현 경로를 제시합니다.'
                },
                products: [
                    {
                        key: 'assist-trading',
                        label: 'Copilot',
                        slogan: '통찰이 빠르면, 판단도 빠르다',
                        description: '시장은 순식간에 변합니다. Copilot이 빠르게 상황을 파악하고 모든 기회를 자신있게 포착할 수 있도록 도와줍니다.',
                        tags: [],
                        bullets: [],
                        metrics: [],
                        ctaLabel: '',
                        ctaTo: '/site/assist-trading'
                    },
                    {
                        key: 'auto-trading',
                        label: 'Auto Copilot',
                        slogan: '당신의 의도, AI의 방향',
                        description: '투자 환경 설정처럼 간단하게 트레이딩을 시작하세요. 위험 허용 범위를 설정하면 AI가 그 경계 내에서 자율적으로 판단하고 지속적으로 거래합니다.',
                        tags: [],
                        bullets: [],
                        metrics: [],
                        ctaLabel: '',
                        ctaTo: '/site/auto-trading'
                    },
                    {
                        key: 'asset-allocation',
                        label: '자산배분',
                        slogan: '세부 사항은 무시하고, 목표에 집중하세요',
                        description: 'AI 자산 관리: 원하는 결과에만 집중하세요. 시스템이 배분 비율, 타이밍, 리밸런싱을 자동으로 처리합니다.',
                        tags: ['목표', '배분', 'AI 관리'],
                        bullets: ['결과 지향 — 프로세스가 아닌 결과에 집중', '능동적 관리 — 이탈 시 자동 수정', '지속적 최적화 — 시장 변화에 즉시 대응'],
                        metrics: [{
                            title: '관점',
                            value: '포트폴리오',
                            note: '포트폴리오 구조 완전 제어',
                            trend: 'stable'
                        }, {
                            title: '목표',
                            value: '시간',
                            note: '장기 목표 지속 추적',
                            trend: 'up'
                        }, {
                            title: '위험 관리',
                            value: '헤징',
                            note: '위험 노출 자동 관리',
                            trend: 'focus'
                        }, {
                            title: '전체 추적',
                            value: '투명성',
                            note: 'AI 의사결정 기록 확인 가능',
                            trend: 'stable'
                        }],
                        ctaLabel: '',
                        ctaTo: '/site/asset-allocation'
                    }
                ],
                proofItems: [
                    {
                        title: '브랜드 중심 스토리텔링',
                        description: '기능 세부사항보다 가치와 방향성을 먼저 전달합니다.',
                        tags: ['브랜드', '웹사이트']
                    },
                    {
                        title: '시스템화된 역량 설계',
                        description: '보조 트레이딩, 자동화, 자산배분을 하나의 프레임워크로 통합합니다.',
                        tags: ['시스템', '설계']
                    },
                    {
                        title: '실행 전 위험 우선',
                        description: '실행이 시작되기 전에 경계를 명확히 설정합니다.',
                        tags: ['위험', '경계']
                    }
                ],
                exploreTitle: {
                    eyebrow: '다음 단계',
                    title: '메커니즘을 이해한 후 POC를 살펴보세요',
                    description: '리소스에서는 시스템 작동 방식을, POC 페이지에서는 제품 구현 방법을 확인할 수 있습니다.'
                },
                exploreCards: [
                    {
                        eyebrow: '리소스',
                        title: '작동 메커니즘과 위험 경계',
                        description: 'D-Sense가 입력, 판단, 실행, 제어를 어떻게 구성하는지 확인하세요.',
                        actionLabel: '리소스 살펴보기',
                        path: '/site/how-it-works'
                    },
                    {
                        eyebrow: 'POC',
                        title: '보조 및 자동 트레이딩 데모',
                        description: '두 가지 핵심 트랙의 가치와 흐름을 더 직접적으로 확인하세요.',
                        actionLabel: 'POC 보기',
                        path: '/site/poc'
                    }
                ],
                ctaEyebrow: '얼리 액세스',
                ctaTitle: 'D-Sense를 탐험할 준비가 되셨나요?',
                ctaDescription: '당신의 로마를 위한 기초를 다지세요. 가입하고 전체 거래 경험을 시작하세요.'
            },
            productPage: {
                hero: {
                    eyebrow: '제품',
                    title: '판단부터 자산배분까지 아우르는 세 가지 역량',
                    description: '단순한 기능 집합이 아닙니다. 실제로 트레이딩 성과를 변화시키는 요소를 중심으로 구성된 사용 가능한 시스템입니다.',
                    tags: ['Copilot', 'Auto Copilot', '자산배분', '통합 워크스페이스'],
                    panelLabel: '제품 매트릭스',
                    panelTitle: '하나의 제품, 세 가지 핵심 트랙',
                    panelDescription: '하나의 시스템 안에서 관찰, 판단, 실행, 자산 검토를 모두 수행하세요.',
                    assistText: '이 페이지는 홈페이지에서 소개한 세 가지 트랙을 확장하여 설명합니다.',
                    topCardLabel: '가치',
                    topCardTitle: '전환 감소, 단편화 감소',
                    topCardText: '판단과 실행을 가능한 한 동일한 워크플로우에 유지',
                    bottomCardLabel: '표현',
                    bottomCardTitle: '브랜드 중심 스토리텔링',
                    bottomCardText: '웹사이트는 방향, 시나리오, 가치를 기준으로 구성됩니다'
                },
                directions: [
                    {
                        eyebrow: 'Copilot',
                        title: '세션 전에 워크스페이스를 열면 AI가 이미 핵심 시그널을 표시해줍니다',
                        description: '각 세션 전에 시스템이 시장 구조 변화, 주요 가격 레벨, AI 인사이트를 정리합니다. 빈 상태에서 시작하지 않고, 시그널부터 시작하여 더 빠르게 판단을 형성할 수 있습니다.',
                        bullets: ['워크스페이스를 열면 집계된 핵심 시그널이 준비되어 있음', 'AI가 이미 구조와 위험 포인트를 강조 표시', '더 적은 모니터링 부담으로 더 빠른 판단'],
                        metrics: [{
                            title: '최적 사용',
                            value: '세션 전/중',
                            note: '관찰 및 의사결정 지원',
                            trend: 'stable'
                        }, {
                            title: '출력',
                            value: '구조화됨',
                            note: '읽기 쉬운 제안, 태그, 위험 포인트',
                            trend: 'up'
                        }],
                        featuresTitle: {
                            eyebrow: '핵심 역량',
                            title: '인사이트에서 트레이딩까지 네 단계',
                            description: '데이터 수집, 시그널 집계, 경계 확인, 빠른 실행으로 이어지는 완벽한 역량 체인입니다.'
                        },
                        flowSteps: [
                            {
                                title: '데이터 수집',
                                description: '시장 데이터, 오더북, 온체인 데이터를 구조화된 입력으로 통합',
                                tags: ['시장', '오더북', '온체인']
                            },
                            {
                                title: '시그널 집계',
                                description: '다차원 시그널을 교차 검증하고 노이즈를 필터링하여 핵심 정보 강조',
                                tags: ['시그널', '검증', '필터']
                            },
                            {
                                title: '경계 확인',
                                description: '위험 규칙과 계정 상태에 따라 실행 가능한 경계 결정',
                                tags: ['위험', '계정', '경계']
                            },
                            {
                                title: '빠른 거래',
                                description: '원클릭으로 사전 정의된 전략 실행, 페이지 전환 불필요',
                                tags: ['원클릭', '전략', '효율성']
                            }
                        ],
                        usageTitle: {
                            eyebrow: '워크플로우',
                            title: '준비 상태 진입까지 세 단계',
                            description: '워크스페이스를 열고 거래를 시작하기까지 단 세 단계입니다.'
                        },
                        usageSteps: [
                            {
                                title: '워크스페이스 열기',
                                description: '시장 데이터와 지표가 준비된 워크스페이스에 진입',
                                detail: '수동 차트 검색 불필요'
                            },
                            {
                                title: '종목 확인/선택',
                                description: '여러 종목의 시그널과 주요 레벨을 빠르게 탐색',
                                detail: '활성 종목에 집중'
                            },
                            {
                                title: '원클릭 거래',
                                description: '경계 확인 후 직접 거래 실행',
                                detail: '더 적은 단계, 더 빠른 실행'
                            }
                        ]
                    },
                    {
                        eyebrow: '모두를 위한 트레이딩',
                        cardEyebrow: '모두를 위한 핀테크',
                        title: 'AI 자동 트레이딩, 제로부터 시작',
                        cardTitle: '코드 없는 AI 자동 트레이딩',
                        description: '위험 선호도만 설정하면 AI가 지속적으로 트레이딩을 실행합니다.',
                        cardDescription: '전략 작성도, 차트 모니터링도 필요 없습니다. 위험 기대치만 설정하면 AI가 적합한 자산을 매칭하고 지속적으로 거래를 반복합니다. 시장으로부터 자유로워지세요.',
                        bullets: ['제로 장벽 — 전략 불필요, 트레이딩 경험 불필요', '위험 설정 — AI에 위험 선호도를 알려주면 나머지는 AI가 처리', '완전 자동화 — 24/7 지속 거래, 완전 핸즈프리'],
                        metrics: [{
                            title: '진입 장벽',
                            value: '제로',
                            note: '위험 허용 범위만 설정',
                            trend: 'up'
                        }, {
                            title: '운영 모드',
                            value: '완전 자동',
                            note: 'AI가 자산을 매칭하고 거래 실행',
                            trend: 'focus'
                        }],
                        featuresTitle: {
                            eyebrow: '핵심 역량',
                            title: '6단계 루프: AI가 자율적으로 반복',
                            description: '데이터 수집부터 결과 피드백까지, AI가 설정한 경계 내에서 판단하고 반복합니다.'
                        },
                        flowSteps: [
                            {
                                title: '데이터 수집',
                                description: '다중 소스 시장 데이터를 통합 기반으로 집계',
                                tags: ['다중 소스', '수집', '입력']
                            },
                            {
                                title: '시장 감지',
                                description: 'AI가 시장 상태와 구조적 변화를 지속적으로 스캔하여 기회 식별',
                                tags: ['감지', '구조', '스캔']
                            },
                            {
                                title: '거래 계획',
                                description: '시장 구조와 위험 경계를 종합하여 자율적 거래 결정 수립',
                                tags: ['분석', '계획', '결정']
                            },
                            {
                                title: '위험 검증',
                                description: '실행 전 모든 행동을 사용자 정의 위험 경계에 대해 검증',
                                tags: ['경계', '검증', '허가']
                            },
                            {
                                title: '자동 실행',
                                description: 'AI가 정의된 경계 내에서 자율적으로 거래 실행',
                                tags: ['주문', '실행', '추적']
                            },
                            {
                                title: '결과 피드백',
                                description: '실행 결과와 상태가 루프로 다시 흘러가 다음 반복 준비',
                                tags: ['피드백', '상태', '반복']
                            }
                        ]
                    },
                    {
                        eyebrow: '자산배분',
                        title: 'AI가 포트폴리오 자산배분 전체를 관리합니다',
                        description: '목표와 위험 선호도를 설정하세요. 포트폴리오 구성부터 동적 리밸런싱까지 AI가 모두 처리합니다. 결과에만 집중하세요.',
                        bullets: ['결과 지향 — 프로세스가 아닌 결과에 집중', '능동적 관리 — 이탈 시 자동 수정', '지속적 최적화 — 시장 변화에 즉시 대응'],
                        metrics: [{
                            title: '관점',
                            value: '포트폴리오',
                            note: '포트폴리오 구조 완전 제어',
                            trend: 'stable'
                        }, {
                            title: '목표',
                            value: '시간',
                            note: '장기 목표 지속 추적',
                            trend: 'up'
                        }, {
                            title: '위험 관리',
                            value: '헤징',
                            note: '위험 노출 자동 관리',
                            trend: 'focus'
                        }, {
                            title: '전체 추적',
                            value: '투명성',
                            note: 'AI 의사결정 기록 확인 가능',
                            trend: 'stable'
                        }],
                        featuresTitle: {
                            eyebrow: '핵심 역량',
                            title: '여섯 단계, AI가 자산배분을 완전 관리',
                            description: '목표 분해부터 동적 리밸런싱까지, AI가 설정한 경계 내에서 자산배분 전략을 처음부터 끝까지 실행합니다.'
                        },
                        flowSteps: [
                            {
                                title: '목표 분해',
                                description: '장기 투자 목표를 실행 가능한 단계로 나누고 명확한 수익 기대치와 위험 허용 범위 설정',
                                tags: ['분해', '목표', '단계']
                            },
                            {
                                title: '배분 계획',
                                description: '목표와 시장 상황에 따라 자산군별 배분 비율과 투자 전략 수립',
                                tags: ['계획', '배분', '전략']
                            },
                            {
                                title: '자산 비교',
                                description: '자산군별 우량 종목을 선별하고 다차원 비교를 통해 최적의 포트폴리오 후보 결정',
                                tags: ['선별', '비교', '목표']
                            },
                            {
                                title: '포트폴리오 구성 및 실행',
                                description: '계획된 비율에 따라 매수 주문을 실행하고 포트폴리오 구성을 완료하며 진행 상황 추적',
                                tags: ['구성', '실행', '포지션']
                            },
                            {
                                title: '위험 모니터링 및 컴플라이언스',
                                description: '포트폴리오 위험 노출 및 컴플라이언스 지표를 실시간 모니터링하여 투자가 사전 설정된 경계 내에 유지되도록 보장',
                                tags: ['모니터링', '위험', '컴플라이언스']
                            },
                            {
                                title: '가치 추적 및 리밸런싱',
                                description: '자산 가치 변화를 지속적으로 추적하고 편차가 임계치를 초과하면 자동으로 리밸런싱을 트리거하여 포트폴리오를 목표 궤도에 유지',
                                tags: ['추적', '리밸런스', '조정']
                            }
                        ],
                        usageTitle: {
                            eyebrow: '워크플로우',
                            title: 'AI 자산 관리까지 세 단계',
                            description: '목표 설정부터 장기 운영까지 — 단 세 단계입니다.'
                        },
                        usageSteps: [
                            {
                                title: '투자 목표 설정',
                                description: 'AI에 위험 선호도와 수익 기대치 전달',
                                detail: '한 번 설정, 지속적 추적'
                            },
                            {
                                title: 'AI 자동 실행',
                                description: '포트폴리오 구성, 모니터링, 리밸런싱 — 모두 AI가 처리',
                                detail: '수동 개입 불필요'
                            },
                            {
                                title: '언제든지 상태 확인',
                                description: '패널을 열어 포트폴리오 성과 및 AI 조정 내역 확인',
                                detail: '투명하고 추적 가능'
                            }
                        ]
                    }
                ]
            },
            resources: {
                proofBarItems: ['시장 입력', '구조화된 판단', '위험 검증', '실행 피드백'],
                howItWorksHero: {
                    eyebrow: '작동 방식',
                    title: '트레이딩 파이프라인, 투명하고 통제 가능',
                    description: 'D-Sense는 블랙박스 시그널 도구가 아닙니다. 입력, 판단, 위험 관리, 실행을 설명 가능한 흐름으로 구성합니다.',
                    panelLabel: '워크플로우 루프',
                    panelTitle: '입력에서 실행 루프까지',
                    panelDescription: '각 단계는 명확한 역할을 가지므로 트레이더가 액션이 발생하는 이유를 이해할 수 있습니다.'
                },
                howItWorksSteps: [
                    {
                        title: '시장 입력',
                        description: '시장 구조, 맥락, 시그널을 사용 가능한 입력으로 집계합니다.',
                        detail: '관찰이 여러 화면에 분산되지 않도록 합니다.'
                    },
                    {
                        title: '전략 판단',
                        description: 'AI와 규칙이 함께 구조화된 제안을 생성합니다.',
                        detail: '결론, 레이블, 이유를 함께 확인할 수 있습니다.'
                    },
                    {
                        title: '위험 검증',
                        description: '모든 행동 허용 전에 안전장치가 확인됩니다.',
                        detail: '조건이 충족되지 않으면 실행이 시작되지 않습니다.'
                    },
                    {
                        title: '실행 피드백',
                        description: '실행 결과와 상태가 하나의 통합 뷰로 반환됩니다.',
                        detail: '상태와 결과를 지속적으로 추적합니다.'
                    }
                ],
                howItWorksFlowSteps: [
                    {
                        title: '데이터 수집',
                        description: '다중 소스 시장 데이터, 오더북, 온체인 데이터를 통합된 구조화 입력으로 결합',
                        tags: ['시장', '온체인', '다중 소스']
                    },
                    {
                        title: '시장 감지',
                        description: 'AI가 시장 상태와 구조적 변화를 지속적으로 스캔하여 핵심 시그널과 기회 식별',
                        tags: ['감지', '스캔', '식별']
                    },
                    {
                        title: '거래 계획',
                        description: '시장 구조와 위험 경계를 종합하여 자율적 거래 결정 및 전략 수립',
                        tags: ['분석', '계획', '결정']
                    },
                    {
                        title: '위험 검증',
                        description: '사용자 정의 위험 경계에 대해 각 행동을 검증하여 컴플라이언스와 안전성 보장',
                        tags: ['경계', '검증', '허가']
                    },
                    {
                        title: '자동 실행',
                        description: 'AI가 경계 내에서 자율적으로 거래를 실행하고 주문 실행 상태를 지속적으로 추적',
                        tags: ['주문', '실행', '추적']
                    },
                    {
                        title: '결과 피드백',
                        description: '실행 결과와 시장 상태가 시스템으로 다시 흘러가 다음 분석 반복 준비',
                        tags: ['피드백', '반복', '루프']
                    }
                ],
                howItWorksAiDecision: [
                    {
                        eyebrow: 'AI 인지',
                        title: 'AI가 시장 상태를 이해하는 방법',
                        description: '시스템은 다중 소스 데이터를 AI가 읽을 수 있는 구조화된 입력으로 변환합니다. LLM을 사용하여 시장 구조, 트렌드 변화, 이상 시그널을 식별하고 가독성 있는 분석 결론을 생성합니다.',
                        bullets: [
                            'K-라인, 오더북, 온체인 등 다차원 데이터 통합',
                            'AI가 주요 가격 범위와 시장 구조 변화 식별',
                            '시그널 집계로 노이즈 필터링 및 고신뢰도 기회 강조',
                            '자연어로 구조화된 분석 결과 출력'
                        ],
                        metrics: [
                            {
                                title: '데이터 차원',
                                value: '50+',
                                note: '기술적 지표 및 시장 시그널',
                                trend: 'up'
                            },
                            {
                                title: '분석 빈도',
                                value: '실시간',
                                note: '지속적인 시장 상태 스캔',
                                trend: 'focus'
                            }
                        ]
                    },
                    {
                        eyebrow: 'AI 의사결정',
                        title: '분석에서 실행까지 — AI가 트레이딩 결정을 내리는 방법',
                        description: '시스템은 시장 상태, 위험 경계, 과거 데이터를 포함한 컨텍스트가 풍부한 프롬프트를 구성합니다. LLM을 호출하여 구조화된 트레이딩 제안을 생성하고, 파싱 엔진이 이를 실행 가능한 액션으로 변환합니다.',
                        bullets: [
                            '의사결정을 위한 다차원 컨텍스트 프롬프트 동적 구축',
                            'LLM이 구조화된 트레이딩 결정 출력 (방향/크기/시점)',
                            '파싱 엔진이 모델 출력을 실행 가능한 트레이딩 명령으로 변환',
                            '위험 검증 통과 후에만 거래소에 제출'
                        ],
                        metrics: [
                            {
                                title: '결정 지연',
                                value: '<30초',
                                note: '분석에서 실행 가능한 제안까지',
                                trend: 'up'
                            },
                            {
                                title: '추적 가능성',
                                value: '전체 체인',
                                note: '프롬프트→출력→액션 전체 감사 가능',
                                trend: 'focus'
                            }
                        ]
                    }
                ],
                howItWorksBlocks: [
                    {
                        title: '입력 계층 — 다중 소스 데이터 수집',
                        description: '거래소 시장 데이터, 온체인 데이터, 기술적 지표, 시장 심리를 통합 데이터 파이프라인으로 결합하여 상위 계층을 위해 정리 및 정규화',
                        tags: ['시장 API', '온체인', '지표', '데이터 파이프라인'],
                        iconName: 'database'
                    },
                    {
                        title: '판단 계층 — AI 분석 엔진',
                        description: 'LLM과 규칙 엔진을 결합하여 다차원 시장 분석 수행. 프롬프트를 동적으로 구성하고 모델을 호출하여 구조화된 시그널과 트레이딩 제안 출력',
                        tags: ['LLM', '프롬프트 엔지니어링', '규칙 엔진', '시그널'],
                        iconName: 'brain'
                    },
                    {
                        title: '실행 계층 — 위험 및 거래 체인',
                        description: '실행 체인 앞단에 위험 검증을 내장하여 모든 거래가 사전 설정된 경계 내에서 실행되도록 보장. 결과가 실시간으로 다시 흘러가 피드백 루프 완성',
                        tags: ['위험 확인', '주문 실행', '동기화', '예외'],
                        iconName: 'shield'
                    }
                ],
                howItWorksSections: {
                    architecture: {
                        eyebrow: '시스템 아키텍처',
                        title: '6단계 폐쇄 루프: 데이터에서 실행까지',
                        description: '모든 단계는 명확한 역할을 가집니다. AI는 경계 내에서 자율적으로 운영되고 지속적으로 반복됩니다.'
                    },
                    aiDecision: {
                        eyebrow: 'AI 인텔리전스',
                        title: '시장 인지 및 의사결정 메커니즘',
                        description: '시스템이 시장 시그널을 이해하고 이를 실행 가능한 트레이딩 결정으로 변환하는 방법입니다.'
                    },
                    layers: {
                        eyebrow: '기술 스택',
                        title: '3계층 아키텍처',
                        description: '시스템은 세 가지 핵심 계층으로 구축되며, 각 계층은 고유한 기술적 책임을 가집니다.'
                    },
                    demo: {
                        eyebrow: '라이브 데모',
                        title: 'AI가 시장을 인지하는 방법 보기',
                        description: '시스템이 시장 시그널을 식별하고 레이블링하는 방법을 보여주는 시뮬레이션된 AI 분석 패널입니다.'
                    }
                },
                howItWorksDemo: {
                    symbol: 'BTC/USDT',
                    symbolChange: '+2.34%',
                    symbolChangeUp: true,
                    price: '67,432.50',
                    priceChange: '+1,542.80',
                    priceChangeUp: true,
                    signalTags: [
                        {label: '구조적 돌파', type: 'signal'},
                        {label: '거래량 급증', type: 'signal'},
                        {label: '높은 변동성', type: 'risk'},
                        {label: 'AI 강세 전망', type: 'info'}
                    ],
                    aiJudgmentTitle: 'AI 판단',
                    aiJudgmentValue: '강세 · 중간 확신도',
                    aiJudgmentNote: '거래량을 동반한 최근 저항선 돌파',
                    keyLevelTitle: '주요 레벨',
                    keyLevelValue: '67,200 / 68,500',
                    keyLevelNote: '저항 68,500, 지지 67,200'
                },
                howItWorksMetrics: [
                    {
                        title: '처리 단계',
                        value: '6단계',
                        note: '수집부터 피드백까지 완전한 폐쇄 루프',
                        trend: 'focus'
                    },
                    {
                        title: '결정 지연',
                        value: '<30초',
                        note: '분석에서 트레이딩 제안까지',
                        trend: 'up'
                    },
                    {
                        title: '데이터 차원',
                        value: '50+',
                        note: '기술적 지표 및 시장 시그널',
                        trend: 'up'
                    }
                ],
                howItWorksCta: {
                    eyebrow: '시작하기',
                    title: '메커니즘을 이해하고 탐험 시작',
                    description: '시스템 작동 방식을 학습한 후 POC를 통해 자신의 트레이딩 스타일에 맞는지 확인하세요.'
                },
                riskHero: {
                    eyebrow: '위험 관리 및 보안',
                    title: '경계 우선, 한계 내에서의 실행',
                    description: '위험 관리는 부가 기능이 아닙니다. 장기적으로 사용할 가치가 있는 시스템의 전제 조건입니다.',
                    panelLabel: '위험 우선',
                    panelTitle: '경계를 행동보다 먼저, 안전을 속도보다 먼저',
                    panelDescription: '위험이 불명확할 때 시스템은 노출을 확대하는 대신 행동을 축소해야 합니다.'
                },
                riskGuardItems: [
                    {
                        title: '위험 모드 우선',
                        description: '실행을 결정하기 전에 선호도와 경계를 설정합니다.',
                        bullets: ['보수적 / 신중 / 균형 / 적극적 / 공격적 모드', '모든 행동 전 임계치 확인', '조건 불충족 시 실행 불가']
                    },
                    {
                        title: '손절 및 포지션 관리',
                        description: '손실폭과 노출을 허용 가능한 범위 내로 유지합니다.',
                        bullets: ['자동 적용 포지션 상한', '이익실현 및 손절매 연동', '무경계 평균단가 낮추기 불가']
                    },
                    {
                        title: '권한 및 키 격리',
                        description: '최소 권한 원칙으로 민감 작업을 처리합니다.',
                        bullets: ['계층적 API 권한', '암호화된 키 격리', '이상 징후 시 안전한 폴백']
                    }
                ],
                riskMatrixItems: [
                    {
                        title: '실행 전 검증',
                        description: '거래소에 도달하기 전에 모든 행동이 위험 조건에 대해 검증됩니다.',
                        tags: ['사전 확인', '검증'],
                        iconName: 'shield'
                    },
                    {
                        title: '권한 경계',
                        description: 'API 및 계정 기능이 필요한 최소 범위로 제한됩니다.',
                        tags: ['권한', '보안'],
                        iconName: 'layers'
                    },
                    {
                        title: '지속적 피드백',
                        description: '실행 결과와 위험 상태가 통합 뷰로 다시 흘러가 모니터링 루프를 완성합니다.',
                        tags: ['피드백', '추적성'],
                        iconName: 'waypoints'
                    }
                ],
                riskSections: {
                    guard: {
                        eyebrow: '위험 규칙',
                        title: '삼중 방어 체계',
                        description: '위험 선호도에서 포지션 관리, 권한 관리까지 각 계층이 안전 경계를 강화합니다.'
                    },
                    matrix: {
                        eyebrow: '실행 가드',
                        title: '모든 단계가 규칙 내에서 실행됩니다',
                        description: '검증 로직이 실행 체인의 모든 노드에 내장되어 행동이 항상 사전 설정된 경계 내에 유지됩니다.'
                    }
                },
                riskCta: {
                    eyebrow: '시작하기',
                    title: '위험 관리가 트레이딩을 이끌게 하세요',
                    description: '위험 경계가 모든 거래를 보호하는 방법을 확인하세요.'
                },
                riskMetrics: [
                    {title: '위험 계층', value: '3계층', note: '모드 → 포지션 → 권한', trend: 'focus'},
                    {title: '검증', value: '실행 전', note: '모든 행동 전 자동 확인', trend: 'up'},
                    {title: '권한', value: '계층화', note: '최소 권한 API 액세스', trend: 'stable'}
                ]
            },
            pocPage: {
                hero: {
                    eyebrow: 'POC',
                    title: '구체적인 POC 페이지를 통해 제품 작동 방식 확인',
                    description: '이 페이지들은 추상적으로 머물지 않습니다. 보조 트레이딩과 자동 트레이딩의 시나리오, 가치, 상호작용 구조를 보여줍니다.',
                    tags: ['Copilot', 'Auto Copilot', '시나리오', '데모'],
                    panelLabel: 'POC 진입',
                    panelTitle: '두 가지 트랙, 두 가지 제품 경험',
                    panelDescription: '보조 트레이딩과 자동화가 각각 다른 목표를 어떻게 지원하는지 살펴보세요.',
                    assistText: 'POC 페이지를 사용하여 시작하기 전에 적합성을 판단하세요.',
                    topCardLabel: '트랙 1',
                    topCardTitle: 'Copilot',
                    topCardText: '시장을 읽고, 판단을 형성하고, 행동 준비',
                    bottomCardLabel: '트랙 2',
                    bottomCardTitle: 'Auto Copilot',
                    bottomCardText: '규칙을 실행하고 결과를 피드백'
                },
                entries: [
                    {
                        title: 'Copilot',
                        slogan: '더 빠르게 보고, 더 자신있게 판단',
                        description: '시장 관찰부터 의사결정 지원까지의 흐름을 살펴보세요.',
                        tags: ['시장 뷰', 'AI 설명', '위험 알림'],
                        path: '/site/poc/assist-trading'
                    },
                    {
                        title: 'Auto Copilot',
                        slogan: '규칙 우선, 실행 지속',
                        description: '정의된 경계 내에서 전략이 계속 실행되고 상태 피드백이 제공되는 방법을 확인하세요.',
                        tags: ['규칙 실행', '위험 우선', '피드백'],
                        path: '/site/poc/auto-trading'
                    },
                    {
                        title: '패턴 인식',
                        slogan: '모든 캔들이 이야기를 들려준다',
                        description: 'AI 기반 분석을 통한 자동 K-라인 패턴 감지를 확인하세요.',
                        tags: ['패턴', 'AI 분석', '백테스트'],
                        path: '/site/poc/pattern'
                    }
                ],
                features: [
                    {
                        title: '시나리오 중심 스토리텔링',
                        description: '모듈만 나열하는 대신 각 페이지가 해결하는 문제를 보여줍니다.',
                        tags: ['시나리오', '가치']
                    },
                    {
                        title: '흐름 중심 구조',
                        description: '모듈의 순서와 정보 흐름을 명확히 합니다.',
                        tags: ['흐름', '구조']
                    },
                    {
                        title: '경계 인식 설명',
                        description: '데모 자체 내에서 안전장치와 한계를 설명합니다.',
                        tags: ['경계', '위험']
                    }
                ],
                faqIntro: {
                    eyebrow: '추가 참고 사항',
                    title: 'POC 전에 이러한 경계를 읽어보세요',
                    description: '사용 전에 몇 가지 자주 묻는 질문이 필수 사항을 명확히 해줍니다.'
                },
                faqItems: [
                    {
                        question: '이 시스템은 누구를 위한 것인가요?',
                        answer: '화면 모니터링 시간을 줄이고, 더 빠른 결정과 명확한 위험 경계를 원하는 개인 트레이더에게 적합합니다.'
                    },
                    {
                        question: '시스템이 자동으로 거래를 실행할 수 있나요?',
                        answer: '네, 보조 및 자동 모드를 지원합니다. 자동화를 활성화할지 여부는 자신의 위험 이해도에 따라 결정해야 합니다.'
                    },
                    {
                        question: '왜 먼저 POC를 검토해야 하나요?',
                        answer: 'POC 페이지는 시스템을 사용하기 전에 제품 로직, 사용 사례, 경계를 이해하는 데 도움이 됩니다.'
                    },
                    {
                        question: '어떻게 시작하나요?',
                        answer: '이미 초대 코드가 있다면 지금 가입할 수 있습니다. 그렇지 않다면 먼저 문의 페이지에서 정보를 제출하세요.'
                    }
                ]
            },
            assistPocPage: {
                hero: {
                    eyebrow: 'Copilot',
                    title: '보조 트레이딩은 맹목적으로 쫓지 않고 기회를 더 빨리 이해하도록 돕습니다',
                    description: '변동성이 큰 시장에서 트레이더가 더 빠르게 관찰, 판단, 행동을 준비할 수 있도록 지원하면서 최종 제어권은 인간에게 유지합니다.',
                    tags: ['시장 시그널', 'AI 지원', '위험 알림', '액션 제안'],
                    panelLabel: '핵심 슬로건',
                    panelTitle: '더 빠르게 보고, 더 자신있게 판단',
                    panelDescription: '행동을 결정하기 전에 시스템을 사용하여 기회를 더 명확하게 확인하세요.',
                    assistText: '보조 트레이딩은 판단을 지원합니다. 대체하지 않습니다.',
                    topCardLabel: '최적 사용',
                    topCardTitle: '세션 전 및 세션 중',
                    topCardText: '빈번한 관찰과 빠른 결정 형성에 이상적',
                    bottomCardLabel: '결과',
                    bottomCardTitle: '더 적은 망설임',
                    bottomCardText: '관찰, 이해, 행동 준비를 하나의 흐름으로 통합'
                },
                features: [
                    {
                        title: '시그널 집계',
                        description: '가장 중요한 시장 레이블과 변화를 한 곳에 유지합니다.',
                        tags: ['시그널', '레이블']
                    },
                    {
                        title: 'AI 지원 판단',
                        description: '구조화된 설명과 제안을 신속하게 얻습니다.',
                        tags: ['AI', '설명']
                    },
                    {
                        title: '위험 우선 알림',
                        description: '행동하기 전에 중요한 한계를 확인합니다.',
                        tags: ['위험', '알림']
                    },
                    {
                        title: '액션 중심 출력',
                        description: '판단을 더 명확한 다음 행동으로 전환합니다.',
                        tags: ['액션', '준비']
                    }
                ],
                flowSteps: [
                    {
                        title: '시장 관찰',
                        description: '한 곳에서 주요 시장 변화와 맥락을 검토합니다.',
                        detail: '더 빠르게 첫 번째 필터링 라운드 완료.'
                    },
                    {
                        title: '시그널 이해',
                        description: 'AI를 사용하여 이유, 레이블, 방향을 구성합니다.',
                        detail: '결론을 더 이해하기 쉽게 만듭니다.'
                    },
                    {
                        title: '경계 확인',
                        description: '행동하기 전에 위험 알림과 실행 한계를 검토합니다.',
                        detail: '비용을 보지 않고 기회만 보지 마십시오.'
                    },
                    {
                        title: '행동 준비',
                        description: '더 명확한 다음 행동 계획을 수립합니다.',
                        detail: '실행 시작 여부는 여전히 귀하가 결정합니다.'
                    }
                ],
                stories: [
                    {
                        eyebrow: '가치 1',
                        title: '기회를 더 빨리 포착',
                        description: '분산된 관찰 프로세스를 하나의 워크스페이스로 모아 전환 비용을 줄입니다.',
                        bullets: ['더 적은 단편적 차트 보기', '더 빠른 정보 수렴', '더 신속한 판단 전환'],
                        metrics: [{
                            title: '관찰',
                            value: '통합됨',
                            note: '핵심 입력이 함께 표시됨',
                            trend: 'stable'
                        }, {title: '속도', value: '더 빠름', note: '전환 및 망설임 감소', trend: 'up'}]
                    },
                    {
                        eyebrow: '가치 2',
                        title: '판단을 더 쉽게 설명',
                        description: '시그널에서 멈추지 마세요. 그와 함께 추론과 위험을 보여주세요.',
                        bullets: ['시그널과 이유를 함께 확인', '위험과 기회를 함께 표현', '나중에 검토를 더 쉽게'],
                        metrics: [{
                            title: '출력',
                            value: '구조화됨',
                            note: '태그, 설명, 제안이 연결되어 유지',
                            trend: 'up'
                        }, {
                            title: '검토 가능성',
                            value: '더 강력함',
                            note: '의사결정 로직을 더 쉽게 추적',
                            trend: 'focus'
                        }]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: '핵심 역량',
                        title: '시그널에서 액션까지, 완전한 경로',
                        description: '기능을 쌓아올리는 대신 보조 트레이딩의 핵심 역량을 사용 가능한 시스템 경로로 구성합니다.'
                    },
                    flow: {
                        eyebrow: '일반적인 흐름',
                        title: '워크스페이스를 열고, 네 단계로 준비',
                        description: '관찰에서 결정까지, 시스템이 분산된 단계를 반복 가능한 워크플로우로 수렴합니다.'
                    },
                    stories: {
                        eyebrow: '가치 제안',
                        title: '보조 트레이딩이 해결하는 두 가지 핵심 문제',
                        description: '과부하 상태에서 기회를 더 빨리 포착하는 방법과 복잡한 판단을 더 쉽게 설명하는 방법.'
                    },
                    demo: {
                        eyebrow: '실시간 데모',
                        title: '트레이딩 워크스페이스 쇼케이스',
                        description: '실시간 시세, 포지션, 거래 내역까지 - 트레이딩 워크스페이스의 핵심 데이터 패널을 완벽하게 보여줍니다.'
                    },
                    cta: {
                        primaryLabel: '초대 코드 받기',
                        primaryTo: '/site/assist-trading'
                    }
                }
            },
            autoPocPage: {
                hero: {
                    eyebrow: 'Auto Copilot',
                    title: '목표를 설정하면 AI가 트레이딩을 실행합니다',
                    description: '위험 허용 범위와 목표 수익률만 설정하세요. AI가 기회를 매칭하고, 포지션을 관리하며, 자본 활용도를 최적화합니다 — 전략 코딩도, 화면 모니터링도 필요 없습니다.',
                    tags: ['위험 선호도', '목표 수익', '자본 효율', 'AI 자율 트레이딩'],
                    panelLabel: '핵심 철학',
                    panelTitle: '당신이 목표를 설정하면, AI가 실행을 담당합니다',
                    panelDescription: '트레이딩에 복잡한 전략 프로그래밍이 필요하지 않습니다. AI에 위험 허용 범위와 수익 기대치를 알려주세요 — 최적의 경로를 자동으로 찾아줍니다.',
                    assistText: '위험 선호도와 수익 목표를 설정하세요. 나머지는 AI가 처리합니다.',
                    topCardLabel: '적합한 대상',
                    topCardTitle: '심플 & 다이렉트',
                    topCardText: '코딩이나 전략 작성 불필요 — 모든 트레이더에게 적합',
                    bottomCardLabel: '핵심 장점',
                    bottomCardTitle: 'AI 기반 의사결정',
                    bottomCardText: 'AI가 시장을 실시간 분석하여 설정된 경계 내에서 최적의 트레이딩 기회를 자율적으로 찾습니다'
                },
                features: [
                    {
                        title: '위험 선호도 설정',
                        description: '최대 허용 손실률을 설정합니다. AI는 이 경계 내에서 엄격하게 운영됩니다.',
                        tags: ['위험 관리', '안전']
                    },
                    {
                        title: '목표 수익 관리',
                        description: '예상 수익률을 설정합니다. AI가 자동으로 일치하는 시장 기회를 찾습니다.',
                        tags: ['목표', '수익']
                    },
                    {
                        title: '자본 활용 최적화',
                        description: 'AI가 거래별 자본을 지능적으로 할당하여 자금 효율성을 극대화합니다.',
                        tags: ['효율성', '포지션']
                    },
                    {
                        title: 'AI 자율 실행',
                        description: '연중무휴 시장 모니터링, 자동 타이밍, 진입 및 청산 — 완전 핸즈프리.',
                        tags: ['자동화', '24/7']
                    },
                ],
                flowSteps: [
                    {
                        title: '위험 경계 설정',
                        description: '최대 허용 손실폭과 거래당 손실 한도를 정의합니다.',
                        detail: '모든 AI 결정은 이 경계 내에 유지됩니다'
                    },
                    {
                        title: '수익 목표 설정',
                        description: '예상 연간 또는 분기별 수익률을 설정합니다.',
                        detail: 'AI는 이 기준을 사용하여 적합한 기회를 매칭합니다'
                    },
                    {
                        title: 'AI 스마트 매칭',
                        description: 'AI가 시장을 실시간 분석하여 위험-수익 프로필에 맞는 기회를 매칭합니다.',
                        detail: '자본 활용도를 기반으로 지능적으로 포지션 할당'
                    },
                    {
                        title: '자동 실행 및 최적화',
                        description: 'AI가 자율적으로 거래를 실행하고 시장 변화에 지속적으로 적응합니다.',
                        detail: '모든 거래는 추적 가능하며 명확한 수익 가시성 확보'
                    }
                ],
                stories: [
                    {
                        eyebrow: '가치 1',
                        title: '전략 불필요, 목표만 있으면 됩니다',
                        description: '트레이딩 전략을 작성할 필요가 없습니다. AI에 위험 경계와 수익 기대치를 알려주면 최적의 경로를 자동으로 찾습니다.',
                        bullets: ['제로 코딩 — 설정하면 즉시 실행', 'AI가 기회 매칭을 자동 최적화', '화면 모니터링 불필요 — 완전 자율'],
                        metrics: [{
                            title: '사용 용이성',
                            value: '최소',
                            note: '시작하는 데 두 가지 파라미터만 필요',
                            trend: 'up'
                        }, {
                            title: '의사결정',
                            value: 'AI 기반',
                            note: 'AI가 수동 판단 및 실행을 대체',
                            trend: 'stable'
                        }]
                    },
                    {
                        eyebrow: '가치 2',
                        title: '모든 달러가 효율적으로 작동합니다',
                        description: 'AI는 단순히 거래만 하는 것이 아니라 자본을 관리합니다. 포지션 크기를 동적으로 조정하여 자본 활용도가 항상 최적 상태로 유지됩니다.',
                        bullets: ['스마트 포지션 관리, 동적 할당', '실시간 자본 활용도 조정', '유휴 자금도 생산적으로'],
                        metrics: [{
                            title: '자본 효율',
                            value: '최적',
                            note: 'AI가 지속적으로 자본 할당 최적화',
                            trend: 'focus'
                        }, {
                            title: '수익 출처',
                            value: '투명',
                            note: '모든 수익과 손실을 추적 가능',
                            trend: 'up'
                        }]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: '핵심 역량',
                        title: 'AI 자율 트레이딩의 네 가지 역량',
                        description: '전략이 필요 없습니다. 선호도만 설정하면 AI가 모든 것을 처리합니다.'
                    },
                    flow: {
                        eyebrow: '시작하기',
                        title: '네 단계, AI가 즉시 시작합니다',
                        description: '선호도를 설정하는 순간 AI가 당신을 위해 작동하기 시작합니다.'
                    },
                    stories: {
                        eyebrow: '가치 제안',
                        title: '심플하지만 강력합니다',
                        description: '복잡한 트레이딩을 단순한 선택으로 전환합니다.'
                    },
                    demo: {
                        eyebrow: '인터랙티브 데모',
                        title: 'AI 자동 트레이딩 데모',
                        description: 'API Key와 위험 선호도를 설정하고 AI 자동 트레이딩의 전체 과정을 경험해보세요.'
                    }
                }
            },
            patternPocPage: {
                hero: {
                    eyebrow: '패턴 POC',
                    title: 'AI 기반 K-라인 패턴 인식',
                    description: '감지부터 분석까지, AI가 K-라인 패턴을 식별, 백테스트, 평가하여 트레이딩 기회를 찾는 방법을 확인하세요.',
                    tags: ['K-라인', 'AI 분석', '백테스트', '시그널'],
                    panelLabel: '핵심 역량',
                    panelTitle: '모든 캔들 패턴이 포착됩니다',
                    panelDescription: '수백 가지 클래식 패턴 감지 알고리즘과 AI 기반 분석을 결합하여 잠재적 트레이딩 기회를 발견할 수 있도록 도와줍니다.',
                    assistText: '더 이상 수동 차트 스캔은 없습니다. 시스템이 자동으로 패턴을 레이블링하고 AI 분석을 제공합니다.',
                    topCardLabel: '감지',
                    topCardTitle: '자동 식별',
                    topCardText: '1분에서 1일 타임프레임까지 패턴 자동 감지',
                    bottomCardLabel: 'AI 분석',
                    bottomCardTitle: '심층 인사이트',
                    bottomCardText: 'AI가 진입, 손절, 목표 가격을 추천합니다'
                },
                features: [
                    {
                        title: '다중 패턴 감지',
                        description: '망치형, 장악형, 모닝스타 등 수백 가지 클래식 K-라인 패턴을 자동 감지합니다.',
                        tags: ['패턴', '커버리지']
                    },
                    {
                        title: 'AI 기반 분석',
                        description: 'LLM 기반 다차원 분석으로 진입 가격, 목표 가격, 손절 제안을 제공합니다.',
                        tags: ['AI', '분석']
                    },
                    {
                        title: '과거 백테스트',
                        description: '각 패턴을 백테스트하여 승률, 손익 비율 및 기타 주요 지표를 확인하여 정보에 기반한 결정을 내립니다.',
                        tags: ['백테스트', '데이터']
                    },
                    {
                        title: '영역 인식',
                        description: '지지/저항 영역과 횡보 범위를 자동 식별하여 주요 가격 레벨을 찾습니다.',
                        tags: ['영역', '지지/저항']
                    },
                ],
                flowSteps: [
                    {
                        title: '심볼 선택',
                        description: '대상 트레이딩 페어를 선택하면 시스템이 자동으로 K-라인 데이터를 가져옵니다.',
                        detail: '무기한 스왑 및 현물 지원, BTC, ETH 및 주요 코인 커버'
                    },
                    {
                        title: '패턴 감지',
                        description: '시스템이 K-라인을 스캔하여 모든 패턴 시그널을 식별하고 표시합니다.',
                        detail: '1분에서 1일 타임프레임 지원, 실시간 업데이트'
                    },
                    {
                        title: 'AI 분석',
                        description: '감지된 패턴에 대한 AI 기반 분석 및 트레이딩 참고 정보 제공.',
                        detail: '진입, 손절, 목표 가격 및 위험 수준 권장 사항 제공'
                    },
                    {
                        title: '백테스트 검증',
                        description: '과거 성과 데이터를 확인하여 시그널 신뢰도를 평가합니다.',
                        detail: '승률, 손익 비율 및 과거 손익 기록'
                    }
                ],
                stories: [
                    {
                        eyebrow: '가치 1',
                        title: '더 포괄적인 패턴 감지',
                        description: '수백 가지 패턴이 자동으로 감지됩니다. 단순한 망치형부터 복잡한 모닝스타까지. 놓칠 수 있는 시그널을 포착하세요.',
                        bullets: ['수동 차트 검토 없는 자동 스캔', '수백 가지 클래식 패턴 커버', '다중 타임프레임 지원'],
                        metrics: [
                            {
                                title: '효율성',
                                value: '자동',
                                note: '실시간 모니터링, 제로 수동 작업',
                                trend: 'up'
                            },
                            {
                                title: '커버리지',
                                value: '광범위',
                                note: '수백 가지 패턴 자동 감지',
                                trend: 'focus'
                            }
                        ]
                    },
                    {
                        eyebrow: '가치 2',
                        title: 'AI를 통한 더 깊은 분석',
                        description: 'AI는 패턴 유형뿐만 아니라 신뢰도, 잠재 손익 비율 및 구체적인 거래 권장 사항을 알려줍니다.',
                        bullets: ['AI 다차원 시그널 분석', '진입, 손절, 목표 가격 제공', '의사결정 지원을 위한 위험 평가'],
                        metrics: [
                            {
                                title: '깊이',
                                value: '종합적',
                                note: '다차원 AI 분석',
                                trend: 'up'
                            },
                            {
                                title: '실행 가능성',
                                value: '실용적',
                                note: '구체적인 가격 제안 및 위험 알림',
                                trend: 'focus'
                            }
                        ]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: '핵심 역량',
                        title: '감지부터 분석까지 완전한 패턴 인식 파이프라인',
                        description: '단순 감지를 넘어 — 감지, 분석, 과거 검증을 완전한 도구 체인으로 구성합니다.'
                    },
                    flow: {
                        eyebrow: '일반적인 흐름',
                        title: '네 단계로 패턴 인식 완료',
                        description: '심볼 선택부터 백테스트 검증까지, 시스템이 전체 패턴 인식 워크플로우를 처리합니다.'
                    },
                    stories: {
                        eyebrow: '가치 제안',
                        title: '패턴 인식이 해결하는 세 가지 문제',
                        description: '수동 차트 작성은 느리고, 시그널이 누락되며, 분석에 깊이가 부족합니다 — 패턴 인식이 이 세 가지를 모두 해결합니다.'
                    },
                    demo: {
                        eyebrow: '인터랙티브 데모',
                        title: '캔들 패턴 인식 데모',
                        description: '캔들 패턴 인식을 통해 시장 구조 변화와 거래 기회를 빠르게 발견하세요.'
                    },
                    cta: {
                        primaryLabel: '초대 코드 받기',
                    }
                }
            },
            membershipPage: {
                hero: {
                    eyebrow: '멤버십',
                    title: '투자 단계에 꼭 맞는 역량',
                    description: '무료 체험부터 전문가급 완전 자동 거래까지, 4단계 멤버십이 통찰부터 실행·배분까지 전체 여정을 지원합니다.',
                    panelLabel: '온라인 개통',
                    panelTitle: '온라인으로 개통, 바로 적용',
                    panelDescription: '등급을 선택하고 온라인으로 결제하면 즉시 적용됩니다. 구독 상태와 만료일은 언제든지 대시보드에서 확인할 수 있습니다.'
                },
                sectionTitles: {
                    compare: {
                        eyebrow: '등급 비교',
                        title: '4단계 멤버십, 필요에 맞게 선택',
                        description: '가격과 기능 매트릭스 한눈에 보기. DCA 플랜 한도는 등급에 따라 상향됩니다.'
                    },
                    features: {
                        eyebrow: '유료 기능',
                        title: '각 기능은 구체적인 문제를 해결합니다',
                        description: 'DCA 어시스턴트 / 헤지 / 패턴 인식 / 자동 거래를 등급별로 해제합니다.'
                    },
                    cta: {
                        eyebrow: '개통 안내',
                        title: '업그레이드할 준비가 되셨나요?',
                        description: '등급을 선택하고 온라인으로 결제하면 즉시 개통되어 해당 기능을 바로 사용할 수 있습니다.'
                    }
                },
                priceLabels: {monthly: '월간', yearly: '연간'},
                tiers: [
                    {
                        name: '무료', tagline: '핵심 기능 체험', monthlyPrice: '0', yearlyPriceMonthly: '0',
                        features: [
                            {label: '리포트 구독·열람', enabled: true},
                            {label: 'DCA 어시스턴트', enabled: false},
                            {label: '헤지 거래', enabled: false},
                            {label: '패턴 인식 퀵 트레이딩', enabled: false},
                            {label: '자동 거래', enabled: false}
                        ]
                    },
                    {
                        name: '스타터', tagline: '계획형 자동 매수', monthlyPrice: '5.9U', yearlyPriceMonthly: '3.9U/월',
                        features: [
                            {label: '리포트 구독·열람', enabled: true},
                            {label: 'DCA 어시스턴트', enabled: true, note: '최대 3개 플랜'},
                            {label: '헤지 거래', enabled: false},
                            {label: '패턴 인식 퀵 트레이딩', enabled: false},
                            {label: '자동 거래', enabled: false}
                        ]
                    },
                    {
                        name: '프로', tagline: '거래·패턴 고급 기능', monthlyPrice: '15.9U', yearlyPriceMonthly: '9.9U/월',
                        highlight: true,
                        features: [
                            {label: '리포트 구독·열람', enabled: true},
                            {label: 'DCA 어시스턴트', enabled: true, note: '최대 10개 플랜'},
                            {label: '헤지 거래', enabled: true},
                            {label: '패턴 인식 퀵 트레이딩', enabled: true},
                            {label: '자동 거래', enabled: false}
                        ]
                    },
                    {
                        name: '전문가', tagline: 'AI 완전 자동 거래', monthlyPrice: '59.9U', yearlyPriceMonthly: '29.9U/월',
                        features: [
                            {label: '리포트 구독·열람', enabled: true},
                            {label: 'DCA 어시스턴트', enabled: true, note: '최대 15개 플랜'},
                            {label: '헤지 거래', enabled: true},
                            {label: '패턴 인식 퀵 트레이딩', enabled: true},
                            {label: '자동 거래', enabled: true}
                        ]
                    }
                ],
                featureIntros: [
                    {
                        title: 'DCA 어시스턴트',
                        description: '종목과 지표 조건으로 플랜을 만들면 시스템이 종가 캔들을 감시하고, 조건 충족 시 자동으로 시장가 매수합니다. 1회 또는 반복 실행.',
                        tags: ['플랜 기반', '조건 트리거', '자동 매수']
                    },
                    {
                        title: '헤지 거래',
                        description: '단일 레그·교차 종목 헤지, 보호 레그 자동 재연결, 익절·손절·익스포저 통합 관리.',
                        tags: ['단일 레그 헤지', '보호 레그', '익스포저 관리']
                    },
                    {
                        title: '패턴 인식 퀵 트레이딩',
                        description: 'AI가 핵심 패턴을 인식하고 원클릭 주문으로 판단을 빠르게 실행합니다.',
                        tags: ['패턴 인식', '퀵 주문']
                    },
                    {
                        title: '자동 거래',
                        description: '리스크 하한을 설정하면 AI가 경계 안에서 스스로 판단하고 지속적으로 거래합니다.',
                        tags: ['AI 판단', '경계 내 자율', '지속 거래']
                    }
                ],
                faqIntro: {
                    eyebrow: 'FAQ',
                    title: '자주 묻는 질문',
                    description: '멤버십과 개통에 관한 자주 묻는 질문입니다.'
                },
                faqItems: [
                    {
                        question: '멤버십은 어떻게 개통하나요?',
                        answer: '가격 페이지에서 등급을 선택하고 온라인으로 결제하면 즉시 개통됩니다. 만료 전 갱신 알림이 발송되며 멤버십 페이지에서 언제든지 구독을 관리할 수 있습니다.'
                    },
                    {
                        question: '멤버십이 만료되면 어떻게 되나요?',
                        answer: '다음 유효 구독 등급으로 자동 강등됩니다(없으면 무료). 새 등급 한도를 초과하는 DCA 플랜은 자동 일시정지되고 인앱 알림이 발송되며, 갱신 후 수동으로 복구합니다.'
                    },
                    {
                        question: 'DCA 플랜 한도는 어떻게 계산되나요?',
                        answer: '등급별: 스타터 3개, 프로 10개, 전문가 15개. 삭제되지 않은 플랜만 집계하며, 일시정지와 실행 중 모두 포함됩니다.'
                    },
                    {
                        question: '연간 요금 할인은 무엇인가요?',
                        answer: '연간 결제는 월 단가가 낮아집니다: 스타터 월 3.9U, 프로 월 9.9U, 전문가 월 29.9U.'
                    }
                ]
            },
            contactPage: {
                hero: {
                    eyebrow: '시작하기',
                    title: '초대 코드를 받고 D-Sense 탐험 시작',
                    description: '아직 초대 코드가 없다면 먼저 정보를 제출해 주세요. 수요와 출시 단계에 따라 다음 단계를 안내해 드립니다.',
                    tags: ['초대 코드', '액세스 신청', '시작하기'],
                    panelLabel: '시작 방법',
                    panelTitle: '코드가 있으면 지금 등록, 없으면 먼저 신청',
                    panelDescription: '초대 기반 액세스를 더 명확하고 쉬운 경로로 전환합니다.',
                    assistText: '정보를 제출하기 전에 액세스 경로를 이해하세요.',
                    topCardLabel: '빠른 시작',
                    topCardTitle: '두 가지 진입점',
                    topCardText: '직접 등록하거나 먼저 초대 신청',
                    bottomCardLabel: '액세스 모델',
                    bottomCardTitle: '초대 기반 출시',
                    bottomCardText: '초대 코드를 사용하여 액세스 리듬 제어'
                },
                contactModes: [
                    {
                        title: '이미 코드가 있나요? 지금 등록하세요',
                        description: '이미 초대 코드를 받았다면 지금 바로 등록할 수 있습니다.',
                        tags: ['등록', '시작']
                    },
                    {
                        title: '먼저 액세스가 필요하신가요? 여기에 신청하세요',
                        description: '먼저 정보를 제출하고 초대 프로세스를 시작하세요.',
                        tags: ['초대', '신청']
                    },
                    {
                        title: '장기적인 워크플로우를 원하는 트레이더를 위해 설계',
                        description: '더 안정적인 작업 시스템을 원하는 개인 트레이더에게 적합합니다.',
                        tags: ['개인', '장기']
                    }
                ],
                applicationSteps: [
                    {
                        title: '정보 제출',
                        description: '기본 정보와 사용 목적을 공유하세요.',
                        detail: '트레이딩 시나리오와 가장 중요하게 생각하는 것을 포함하세요.'
                    },
                    {
                        title: '적합성 확인',
                        description: '액세스 발급 전에 방향과 기대치를 확인합니다.',
                        detail: '위험 이해도와 기대치가 여기서 중요합니다.'
                    },
                    {
                        title: '초대 코드 수신',
                        description: '승인되면 등록을 위한 코드를 받게 됩니다.',
                        detail: '이미 코드가 있다면 바로 건너뛸 수 있습니다.'
                    },
                    {
                        title: '등록 및 시작',
                        description: '계정을 만들고 시스템을 시작하세요.',
                        detail: '그런 다음 제품 및 POC 페이지를 더 깊이 탐험하세요.'
                    }
                ],
                faqIntro: {
                    eyebrow: '추가 참고 사항',
                    title: '먼저 일반적인 질문을 명확히 하세요',
                    description: '진행하기 전에 초대 경로와 액세스 로직을 이해하세요.'
                },
                faqItems: [
                    {
                        question: '왜 초대 기반 액세스를 사용하나요?',
                        answer: '웹사이트는 현재 출시 속도를 제어하고 피드백 품질을 높게 유지하기 위해 초대 기반 온보딩을 사용합니다.'
                    },
                    {
                        question: '초대 코드 없이 시작할 수 있나요?',
                        answer: '네. 먼저 문의 페이지에서 정보를 제출하시면 출시 단계에 따라 다음 단계를 안내해 드립니다.'
                    },
                    {
                        question: '등록 후 무엇을 할 수 있나요?',
                        answer: '제품으로 이동하여 로그인하고 트레이딩 워크플로우를 더 깊이 학습할 수 있습니다.'
                    },
                    {
                        question: '어떤 언어가 지원되나요?',
                        answer: '이번 버전은 웹사이트, 로그인 페이지 및 등록 페이지에서 중국어 간체, 중국어 번체, 영어, 한국어, 일본어, 프랑스어를 지원합니다.'
                    }
                ]
            }
        },
        'en-US': {
            primaryCta: {label: 'Register Now', path: '/register'},
            secondaryCta: {label: 'Subscribe now', path: '/membership'},
            waitlistCta: {label: 'Get an Invitation Code', path: '/site/contact'},
            tertiaryCta: {label: 'Sign In', path: '/login'},
            siteNavItems: [
                {label: 'Home', path: '/'},
                {label: 'Copilot', path: '/site/assist-trading'},
                {label: 'Auto Copilot', path: '/site/auto-trading'},
                {label: 'Asset Allocation', path: '/site/asset-allocation'},
                {
                    label: 'Resources',
                    children: [{label: 'How It Works', path: '/site/how-it-works'}, {
                        label: 'Risk & Security',
                        path: '/site/risk-control'
                    }]
                },
                {
                    label: 'POC',
                    children: [
                        {label: 'Copilot', path: '/site/poc/assist-trading'},
                        {label: 'Auto Copilot', path: '/site/poc/auto-trading'},
                        {label: 'Pattern', path: '/site/poc/pattern'}
                    ]
                },
                {label: 'Pricing', path: '/site/membership'},
                {label: 'Contact', path: '/site/contact'},
            ],
            footerNavItems: [
                {label: 'Home', path: '/'},
                {label: 'Copilot', path: '/site/assist-trading'},
                {label: 'Auto Copilot', path: '/site/auto-trading'},
                {label: 'Asset Allocation', path: '/site/asset-allocation'},
                {label: 'How It Works', path: '/site/how-it-works'},
                {label: 'Risk & Security', path: '/site/risk-control'},
                {label: 'POC Overview', path: '/site/poc'},
                {label: 'Copilot', path: '/site/poc/assist-trading'},
                {label: 'Auto Copilot', path: '/site/poc/auto-trading'},
                {label: 'Pattern', path: '/site/poc/pattern'},
                {label: 'Pricing', path: '/site/membership'},
                {label: 'Contact', path: '/site/contact'}
            ],
            heroMetrics: [
                {
                    title: 'Opportunities',
                    value: 'Insight',
                    note: 'AI-powered signal and structure analysis',
                    trend: 'up'
                },
                {
                    title: 'Growth',
                    value: 'Alignment',
                    note: 'Strategy-to-goal matching with continuous tracking',
                    trend: 'stable'
                },
                {
                    title: 'Security',
                    value: 'Protection',
                    note: 'Compliance-driven risk control, investment fully managed',
                    trend: 'focus'
                }
            ],
            home: {
                hero: {
                    eyebrow: 'A steady pace leads to far',
                    title: 'Build your own BlackRock',
                    description: 'Your AI asset manager that grows with you. Run a sustainable trading and asset management system on your own rules, rhythm, and boundaries.',
                    tags: ['Copilot', 'Auto Copilot', 'Asset Allocation', 'Risk First'],
                    panelLabel: 'Asset Management Made Simple',
                    panelTitle: 'AI does the heavy lifting, you make the calls',
                    panelDescription: 'Portfolio construction, risk allocation, strategy execution\u2014institutional-grade capabilities, now systematized by AI for you.',
                    assistText: 'Build consistent decision-making and execution habits with a clearer system.',
                    topCardLabel: 'Vision',
                    topCardTitle: 'Long-term Consistency',
                    topCardText: 'Support a stable, reviewable, and sustainable execution style',
                    bottomCardLabel: 'Experience Model',
                    bottomCardTitle: 'Invite-based Access',
                    bottomCardText: 'Defaults to Simplified Chinese, with Traditional Chinese and English available'
                },
                section: {
                    eyebrow: 'Product Directions',
                    title: 'Three product tracks, one system',
                    description: 'From information to execution to allocation — a clear path for AI delivery.'
                },
                products: [
                    {
                        key: 'assist-trading',
                        label: 'Copilot',
                        slogan: 'Quick insight, faster decisions',
                        description: 'Markets shift in an instant. Copilot helps you see fast and seize every opportunity with confidence.',
                        tags: [],
                        bullets: [],
                        metrics: [],
                        ctaLabel: '',
                        ctaTo: '/site/assist-trading'
                    },
                    {
                        key: 'auto-trading',
                        label: 'Auto Copilot',
                        slogan: 'Your intent, AI\'s direction',
                        description: 'Make trading as simple as setting investment preferences. Define your risk tolerance, and AI autonomously decides and executes within those boundaries — continuously.',
                        tags: [],
                        bullets: [],
                        metrics: [],
                        ctaLabel: '',
                        ctaTo: '/site/auto-trading'
                    },
                    {
                        key: 'asset-allocation',
                        label: 'Asset Allocation',
                        slogan: 'Ignore details, focus on your goals.',
                        description: 'AI asset management: you only care about what you want to achieve. The system handles the allocation mix, timing, and rebalancing automatically.',
                        tags: ['Goals', 'Allocation', 'AI Management'],
                        bullets: ['Outcome-driven \u2014 care about results, not process', 'Active management \u2014 auto-correct deviations', 'Continuous optimization \u2014 adapts as markets shift'],
                        metrics: [{
                            title: 'View',
                            value: 'Portfolio',
                            note: 'Full control of portfolio structure',
                            trend: 'stable'
                        }, {
                            title: 'Goal',
                            value: 'Time',
                            note: 'Track long-term goals consistently',
                            trend: 'up'
                        }, {
                            title: 'Risk Control',
                            value: 'Hedging',
                            note: 'Auto-managed risk exposure',
                            trend: 'focus'
                        }, {
                            title: 'Full Traceability',
                            value: 'Transparency',
                            note: 'AI decision history available',
                            trend: 'stable'
                        }],
                        ctaLabel: '',
                        ctaTo: '/site/asset-allocation'
                    }
                ],
                proofItems: [
                    {
                        title: 'Brand-led storytelling',
                        description: 'Lead with value and direction before feature details.',
                        tags: ['Brand', 'Website']
                    },
                    {
                        title: 'Systemized capability design',
                        description: 'Unify assisted trading, automation, and allocation into one framework.',
                        tags: ['System', 'Design']
                    },
                    {
                        title: 'Risk before action',
                        description: 'Make boundaries explicit before execution begins.',
                        tags: ['Risk', 'Boundary']
                    }
                ],
                exploreTitle: {
                    eyebrow: 'Next Step',
                    title: 'Understand the mechanism, then explore the POC',
                    description: 'Resources explain how the system works. POC pages show how the product lands.'
                },
                exploreCards: [
                    {
                        eyebrow: 'Resources',
                        title: 'Mechanism and risk boundaries',
                        description: 'See how D-Sense organizes input, judgment, execution, and control.',
                        actionLabel: 'Explore Resources',
                        path: '/site/how-it-works'
                    },
                    {
                        eyebrow: 'POC',
                        title: 'Assisted and automated trading demos',
                        description: 'See the value and flow of the two core tracks more directly.',
                        actionLabel: 'View POC',
                        path: '/site/poc'
                    }
                ],
                ctaEyebrow: 'Early Access',
                ctaTitle: 'Ready to explore D-Sense?',
                ctaDescription: 'Lay the foundation for your Rome. Register to unlock the full trading experience.'
            },
            productPage: {
                hero: {
                    eyebrow: 'Product',
                    title: 'Three capabilities across the path from decision to allocation',
                    description: 'This is not a pile of features. It is a usable system organized around what actually changes trading outcomes.',
                    tags: ['Copilot', 'Auto Copilot', 'Asset Allocation', 'Unified Workspace'],
                    panelLabel: 'Product Matrix',
                    panelTitle: 'One product, three core tracks',
                    panelDescription: 'Observe, judge, execute, and review assets inside one system.',
                    assistText: 'This page expands the three tracks introduced on the homepage.',
                    topCardLabel: 'Value',
                    topCardTitle: 'Less switching, less fragmentation',
                    topCardText: 'Keep judgment and execution in the same workflow as much as possible',
                    bottomCardLabel: 'Expression',
                    bottomCardTitle: 'Brand-led narrative',
                    bottomCardText: 'The website is organized by direction, scenario, and value'
                },
                directions: [
                    {
                        eyebrow: 'Copilot',
                        title: 'Open the workspace before the session — AI has already surfaced the key signals',
                        description: 'Before each session, the system organizes market structure shifts, key price levels, and AI insights. You do not start from zero. You start from the signals and form your judgment faster.',
                        bullets: ['Aggregated key signals ready when you open the workspace', 'Structure and risk points already highlighted by AI', 'Faster judgment with less screen pressure'],
                        metrics: [{
                            title: 'Best For',
                            value: 'Pre / In Session',
                            note: 'Observation and decision support',
                            trend: 'stable'
                        }, {
                            title: 'Output',
                            value: 'Structured',
                            note: 'Readable suggestions, tags, and risk points',
                            trend: 'up'
                        }],
                        featuresTitle: {
                            eyebrow: 'Core Capabilities',
                            title: 'Four steps from insight to trading',
                            description: 'A complete capability chain from data aggregation, signal aggregation, boundary checks to quick execution.'
                        },
                        flowSteps: [
                            {
                                title: 'Data Aggregation',
                                description: 'Integrate market data, order books, and on-chain data into structured inputs',
                                tags: ['Market', 'Order Book', 'On-Chain']
                            },
                            {
                                title: 'Signal Aggregation',
                                description: 'Cross-validate multi-dimensional signals, filter noise to highlight key info',
                                tags: ['Signals', 'Validation', 'Filter']
                            },
                            {
                                title: 'Boundary Check',
                                description: 'Determine actionable boundaries based on risk rules and account status',
                                tags: ['Risk', 'Account', 'Boundary']
                            },
                            {
                                title: 'Quick Trade',
                                description: 'Execute predefined strategies with one click, no page switching required',
                                tags: ['One-Click', 'Strategy', 'Efficiency']
                            }
                        ],
                        usageTitle: {
                            eyebrow: 'Workflow',
                            title: 'Three steps to get in the zone',
                            description: 'From opening the workspace to starting a trade — just three steps.'
                        },
                        usageSteps: [
                            {
                                title: 'Open Workspace',
                                description: 'Enter the workspace with market data and indicators ready',
                                detail: 'No manual chart searching'
                            },
                            {
                                title: 'View / Select Instruments',
                                description: 'Quickly browse signals and key levels across instruments',
                                detail: 'Focus on active instruments'
                            },
                            {
                                title: 'One-Click Trade',
                                description: 'Execute trades directly after boundary confirmation',
                                detail: 'Fewer steps, faster action'
                            }
                        ]
                    },
                    {
                        eyebrow: 'Trading for Everyone',
                        cardEyebrow: 'Fintech for Everyone',
                        title: 'AI Auto Trading, Start from Zero',
                        cardTitle: 'Zero-code AI Auto Trading',
                        description: 'Just set your risk preference, and AI will trade for you continuously.',
                        cardDescription: 'No strategy writing, no screen watching. Just set your risk expectations, and AI will match the right assets and iterate trades continuously. Free your mind from the market.',
                        bullets: ['Zero Barriers — No strategy needed, no trading experience required', 'Set Risk — Tell AI your risk preference, it handles the rest', 'Fully Automated — 24/7 continuous trading, truly hands-free'],
                        metrics: [{
                            title: 'Entry Barrier',
                            value: 'Zero',
                            note: 'Just set your risk tolerance',
                            trend: 'up'
                        }, {
                            title: 'Operation Mode',
                            value: 'Fully Auto',
                            note: 'AI matches assets and executes trades',
                            trend: 'focus'
                        }],
                        featuresTitle: {
                            eyebrow: 'Core Capabilities',
                            title: '6-Step Loop: AI Iterates Autonomously',
                            description: 'From data aggregation to outcome feedback, AI decides and iterates within your boundaries.'
                        },
                        flowSteps: [
                            {
                                title: 'Data Aggregation',
                                description: 'Aggregate multi-source market data into a unified foundation',
                                tags: ['Multi-source', 'Aggregation', 'Input']
                            },
                            {
                                title: 'Market Sensing',
                                description: 'AI continuously scans market states and structural shifts for opportunities',
                                tags: ['Sensing', 'Structure', 'Scanning']
                            },
                            {
                                title: 'Trade Planning',
                                description: 'Synthesize market structure and risk boundaries into autonomous trading decisions',
                                tags: ['Analysis', 'Planning', 'Decision']
                            },
                            {
                                title: 'Risk Validation',
                                description: 'Validate every action against the user-defined risk boundaries before execution',
                                tags: ['Boundary', 'Validate', 'Clearance']
                            },
                            {
                                title: 'Auto Execute',
                                description: 'AI autonomously executes trades within your defined boundaries',
                                tags: ['Order', 'Execution', 'Tracking']
                            },
                            {
                                title: 'Outcome Feedback',
                                description: 'Execution results and status flow back into the loop for the next iteration',
                                tags: ['Feedback', 'Status', 'Iteration']
                            }
                        ]
                    },
                    {
                        eyebrow: 'Asset Allocation',
                        title: 'AI manages your entire portfolio allocation',
                        description: 'Set your goals and risk preferences. From portfolio construction to dynamic rebalancing, AI handles it all. You just focus on the outcomes.',
                        bullets: ['Outcome-driven \u2014 care about results, not process', 'Active management \u2014 auto-correct deviations', 'Continuous optimization \u2014 adapts as markets shift'],
                        metrics: [{
                            title: 'View',
                            value: 'Portfolio',
                            note: 'Full control of portfolio structure',
                            trend: 'stable'
                        }, {
                            title: 'Goal',
                            value: 'Time',
                            note: 'Track long-term goals consistently',
                            trend: 'up'
                        }, {
                            title: 'Risk Control',
                            value: 'Hedging',
                            note: 'Auto-managed risk exposure',
                            trend: 'focus'
                        }, {
                            title: 'Full Traceability',
                            value: 'Transparency',
                            note: 'AI decision history available',
                            trend: 'stable'
                        }],
                        featuresTitle: {
                            eyebrow: 'Core Capabilities',
                            title: 'Six steps, AI manages your allocation end to end',
                            description: 'From goal decomposition to dynamic rebalancing, AI runs your asset allocation strategy end to end within your boundaries.'
                        },
                        flowSteps: [
                            {
                                title: 'Goal Decomposition',
                                description: 'Break long-term investment goals into actionable phases with clear return expectations and risk tolerance',
                                tags: ['Decompose', 'Goals', 'Phases']
                            },
                            {
                                title: 'Allocation Planning',
                                description: 'Plan asset class allocation ratios and investment strategies based on goals and market conditions',
                                tags: ['Plan', 'Allocation', 'Strategy']
                            },
                            {
                                title: 'Asset Comparison',
                                description: 'Screen quality targets across asset classes and determine the optimal portfolio candidates through multi-dimensional comparison',
                                tags: ['Screen', 'Compare', 'Targets']
                            },
                            {
                                title: 'Portfolio Construction & Execution',
                                description: 'Execute buy orders according to the planned ratios, complete portfolio construction and track progress',
                                tags: ['Build', 'Execute', 'Position']
                            },
                            {
                                title: 'Risk Monitoring & Compliance',
                                description: 'Monitor portfolio risk exposure and compliance metrics in real time, keeping investments within preset boundaries',
                                tags: ['Monitor', 'Risk', 'Compliance']
                            },
                            {
                                title: 'Value Tracking & Rebalancing',
                                description: 'Track asset value changes continuously, auto-trigger rebalancing when deviation exceeds thresholds to keep the portfolio on target',
                                tags: ['Track', 'Rebalance', 'Adjust']
                            }
                        ],
                        usageTitle: {
                            eyebrow: 'Workflow',
                            title: 'Three steps to AI asset management',
                            description: 'From setting goals to long-term operation \u2014 just three steps.'
                        },
                        usageSteps: [
                            {
                                title: 'Set Investment Goals',
                                description: 'Tell AI your risk preference and return expectations',
                                detail: 'Set once, track continuously'
                            },
                            {
                                title: 'AI Runs Automatically',
                                description: 'Portfolio construction, monitoring, and rebalancing \u2014 all by AI',
                                detail: 'No manual intervention needed'
                            },
                            {
                                title: 'Check Status Anytime',
                                description: 'Open the panel to view portfolio performance and AI adjustment history',
                                detail: 'Transparent and traceable'
                            }
                        ]
                    }
                ]
            },
            resources: {
                proofBarItems: ['Market Input', 'Structured Judgment', 'Risk Check', 'Execution Feedback'],
                howItWorksHero: {
                    eyebrow: 'How It Works',
                    title: 'Trading pipeline, transparent and in control',
                    description: 'D-Sense is not a black-box signal tool. It organizes input, judgment, risk control, and execution into an explainable flow.',
                    panelLabel: 'Workflow Loop',
                    panelTitle: 'From input to execution loop',
                    panelDescription: 'Each step has a clear job so traders can understand why an action happens.'
                },
                howItWorksSteps: [
                    {
                        title: 'Market Input',
                        description: 'Aggregate market structure, context, and signals into usable inputs.',
                        detail: 'Keep observation from being scattered across multiple screens.'
                    },
                    {
                        title: 'Strategy Judgment',
                        description: 'AI and rules produce structured suggestions together.',
                        detail: 'Conclusions, labels, and reasons stay visible together.'
                    },
                    {
                        title: 'Risk Validation',
                        description: 'Guardrails are checked before any action is allowed.',
                        detail: 'No execution starts when conditions fail.'
                    },
                    {
                        title: 'Execution Feedback',
                        description: 'Execution results and states return to one unified view.',
                        detail: 'Track status and outcomes continuously.'
                    }
                ],
                howItWorksFlowSteps: [
                    {
                        title: 'Data Aggregation',
                        description: 'Integrate multi-source market data, order books, and on-chain data into a unified structured input',
                        tags: ['Market', 'On-Chain', 'Multi-Source']
                    },
                    {
                        title: 'Market Sensing',
                        description: 'AI continuously scans market states and structural shifts to identify key signals and opportunities',
                        tags: ['Sensing', 'Scan', 'Identify']
                    },
                    {
                        title: 'Trade Planning',
                        description: 'Synthesize market structure and risk boundaries into autonomous trading decisions and strategies',
                        tags: ['Analysis', 'Planning', 'Decision']
                    },
                    {
                        title: 'Risk Validation',
                        description: 'Validate each action against user-defined risk boundaries to ensure compliance and safety',
                        tags: ['Boundary', 'Validate', 'Clearance']
                    },
                    {
                        title: 'Auto Execute',
                        description: 'AI autonomously executes trades within boundaries and tracks order execution status continuously',
                        tags: ['Order', 'Execution', 'Tracking']
                    },
                    {
                        title: 'Outcome Feedback',
                        description: 'Execution results and market states flow back into the system for the next analysis iteration',
                        tags: ['Feedback', 'Iteration', 'Loop']
                    }
                ],
                howItWorksAiDecision: [
                    {
                        eyebrow: 'AI Perception',
                        title: 'How AI Understands Market States',
                        description: 'The system transforms multi-source data into AI-readable structured inputs. Using LLMs, it identifies market structures, trend changes, and anomalous signals, then generates readable analytical conclusions.',
                        bullets: [
                            'Integrates K-line, order book, on-chain and other multi-dimensional data',
                            'AI identifies key price ranges and market structure shifts',
                            'Signal aggregation filters noise and highlights high-confidence opportunities',
                            'Outputs structured analysis in natural language'
                        ],
                        metrics: [
                            {
                                title: 'Data Dimensions',
                                value: '50+',
                                note: 'Technical indicators and market signals',
                                trend: 'up'
                            },
                            {
                                title: 'Analysis Frequency',
                                value: 'Real-time',
                                note: 'Continuous market state scanning',
                                trend: 'focus'
                            }
                        ]
                    },
                    {
                        eyebrow: 'AI Decision',
                        title: 'From Analysis to Execution — How AI Makes Trading Decisions',
                        description: 'The system constructs a context-rich prompt incorporating market states, risk boundaries, and historical data. It calls the LLM to generate structured trading proposals, then the parsing engine converts them into executable actions.',
                        bullets: [
                            'Dynamically builds multi-dimensional context prompts for decisions',
                            'LLM outputs structured trading decisions (direction/size/timing)',
                            'Parsing engine converts model output into executable trading instructions',
                            'Only submitted to the exchange after risk validation passes'
                        ],
                        metrics: [
                            {
                                title: 'Decision Latency',
                                value: '<30s',
                                note: 'From analysis to actionable suggestion',
                                trend: 'up'
                            },
                            {
                                title: 'Traceability',
                                value: 'Full Chain',
                                note: 'Prompt→Output→Action fully auditable',
                                trend: 'focus'
                            }
                        ]
                    }
                ],
                howItWorksBlocks: [
                    {
                        title: 'Input Layer — Multi-Source Data Aggregation',
                        description: 'Integrate exchange market data, on-chain data, technical indicators, and market sentiment into a unified data pipeline, cleaned and normalized for upper layers.',
                        tags: ['Market API', 'On-Chain', 'Indicators', 'Data Pipeline'],
                        iconName: 'database'
                    },
                    {
                        title: 'Judgment Layer — AI Analysis Engine',
                        description: 'Combine LLMs with rule engines for multi-dimensional market analysis. Dynamically construct prompts and call models to output structured signals and trading suggestions.',
                        tags: ['LLM', 'Prompt Engineering', 'Rule Engine', 'Signal'],
                        iconName: 'brain'
                    },
                    {
                        title: 'Execution Layer — Risk & Trade Chain',
                        description: 'Embed risk validation at the front of the execution chain so every trade runs within preset boundaries. Results stream back in real time, closing the feedback loop.',
                        tags: ['Risk Check', 'Order Execution', 'Sync', 'Exception'],
                        iconName: 'shield'
                    }
                ],
                howItWorksSections: {
                    architecture: {
                        eyebrow: 'System Architecture',
                        title: '6-Step Closed Loop: From Data to Execution',
                        description: 'Every step has a clear role. AI operates autonomously within boundaries and iterates continuously.'
                    },
                    aiDecision: {
                        eyebrow: 'AI Intelligence',
                        title: 'Market Perception & Decision Mechanism',
                        description: 'How the system understands market signals and translates them into executable trading decisions.'
                    },
                    layers: {
                        eyebrow: 'Tech Stack',
                        title: 'Three-Layer Architecture',
                        description: 'The system is built on three core layers, each with a distinct technical responsibility.'
                    },
                    demo: {
                        eyebrow: 'Live Demo',
                        title: 'See How AI Perceives the Market',
                        description: 'A simulated AI analysis panel showing how the system identifies and labels market signals.'
                    }
                },
                howItWorksDemo: {
                    symbol: 'BTC/USDT',
                    symbolChange: '+2.34%',
                    symbolChangeUp: true,
                    price: '67,432.50',
                    priceChange: '+1,542.80',
                    priceChangeUp: true,
                    signalTags: [
                        {label: 'Structure Break', type: 'signal'},
                        {label: 'Volume Spike', type: 'signal'},
                        {label: 'High Volatility', type: 'risk'},
                        {label: 'AI Bullish', type: 'info'}
                    ],
                    aiJudgmentTitle: 'AI Judgment',
                    aiJudgmentValue: 'Bullish · Medium Confidence',
                    aiJudgmentNote: 'Price breaking through recent resistance on volume',
                    keyLevelTitle: 'Key Levels',
                    keyLevelValue: '67,200 / 68,500',
                    keyLevelNote: 'Resistance 68,500, Support 67,200'
                },
                howItWorksMetrics: [
                    {
                        title: 'Process Steps',
                        value: '6-Step',
                        note: 'A complete closed loop from aggregation to feedback',
                        trend: 'focus'
                    },
                    {
                        title: 'Decision Latency',
                        value: '<30s',
                        note: 'From analysis to trading suggestion',
                        trend: 'up'
                    },
                    {
                        title: 'Data Dimensions',
                        value: '50+',
                        note: 'Technical indicators and market signals',
                        trend: 'up'
                    }
                ],
                howItWorksCta: {
                    eyebrow: 'Get Started',
                    title: 'Understand the mechanism, start exploring',
                    description: 'After learning how the system works, verify if it fits your trading style through our POC.'
                },
                riskHero: {
                    eyebrow: 'Risk & Security',
                    title: 'Boundaries first, actions within limits',
                    description: 'Risk control is not an extra. It is a prerequisite for a system worth using over time.',
                    panelLabel: 'Risk First',
                    panelTitle: 'Put boundaries before actions and safety before speed',
                    panelDescription: 'When risk is unclear, the system should reduce action instead of amplifying exposure.'
                },
                riskGuardItems: [
                    {
                        title: 'Risk modes first',
                        description: 'Set preference and boundaries before deciding on execution.',
                        bullets: ['Conservative / Cautious / Balanced / Active / Aggressive modes', 'Thresholds checked before every action', 'No execution when conditions fail']
                    },
                    {
                        title: 'Stops and position control',
                        description: 'Keep drawdown and exposure within acceptable bounds.',
                        bullets: ['Auto-enforced position caps', 'Take-profit and stop-loss linked together', 'No boundaryless averaging down']
                    },
                    {
                        title: 'Permission and key isolation',
                        description: 'Handle sensitive operations with least-privilege principles.',
                        bullets: ['Tiered API permissions', 'Encrypted key isolation', 'Safe fallback on anomalies']
                    }
                ],
                riskMatrixItems: [
                    {
                        title: 'Pre-execution validation',
                        description: 'Every action is validated against risk conditions before it reaches the exchange.',
                        tags: ['Pre-check', 'Validation'],
                        iconName: 'shield'
                    },
                    {
                        title: 'Permission boundary',
                        description: 'API and account capabilities are constrained to the minimum necessary scope.',
                        tags: ['Permission', 'Security'],
                        iconName: 'layers'
                    },
                    {
                        title: 'Continuous feedback',
                        description: 'Execution results and risk states flow back to a unified view, closing the monitoring loop.',
                        tags: ['Feedback', 'Traceability'],
                        iconName: 'waypoints'
                    }
                ],
                riskSections: {
                    guard: {
                        eyebrow: 'Risk Rules',
                        title: 'Three layers of defense',
                        description: 'From risk preference to position control to permission management, each layer tightens the safety boundary.'
                    },
                    matrix: {
                        eyebrow: 'Execution Guard',
                        title: 'Every step runs within the rules',
                        description: 'Validation logic is embedded at every node of the execution chain so actions always stay within preset boundaries.'
                    }
                },
                riskCta: {
                    eyebrow: 'Get Started',
                    title: 'Let risk control lead your trading',
                    description: 'See how risk boundaries protect every trade you make.'
                },
                riskMetrics: [
                    {title: 'Risk Layers', value: '3 tiers', note: 'Mode → Position → Permission', trend: 'focus'},
                    {title: 'Validation', value: 'Pre-exec', note: 'Auto-checked before every action', trend: 'up'},
                    {title: 'Permission', value: 'Tiered', note: 'Least-privilege API access', trend: 'stable'}
                ]
            },
            pocPage: {
                hero: {
                    eyebrow: 'POC',
                    title: 'See how the product works through concrete POC pages',
                    description: 'These pages do not stay abstract. They show the scenarios, value, and interaction structure of assisted and automated trading.',
                    tags: ['Copilot', 'Auto Copilot', 'Scenarios', 'Demos'],
                    panelLabel: 'POC Entry',
                    panelTitle: 'Two tracks, two product experiences',
                    panelDescription: 'Explore how assisted trading and automation serve different goals.',
                    assistText: 'Use the POC pages to judge fit before deciding to start.',
                    topCardLabel: 'Track One',
                    topCardTitle: 'Copilot',
                    topCardText: 'Read the market, shape judgment, prepare action',
                    bottomCardLabel: 'Track Two',
                    bottomCardTitle: 'Auto Copilot',
                    bottomCardText: 'Let rules run and let results feed back'
                },
                entries: [
                    {
                        title: 'Copilot',
                        slogan: 'See faster, judge with more confidence',
                        description: 'Walk through the flow from market observation to decision support.',
                        tags: ['Market View', 'AI Explanation', 'Risk Alerts'],
                        path: '/site/poc/assist-trading'
                    },
                    {
                        title: 'Auto Copilot',
                        slogan: 'Rules first, execution continues',
                        description: 'See how strategies keep running inside defined boundaries with status feedback.',
                        tags: ['Rule Execution', 'Risk First', 'Feedback'],
                        path: '/site/poc/auto-trading'
                    },
                    {
                        title: 'Pattern Recognition',
                        slogan: 'Every candle tells a story',
                        description: 'See automated K-line pattern detection with AI-powered analysis.',
                        tags: ['Pattern', 'AI Analysis', 'Backtest'],
                        path: '/site/poc/pattern'
                    }
                ],
                features: [
                    {
                        title: 'Scenario-led storytelling',
                        description: 'Show what each page solves instead of only listing modules.',
                        tags: ['Scenario', 'Value']
                    },
                    {
                        title: 'Flow-led structure',
                        description: 'Make the order of modules and information flow explicit.',
                        tags: ['Flow', 'Structure']
                    },
                    {
                        title: 'Boundary-aware explanation',
                        description: 'Explain guardrails and limits inside the demo itself.',
                        tags: ['Boundary', 'Risk']
                    }
                ],
                faqIntro: {
                    eyebrow: 'Additional Notes',
                    title: 'Read these boundaries before the POC',
                    description: 'A few high-frequency questions clarify the essentials before use.'
                },
                faqItems: [
                    {
                        question: 'Who is this system for?',
                        answer: 'It fits individual traders who want less screen-watching, faster decisions, and clearer risk boundaries.'
                    },
                    {
                        question: 'Can the system execute trades automatically?',
                        answer: 'Yes, it supports assisted and automated modes. Whether to enable automation should depend on your own risk understanding.'
                    },
                    {
                        question: 'Why review the POC first?',
                        answer: 'The POC pages help you understand product logic, use cases, and boundaries before trying the system.'
                    },
                    {
                        question: 'How do I get started?',
                        answer: 'If you already have an invitation code, you can register now. Otherwise, submit your info from the contact page first.'
                    }
                ]
            },
            assistPocPage: {
                hero: {
                    eyebrow: 'Copilot',
                    title: 'Assisted trading helps you understand opportunities faster without chasing blindly',
                    description: 'It helps traders observe, judge, and prepare actions faster in volatile markets while keeping final control in human hands.',
                    tags: ['Market Signals', 'AI Assist', 'Risk Alerts', 'Action Suggestions'],
                    panelLabel: 'Core Slogan',
                    panelTitle: 'See faster, judge with more confidence',
                    panelDescription: 'Use the system to see opportunities more clearly before deciding to act.',
                    assistText: 'Assisted trading supports judgment. It does not replace it.',
                    topCardLabel: 'Best For',
                    topCardTitle: 'Before and During Sessions',
                    topCardText: 'Ideal for frequent observation and faster decision shaping',
                    bottomCardLabel: 'Outcome',
                    bottomCardTitle: 'Less hesitation',
                    bottomCardText: 'Bring observation, understanding, and action prep into one flow'
                },
                features: [
                    {
                        title: 'Signal aggregation',
                        description: 'Keep the most important market labels and changes in one place.',
                        tags: ['Signals', 'Labels']
                    },
                    {
                        title: 'AI-assisted judgment',
                        description: 'Get structured explanations and suggestions quickly.',
                        tags: ['AI', 'Explanation']
                    },
                    {
                        title: 'Risk-first alerts',
                        description: 'See important limits before you move.',
                        tags: ['Risk', 'Alerts']
                    },
                    {
                        title: 'Action-oriented output',
                        description: 'Turn judgment into a clearer next action.',
                        tags: ['Action', 'Preparation']
                    }
                ],
                flowSteps: [
                    {
                        title: 'Watch the market',
                        description: 'Review key market changes and context from one place.',
                        detail: 'Finish the first round of filtering faster.'
                    },
                    {
                        title: 'Understand signals',
                        description: 'Use AI to organize reasons, labels, and direction.',
                        detail: 'Make conclusions easier to understand.'
                    },
                    {
                        title: 'Confirm boundaries',
                        description: 'Review risk alerts and action limits before moving.',
                        detail: 'Do not look at opportunity without also seeing cost.'
                    },
                    {
                        title: 'Prepare action',
                        description: 'Shape a clearer next action plan.',
                        detail: 'You still decide whether execution starts.'
                    }
                ],
                stories: [
                    {
                        eyebrow: 'Value One',
                        title: 'Spot opportunities faster',
                        description: 'Pull a scattered observation process back into one workspace to reduce switching cost.',
                        bullets: ['Less fragmented chart-watching', 'Faster information convergence', 'Quicker movement into judgment'],
                        metrics: [{
                            title: 'Observation',
                            value: 'Unified',
                            note: 'Key inputs are shown together',
                            trend: 'stable'
                        }, {title: 'Speed', value: 'Faster', note: 'Less switching and hesitation', trend: 'up'}]
                    },
                    {
                        eyebrow: 'Value Two',
                        title: 'Make judgment easier to explain',
                        description: 'Do not stop at signals. Show the reasoning and risk with them.',
                        bullets: ['See signals and reasons together', 'Express risk and opportunity together', 'Make reviews easier later'],
                        metrics: [{
                            title: 'Output',
                            value: 'Structured',
                            note: 'Tags, explanations, and suggestions stay linked',
                            trend: 'up'
                        }, {
                            title: 'Reviewability',
                            value: 'Stronger',
                            note: 'Trace decision logic more easily',
                            trend: 'focus'
                        }]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: 'Core Capabilities',
                        title: 'From signals to action, a complete path',
                        description: 'Instead of piling up features, organize the key capabilities of assisted trading into a usable system path.'
                    },
                    flow: {
                        eyebrow: 'Typical Flow',
                        title: 'Open your workspace, four steps to prepare',
                        description: 'From observation to decision, the system converges scattered steps into a repeatable workflow.'
                    },
                    stories: {
                        eyebrow: 'Value Proposition',
                        title: 'Two key problems assisted trading solves',
                        description: 'How to spot opportunities faster when overloaded, and how to make complex judgments easier to explain.'
                    },
                    demo: {
                        eyebrow: 'Live Demo',
                        title: 'Trading Workspace Showcase',
                        description: 'From real-time market data and positions to historical trades, a complete showcase of the trading workspace\'s core data panels.'
                    },
                    cta: {
                        primaryLabel: 'Get an Invitation Code',
                        primaryTo: '/site/assist-trading'
                    }
                }
            },
            autoPocPage: {
                hero: {
                    eyebrow: 'Auto Copilot',
                    title: 'Set your goals, AI does the trading',
                    description: 'Just set your risk tolerance and target return rate. AI matches opportunities, manages positions, and optimizes capital utilization — no strategy coding, no screen-watching required.',
                    tags: ['Risk Preference', 'Target Return', 'Capital Efficiency', 'AI Autonomous Trading'],
                    panelLabel: 'Core Philosophy',
                    panelTitle: 'You set the goals, AI handles execution',
                    panelDescription: 'Trading doesn\'t require complex strategy programming. Tell AI your risk tolerance and return expectations — it finds the optimal path automatically.',
                    assistText: 'Set your risk preference and return targets. AI takes care of the rest.',
                    topCardLabel: 'Perfect For',
                    topCardTitle: 'Simple & Direct',
                    topCardText: 'No coding or strategy writing needed — suitable for all traders',
                    bottomCardLabel: 'Core Advantage',
                    bottomCardTitle: 'AI-Driven Decisions',
                    bottomCardText: 'AI analyzes the market in real-time, autonomously finding optimal trading opportunities within your boundaries'
                },
                features: [
                    {
                        title: 'Risk Preference Setting',
                        description: 'Set your maximum acceptable loss ratio. AI operates strictly within this boundary.',
                        tags: ['Risk Control', 'Safety']
                    },
                    {
                        title: 'Target Return Management',
                        description: 'Set your expected return rate. AI automatically finds matching market opportunities.',
                        tags: ['Target', 'Return']
                    },
                    {
                        title: 'Capital Utilization Optimization',
                        description: 'AI intelligently allocates capital per trade, maximizing fund efficiency.',
                        tags: ['Efficiency', 'Position']
                    },
                    {
                        title: 'AI Autonomous Execution',
                        description: '24/7 market monitoring with automatic timing, entry, and exit — fully hands-free.',
                        tags: ['Automated', '24/7']
                    },
                ],
                flowSteps: [
                    {
                        title: 'Set Risk Boundaries',
                        description: 'Define your maximum acceptable drawdown and per-trade loss limit.',
                        detail: 'All AI decisions stay within these boundaries'
                    },
                    {
                        title: 'Set Return Targets',
                        description: 'Set your expected annual or quarterly return rate.',
                        detail: 'AI uses this benchmark to match suitable opportunities'
                    },
                    {
                        title: 'AI Smart Matching',
                        description: 'AI analyzes markets in real-time, matching opportunities to your risk-return profile.',
                        detail: 'Intelligently allocates positions based on capital utilization'
                    },
                    {
                        title: 'Auto Execute & Optimize',
                        description: 'AI autonomously executes trades and continuously adapts to market changes.',
                        detail: 'Every trade is traceable with clear profit visibility'
                    }
                ],
                stories: [
                    {
                        eyebrow: 'Value One',
                        title: 'No strategies, just goals',
                        description: 'You don\'t need to write any trading strategies. Tell AI your risk boundary and return expectations — it finds the best path automatically.',
                        bullets: ['Zero coding — set it and it runs', 'AI auto-optimizes opportunity matching', 'No screen-watching — fully autonomous'],
                        metrics: [{
                            title: 'Ease of Use',
                            value: 'Minimal',
                            note: 'Just two parameters to get started',
                            trend: 'up'
                        }, {
                            title: 'Decision Making',
                            value: 'AI-Driven',
                            note: 'AI replaces manual judgment and execution',
                            trend: 'stable'
                        }]
                    },
                    {
                        eyebrow: 'Value Two',
                        title: 'Every dollar works efficiently',
                        description: 'AI doesn\'t just trade — it manages your capital. Dynamically adjusts position sizes so your capital utilization stays optimal at all times.',
                        bullets: ['Smart position management, dynamic allocation', 'Real-time capital utilization adjustment', 'Idle funds stay productive'],
                        metrics: [{
                            title: 'Capital Efficiency',
                            value: 'Optimal',
                            note: 'AI continuously optimizes capital allocation',
                            trend: 'focus'
                        }, {
                            title: 'Return Source',
                            value: 'Transparent',
                            note: 'Every gain and loss is traceable',
                            trend: 'up'
                        }]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: 'Core Capabilities',
                        title: 'Four Capabilities of AI Autonomous Trading',
                        description: 'No strategies needed. Just set your preferences — AI handles everything.'
                    },
                    flow: {
                        eyebrow: 'Getting Started',
                        title: 'Four steps, AI starts immediately',
                        description: 'The moment you set your preferences, AI starts working for you.'
                    },
                    stories: {
                        eyebrow: 'Value Proposition',
                        title: 'Simple, yet powerful',
                        description: 'Turning complex trading into simple choices.'
                    },
                    demo: {
                        eyebrow: 'Interactive Demo',
                        title: 'AI Auto Trading Demo',
                        description: 'Configure your API Key and risk preference to experience the complete AI auto trading workflow.'
                    }
                }
            },
            patternPocPage: {
                hero: {
                    eyebrow: 'Pattern POC',
                    title: 'AI-powered K-line pattern recognition',
                    description: 'From detection to analysis, see how AI identifies, backtests, and evaluates K-line patterns for trading opportunities.',
                    tags: ['K-line', 'AI Analysis', 'Backtest', 'Signals'],
                    panelLabel: 'Core Capability',
                    panelTitle: 'Every candle pattern gets seen',
                    panelDescription: 'Hundreds of classic pattern detection algorithms combined with AI-powered analysis to help you discover potential trading opportunities.',
                    assistText: 'No more manual chart scanning. The system auto-labels patterns and provides AI analysis.',
                    topCardLabel: 'Detection',
                    topCardTitle: 'Auto Identify',
                    topCardText: 'Auto-detects patterns across 1m to 1D timeframes',
                    bottomCardLabel: 'AI Analysis',
                    bottomCardTitle: 'Deep Insight',
                    bottomCardText: 'AI provides entry, stop-loss, and target recommendations'
                },
                features: [
                    {
                        title: 'Multi-pattern detection',
                        description: 'Auto-detects hundreds of classic K-line patterns including hammer, engulfing, morning star and more.',
                        tags: ['Patterns', 'Coverage']
                    },
                    {
                        title: 'AI-powered analysis',
                        description: 'LLM-powered multi-dimensional analysis with entry price, target price, and stop-loss suggestions.',
                        tags: ['AI', 'Analysis']
                    },
                    {
                        title: 'Historical backtest',
                        description: 'Backtest every pattern to see win rate, profit/loss ratio, and other key metrics for informed decisions.',
                        tags: ['Backtest', 'Data']
                    },
                    {
                        title: 'Zone recognition',
                        description: 'Auto-identifies support/resistance zones and consolidation ranges to spot key price levels.',
                        tags: ['Zones', 'S/R']
                    },
                ],
                flowSteps: [
                    {
                        title: 'Select symbol',
                        description: 'Choose your target trading pair, system fetches K-line data automatically.',
                        detail: 'Supports perpetual swaps and spot, covers BTC, ETH and other majors'
                    },
                    {
                        title: 'Pattern detection',
                        description: 'System scans K-lines, identifies and marks all pattern signals.',
                        detail: 'Supports 1m to 1D timeframes with real-time updates'
                    },
                    {
                        title: 'AI analysis',
                        description: 'AI-powered analysis on detected patterns with trading reference.',
                        detail: 'Provides entry, stop-loss, target prices and risk level recommendations'
                    },
                    {
                        title: 'Backtest validation',
                        description: 'View historical performance data to gauge signal reliability.',
                        detail: 'Win rate, profit/loss ratio, and historical P&L records'
                    }
                ],
                stories: [
                    {
                        eyebrow: 'Value One',
                        title: 'More comprehensive pattern detection',
                        description: 'Hundreds of patterns detected automatically, from simple hammers to complex morning stars. Catch signals you might miss.',
                        bullets: ['Auto-scan with no manual chart review', 'Hundreds of classic patterns covered', 'Multi-timeframe support'],
                        metrics: [
                            {
                                title: 'Efficiency',
                                value: 'Auto',
                                note: 'Real-time monitoring, zero manual effort',
                                trend: 'up'
                            },
                            {
                                title: 'Coverage',
                                value: 'Broad',
                                note: 'Hundreds of patterns auto-detected',
                                trend: 'focus'
                            }
                        ]
                    },
                    {
                        eyebrow: 'Value Two',
                        title: 'Deeper analysis with AI',
                        description: 'AI tells you not just the pattern type but also confidence, potential P&L ratio, and specific trade recommendations.',
                        bullets: ['AI multi-dimensional signal analysis', 'Entry, stop-loss, target prices given', 'Risk assessment for decision support'],
                        metrics: [
                            {
                                title: 'Depth',
                                value: 'Comprehensive',
                                note: 'Multi-dimensional AI analysis',
                                trend: 'up'
                            },
                            {
                                title: 'Actionability',
                                value: 'Practical',
                                note: 'Specific price suggestions and risk alerts',
                                trend: 'focus'
                            }
                        ]
                    }
                ],
                sectionTitles: {
                    features: {
                        eyebrow: 'Core Capabilities',
                        title: 'Complete pattern recognition pipeline from detection to analysis',
                        description: 'More than just detection — organized detection, analysis, and historical validation into a complete toolchain.'
                    },
                    flow: {
                        eyebrow: 'Typical Flow',
                        title: 'Four steps to complete pattern recognition',
                        description: 'From symbol selection to backtest validation, the system handles the full pattern recognition workflow.'
                    },
                    stories: {
                        eyebrow: 'Value Proposition',
                        title: 'Three problems pattern recognition solves',
                        description: 'Manual charting is slow, signals get missed, analysis lacks depth — pattern recognition clears all three.'
                    },
                    demo: {
                        eyebrow: 'Interactive Demo',
                        title: 'Candlestick Pattern Recognition Demo',
                        description: 'Quickly discover market structure changes and trading opportunities through candlestick pattern recognition.'
                    },
                    cta: {
                        primaryLabel: 'Get an Invitation Code',
                    }
                }
            },
            membershipPage: {
                hero: {
                    eyebrow: 'Membership',
                    title: 'The right capability for every stage of investing',
                    description: 'From free tier to expert-level fully automated trading, four membership tiers cover the full path from insight to execution to allocation.',
                    panelLabel: 'Subscribe online',
                    panelTitle: 'Subscribe online, active instantly',
                    panelDescription: 'Pick your tier and pay online — your subscription takes effect immediately. Track its status and expiry anytime from your dashboard.'
                },
                sectionTitles: {
                    compare: {
                        eyebrow: 'Tier comparison',
                        title: 'Four tiers, choose what fits',
                        description: 'Pricing and feature matrix at a glance. DCA plan limits grow with your tier.'
                    },
                    features: {
                        eyebrow: 'Premium features',
                        title: 'Each capability solves a concrete problem',
                        description: 'DCA Assistant, hedging, pattern recognition and auto trading unlock by tier.'
                    },
                    cta: {
                        eyebrow: 'Get started',
                        title: 'Ready to upgrade?',
                        description: 'Pick your tier and pay online — your subscription takes effect instantly.'
                    }
                },
                priceLabels: {monthly: 'Monthly', yearly: 'Yearly'},
                tiers: [
                    {
                        name: 'Free', tagline: 'Core features', monthlyPrice: '0', yearlyPriceMonthly: '0',
                        features: [
                            {label: 'Watch reports', enabled: true},
                            {label: 'DCA Assistant', enabled: false},
                            {label: 'Hedging', enabled: false},
                            {label: 'Pattern quick trading', enabled: false},
                            {label: 'Auto trading', enabled: false}
                        ]
                    },
                    {
                        name: 'Starter',
                        tagline: 'Planned auto buys',
                        monthlyPrice: '5.9U',
                        yearlyPriceMonthly: '3.9U/mo',
                        features: [
                            {label: 'Watch reports', enabled: true},
                            {label: 'DCA Assistant', enabled: true, note: 'Up to 3 plans'},
                            {label: 'Hedging', enabled: false},
                            {label: 'Pattern quick trading', enabled: false},
                            {label: 'Auto trading', enabled: false}
                        ]
                    },
                    {
                        name: 'Pro',
                        tagline: 'Advanced trading & patterns',
                        monthlyPrice: '15.9U',
                        yearlyPriceMonthly: '9.9U/mo',
                        highlight: true,
                        features: [
                            {label: 'Watch reports', enabled: true},
                            {label: 'DCA Assistant', enabled: true, note: 'Up to 10 plans'},
                            {label: 'Hedging', enabled: true},
                            {label: 'Pattern quick trading', enabled: true},
                            {label: 'Auto trading', enabled: false}
                        ]
                    },
                    {
                        name: 'Expert',
                        tagline: 'Full AI auto trading',
                        monthlyPrice: '59.9U',
                        yearlyPriceMonthly: '29.9U/mo',
                        features: [
                            {label: 'Watch reports', enabled: true},
                            {label: 'DCA Assistant', enabled: true, note: 'Up to 15 plans'},
                            {label: 'Hedging', enabled: true},
                            {label: 'Pattern quick trading', enabled: true},
                            {label: 'Auto trading', enabled: true}
                        ]
                    }
                ],
                featureIntros: [
                    {
                        title: 'DCA Assistant',
                        description: 'Build plans from symbol and indicator conditions; the system watches closed candles and buys spot at market when a trigger fires. One-shot or recurring.',
                        tags: ['Plan-based', 'Condition triggers', 'Auto buy']
                    },
                    {
                        title: 'Hedging',
                        description: 'Single-leg and cross-symbol hedging with automatic protection-leg re-mounting and unified take-profit, stop-loss and exposure management.',
                        tags: ['Single-leg hedge', 'Protection leg', 'Exposure control']
                    },
                    {
                        title: 'Pattern quick trading',
                        description: 'AI recognizes key patterns and enables one-click orders so decisions land faster.',
                        tags: ['Pattern recognition', 'Quick orders']
                    },
                    {
                        title: 'Auto trading',
                        description: 'Set your risk boundaries and let AI decide and trade autonomously within them.',
                        tags: ['AI decisions', 'Bounded autonomy', 'Continuous trading']
                    }
                ],
                faqIntro: {
                    eyebrow: 'FAQ',
                    title: 'Frequently asked questions',
                    description: 'Common questions about membership and activation.'
                },
                faqItems: [
                    {
                        question: 'How do I subscribe?',
                        answer: 'Pick your tier on the pricing page and pay online — your subscription takes effect immediately. You will get a renewal reminder before it expires and can manage your subscription anytime from the membership page.'
                    },
                    {
                        question: 'What happens when my membership expires?',
                        answer: 'You are downgraded to the next active subscription tier (or free). DCA plans beyond the new tier limit are paused automatically with an in-app notice; restore them manually after renewing.'
                    },
                    {
                        question: 'How is the DCA plan limit calculated?',
                        answer: 'By tier: Starter 3 plans, Pro 10, Expert 15. Only non-deleted plans count, paused and running alike.'
                    },
                    {
                        question: 'What is the yearly discount?',
                        answer: 'Yearly billing is charged as a lower monthly equivalent: Starter 3.9U/mo, Pro 9.9U/mo, Expert 29.9U/mo.'
                    }
                ]
            },
            contactPage: {
                hero: {
                    eyebrow: 'Get Started',
                    title: 'Get your invitation code and start exploring D-Sense',
                    description: 'If you do not have an invite code yet, submit your information first and we will arrange the next step based on demand and rollout stage.',
                    tags: ['Invitation Code', 'Apply for Access', 'Get Started'],
                    panelLabel: 'How to Start',
                    panelTitle: 'Register now with a code, or apply first',
                    panelDescription: 'Turn invite-based access into a clearer, easier path.',
                    assistText: 'Understand the access path before you decide to submit your information.',
                    topCardLabel: 'Faster Start',
                    topCardTitle: 'Two Entry Points',
                    topCardText: 'Register directly, or apply for an invite first',
                    bottomCardLabel: 'Access Model',
                    bottomCardTitle: 'Invitation-based rollout',
                    bottomCardText: 'Use invite codes to control access rhythm'
                },
                contactModes: [
                    {
                        title: 'Have a code already? Register now',
                        description: 'If you already received an invite code, you can register right away.',
                        tags: ['Register', 'Start']
                    },
                    {
                        title: 'Need access first? Apply here',
                        description: 'Submit your information first and enter the invite process.',
                        tags: ['Invite', 'Apply']
                    },
                    {
                        title: 'Built for traders who want a longer-term workflow',
                        description: 'A better fit for individual traders who want a steadier working system.',
                        tags: ['Individual', 'Long-term']
                    }
                ],
                applicationSteps: [
                    {
                        title: 'Submit your info',
                        description: 'Share your basics and intended use case.',
                        detail: 'Include your trading scenario and what you care about most.'
                    },
                    {
                        title: 'Align on fit',
                        description: 'We confirm direction and expectations before issuing access.',
                        detail: 'Risk understanding and expectations matter here.'
                    },
                    {
                        title: 'Receive an invite code',
                        description: 'Once approved, you receive a code for registration.',
                        detail: 'If you already have one, you can skip directly ahead.'
                    },
                    {
                        title: 'Register and start',
                        description: 'Create your account and continue into the system.',
                        detail: 'Then explore the product and POC pages in more depth.'
                    }
                ],
                faqIntro: {
                    eyebrow: 'Additional Notes',
                    title: 'Clarify the common questions first',
                    description: 'Understand the invite path and access logic before you proceed.'
                },
                faqItems: [
                    {
                        question: 'Why use invitation-based access?',
                        answer: 'The website currently uses invitation-based onboarding to control rollout pace and keep feedback quality high.'
                    },
                    {
                        question: 'Can I start without an invite code?',
                        answer: 'Yes. Submit your information on the contact page first and we will arrange the next step based on rollout stage.'
                    },
                    {
                        question: 'What can I do after registration?',
                        answer: 'You can continue into the product, sign in, and learn the trading workflow in more depth.'
                    },
                    {
                        question: 'Which languages are supported?',
                        answer: 'This iteration supports Simplified Chinese, Traditional Chinese, English, Korean, Japanese, and French for the website, login page, and registration page.'
                    }
                ]
            }
        }
    };

    return copies[locale] ?? copies['en-US'];
};

export const useSiteContent = () => {
    const {locale, t} = useI18n();

    return useMemo(() => ({
        brandSubtitle: t('site.brand.subtitle'),
        authLoginHighlights: [
            {title: t('auth.login.highlight.1.title'), description: t('auth.login.highlight.1.description')},
            {title: t('auth.login.highlight.2.title'), description: t('auth.login.highlight.2.description')},
            {title: t('auth.login.highlight.3.title'), description: t('auth.login.highlight.3.description')}
        ] as AuthHighlightItem[],
        authRegisterHighlights: [
            {title: t('auth.register.highlight.1.title'), description: t('auth.register.highlight.1.description')},
            {title: t('auth.register.highlight.2.title'), description: t('auth.register.highlight.2.description')},
            {title: t('auth.register.highlight.3.title'), description: t('auth.register.highlight.3.description')}
        ] as AuthHighlightItem[],
        ...buildSiteCopy(locale as CopyLocale)
    }), [locale, t]);
};
